import React, { useState, useEffect, useRef, useMemo } from "react";
import usePersistentPageState from "../hooks/usePersistentPageState";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  FaFlag,
  FaCar,
} from "react-icons/fa";
import { QRCodeSVG } from "qrcode.react";
import { BASE_URL } from "../config";
import talentLogo2 from "../assets/talentlogo2.png";
import { showError, showSuccess, showConfirm, showToast } from "../utils/dialog";
import usePageMeta from "../hooks/usePageMeta";
import {
  addAppCalendarDays,
  formatAppDate,
  formatAppDateTime,
  formatAppIntlLabel,
  formatAppRelativeShortAgo,
  formatAppTime,
  getAppTodayIso,
  getAppWallDateParts,
  getAppWeekStartIso,
  fromDatetimeLocalValue,
  parseDbDateTime,
  toDatetimeLocalValue,
  toMysqlUtcWallString,
} from "../utils/appDateTime";
import { useAppPermissions } from "../context/PermissionContext";
import Layout from "./Layout";
import "../Styles/Personnel.css";
import "../Styles/PrintTimesheet.css";
import ActionDropdown from "../components/ActionDropdown";
import ClientDetailModal from "../components/ClientDetailModal";
import TimesheetDrawer from "./TimesheetDrawer";
import TimesheetModal from "./TimesheetModal";
import CreateShiftModal from "./CreateShiftModal";
import ShiftDetailModal from "../components/ShiftDetailModal";
import DuplicateShiftModal from "../components/DuplicateShiftModal";
import RichAuditsPanel from "../components/RichAuditsPanel";
import ShiftDetailPanel from "../components/ShiftDetailPanel";
import AssignmentDetailModal from "../components/AssignmentDetailModal";
import RightPanel from "../components/RightPanel";
import PersonnelDetailModal from "../components/PersonnelDetailModal";
import UnsavedChangesModal from "../components/UnsavedChangesModal";
import {
  AtlasFilterPanel,
  AtlasFilterChipBar,
  useFilterPanel,
  JobsStaffingRequestsFilterFields,
  buildJobsStaffingRequestsFilterChips,
  JOBS_STAFFING_SORT_OPTIONS,
  JobsTimesheetsFilterFields,
  buildJobsTimesheetsFilterChips,
  JobsShiftAssignmentFilterFields,
  buildJobsShiftAssignmentFilterChips,
  JOBS_SHIFT_ASSIGNMENT_SORT_OPTIONS,
  JobsShiftsFilterFields,
  buildJobsShiftsFilterChips,
  JOBS_SHIFTS_SORT_OPTIONS,
} from "../components/filters";
import { AtlasListToolbar } from "../components/list-toolbar";
import {
  AtlasPageHeader,
  AtlasPageDivider,
  AtlasTabBar,
} from "../components/page-layout";
import { groupTimesheetsIntoSlots } from "../utils/timesheetSlots";
import TimesheetWeekView from "../components/timesheets/TimesheetWeekView";
import TimesheetsPanel from "../components/timesheets/TimesheetsPanel";
import {
  buildWeekStaffRows,
  filterTimesheetsToWeekRange,
  groupTimesheetsByLocation,
  resolveStaffRequestIdsFromRows,
  resolveSupervisorsFromRows,
  timesheetRowDateKey,
} from "../utils/timesheetWeekRows";
import { triggerTimesheetPrint } from "../utils/timesheetViewHelpers";
import WrappingSelect from "../components/WrappingSelect";
import ClickableDropdown from "../components/ClickableDropdown";
import "react-phone-number-input/style.css";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import SearchableMultiSelectReact from "../components/SearchableMultiSelectReact";
import DateTimePickerPanel from "../components/DateTimePicker.jsx";
import "../components/ShiftDetailModal.css";
import StaffAvatarCircle from "../components/StaffAvatarCircle";
import defaultProfilePic from "../assets/default_profile_pic.jpeg";
import {
  resolveBackendUrl,
  shiftStaffEmployeeForAvatar,
} from "../utils/accountAvatarUrl";
import {
  resolveShiftStaffDisplayName,
  shiftHasRealStaff,
} from "../utils/shiftStaffDisplay";
import { isSubmittedActualState } from "../utils/shiftActualState";
import { normalizeBreakMinutes } from "../utils/shiftListStatusDisplay";
import { mergeShiftRowAfterActualsSubmit } from "../utils/shiftRowActualsMerge";
import ShiftStatusCell from "../components/shifts/ShiftStatusCell";
import ShiftActualTimeSubmissionCell from "../components/shifts/ShiftActualTimeSubmissionCell";
import useStaffingRequestReplicate from "../hooks/useStaffingRequestReplicate";
import { useDeleteKeyEasterEgg } from "../hooks/useDeleteKeyEasterEgg";

const emptyJobTabClientLoc = () => ({
  filterClient: "",
  clientSearch: "",
  filterClientList: [],
  filterLocation: "",
  filterLocations: [],
  filterLocationList: [],
  locationSearch: "",
  locations: [],
});

const JOBS_CLIENT_LOC_TAB_KEYS = [
  "STAFFINGREQUESTS",
  "SHIFTS",
  "TIMESHEETS",
  "SHIFTASSIGNMENT",
];

function initialJobsClientLocByTab() {
  return Object.fromEntries(
    JOBS_CLIENT_LOC_TAB_KEYS.map((k) => [k, emptyJobTabClientLoc()]),
  );
}

async function fetchStaffRequestContactOptions(clientId, locationId) {
  const token =
    localStorage.getItem("token") || localStorage.getItem("authToken");
  const authOpts = token
    ? { headers: { Authorization: `Bearer ${token}` } }
    : undefined;
  const url = `${BASE_URL}/api/clients/${encodeURIComponent(clientId)}/staff-request-contact-options?locationId=${encodeURIComponent(locationId)}`;
  const res = await fetch(url, authOpts);
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      json.message || "Failed to load staffing request contact options",
    );
  }
  const payload = json.data || {};
  return {
    contacts: Array.isArray(payload.contacts) ? payload.contacts : [],
    supervisors: Array.isArray(payload.supervisors) ? payload.supervisors : [],
  };
}

function formatShiftStatHours(value) {
  return `${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}h`;
}

function formatShiftStatPercent(value) {
  return `${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
}

function ShiftsSummaryStatRow({
  compact = false,
  shiftsCount = 0,
  actualHours = 0,
  submittedPercent = 0,
  conflictCount = 0,
  className = "",
}) {
  const cardClass = `border-2 border-[#dc1f0033] w-full rounded-xl flex flex-col sm:flex-row sm:items-center justify-center sm:justify-between gap-1 sm:gap-2 ${
    compact ? "p-2 h-auto sm:h-14" : "p-3 h-auto sm:h-16"
  }`;
  const labelClass = `${compact ? "text-[11px]" : "text-sm"} leading-tight text-[#2c3b52] min-w-0 text-center sm:text-left`;
  const valueClass = `shrink-0 whitespace-nowrap text-[#DC1F00] font-semibold text-center sm:text-right ${
    compact ? "text-sm" : "text-base"
  }`;

  const items = [
    {
      key: "shifts",
      label: "Shifts",
      value: Number(shiftsCount).toLocaleString(),
    },
    {
      key: "hours",
      label: "Actual Shift Hours",
      value: formatShiftStatHours(actualHours),
    },
    {
      key: "submitted",
      label: "Actuals Submitted & Not in Conflict",
      value: formatShiftStatPercent(submittedPercent),
    },
    {
      key: "conflict",
      label: "Actuals in Conflict",
      value: Number(conflictCount).toLocaleString(),
    },
  ];

  return (
    <div
      className={`grid grid-cols-2 lg:grid-cols-4 gap-3 min-w-0 ${
        compact ? "mb-3 pb-0.5" : "mb-4"
      } ${className}`.trim()}
    >
      {items.map((item) => (
        <div key={item.key} className={cardClass}>
          <span className={`${labelClass} line-clamp-2`}>{item.label}</span>
          <span className={valueClass}>{item.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function Jobs({
  embedStaffRequestId: embedIdProp = null,
  embedPreviewRow = null,
  embedReplicateWeekWindow = null,
  onEmbedClose = null,
} = {}) {
  usePageMeta(embedIdProp ? "Schedule" : "Jobs");
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const embedFromUrl = searchParams.get("embed") === "1";
  const embedStaffRequestId =
    embedIdProp ||
    (embedFromUrl ? searchParams.get("staffRequestId") : null) ||
    null;
  const embedMode = Boolean(embedStaffRequestId);
  const embedUsesProps = Boolean(embedIdProp);
  const { canWriteShifts, canReadShiftActual, canWriteShiftActual } =
    useAppPermissions();
  const shiftsReadOnly = !canWriteShifts;
  const SHIFTS_CHECKIN_STATUS_OPTIONS = [
    { value: "confirmed", label: "Confirmed" },
    { value: "unassigned", label: "Unassigned" },
    { value: "tentative", label: "Tentative" },
    { value: "declined", label: "Unfilled" },
    { value: "deleted", label: "Deleted" },
    { value: "cancelled", label: "Cancelled" },
    { value: "future", label: "Future" },
    { value: "late", label: "Late" },
    { value: "checkIn", label: "Checked in" },
    { value: "signoffPending", label: "Pending signoff" },
    { value: "signoffPendingReview", label: "Pending signoff review" },
    { value: "noshow", label: "No Show" },
    { value: "signoffComplete", label: "Signoff complete" },
    { value: "not_submitted", label: "Not Submitted" },
    { value: "as_scheduled", label: "As Scheduled" },
    { value: "different", label: "Different" },
    { value: "no_show_actual", label: "No Show (Actual)" },
  ];

  const mapActualStateForApi = (value) => {
    const key = String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[\s-]/g, "");
    if (key === "notsubmitted") return "not_submitted";
    if (key === "asscheduled") return "as_scheduled";
    if (key === "noshow") return "no_show";
    return String(value || "").trim();
  };

  const normalizeShiftStatusToken = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[\s_]+/g, "");
  const SHIFT_STATE_STATUS_VALUES = new Set([
    "confirmed",
    "unassigned",
    "tentative",
    "declined",
    "deleted",
    "cancelled",
  ]);
  const CHECKIN_STATUS_VALUES = new Set([
    "future",
    "late",
    "checkin",
    "noshow",
    "signoffpending",
    "signoffpendingreview",
    "signoffcomplete",
  ]);
  const ACTUAL_STATUS_VALUES = new Set([
    "notsubmitted",
    "asscheduled",
    "different",
    "noshowactual",
    "noshow",
  ]);

  const buildShiftsQueryParams = (
    shiftsFiltersState,
    search = "",
    { filterClient, filterLocation } = {},
  ) => {
    const params = new URLSearchParams();
    params.set("search", search || "");

    if (shiftsFiltersState.fromDate) {
      params.set("date_from", shiftsFiltersState.fromDate);
    }
    if (shiftsFiltersState.toDate) params.set("date_to", shiftsFiltersState.toDate);

    const sortBy = String(shiftsFiltersState.sortBy || "scheduled");
    const sortDirection =
      String(shiftsFiltersState.sortDirection || "asc").toLowerCase() === "desc"
        ? "desc"
        : "asc";
    params.set("sortBy", sortBy);
    params.set("sortDirection", sortDirection);

    if (filterClient) params.set("client_id", String(filterClient));
    if (filterLocation) params.set("client_location_id", String(filterLocation));

    const selectedClients = Array.isArray(shiftsFiltersState.clients)
      ? shiftsFiltersState.clients.filter(Boolean)
      : [];
    const selectedClientLocations = Array.isArray(
      shiftsFiltersState.clientLocations,
    )
      ? shiftsFiltersState.clientLocations.filter(Boolean)
      : [];
    const selectedSupervisors = Array.isArray(
      shiftsFiltersState.shiftSupervisors,
    )
      ? shiftsFiltersState.shiftSupervisors.filter(Boolean)
      : [];

    if (selectedClients.length) {
      params.set("client_ids", selectedClients.join(","));
    } else if (shiftsFiltersState.client) {
      params.set("client_id", String(shiftsFiltersState.client));
    }
    if (selectedClientLocations.length) {
      params.set("client_location_ids", selectedClientLocations.join(","));
    } else if (shiftsFiltersState.clientLocation) {
      params.set("client_location_id", String(shiftsFiltersState.clientLocation));
    }
    if (
      Array.isArray(shiftsFiltersState.shiftStatus) &&
      shiftsFiltersState.shiftStatus.length
    ) {
      const normalized = shiftsFiltersState.shiftStatus
        .map((v) => normalizeShiftStatusToken(v))
        .filter(Boolean);
      const shiftStateStatuses = normalized.filter((v) =>
        SHIFT_STATE_STATUS_VALUES.has(v),
      );
      const checkinStatuses = normalized.filter((v) =>
        CHECKIN_STATUS_VALUES.has(v),
      );
      const actualStatuses = normalized
        .filter((v) => ACTUAL_STATUS_VALUES.has(v))
        .map((v) => {
          if (v === "notsubmitted") return "not_submitted";
          if (v === "asscheduled") return "as_scheduled";
          if (v === "noshowactual" || v === "noshow") return "no_show";
          return v;
        });

      if (checkinStatuses.length) params.set("checkinState", checkinStatuses.join(","));
      if (shiftStateStatuses.length) params.set("shiftState", shiftStateStatuses.join(","));
      if (actualStatuses.length) params.set("actualState", actualStatuses.join(","));
    }
    if (shiftsFiltersState.staff) {
      params.set("accountId", String(shiftsFiltersState.staff));
    }
    if (shiftsFiltersState.assignmentType === "Field Staff") {
      params.set("assignmentType", "fieldstaff");
    } else if (shiftsFiltersState.assignmentType === "OutSourced Agency") {
      params.set("assignmentType", "outsorucedagency");
    }
    if (shiftsFiltersState.clientPosition) {
      params.set("position_id", String(shiftsFiltersState.clientPosition));
    }
    if (shiftsFiltersState.outsourcedAgency) {
      params.set("outsourcedPayerId", String(shiftsFiltersState.outsourcedAgency));
    }
    if (selectedSupervisors.length) {
      params.set("supervisor_ids", selectedSupervisors.join(","));
    } else if (String(shiftsFiltersState.shiftSupervisor || "").trim()) {
      params.set("supervisorId", String(shiftsFiltersState.shiftSupervisor));
    }
    if (
      Array.isArray(shiftsFiltersState.actualTimeStatus) &&
      shiftsFiltersState.actualTimeStatus.length
    ) {
      params.set(
        "actualState",
        shiftsFiltersState.actualTimeStatus.map(mapActualStateForApi).join(","),
      );
    }
    if (shiftsFiltersState.actualTimeConflict === "yes") {
      params.set("actualTimeConflict", "1");
    } else if (shiftsFiltersState.actualTimeConflict === "no") {
      params.set("actualTimeConflict", "0");
    }
    if (shiftsFiltersState.travelTime === "yes") {
      params.set("travelTimeAdd", "1");
    } else if (shiftsFiltersState.travelTime === "no") {
      params.set("travelTimeAdd", "0");
    }
    if (shiftsFiltersState.timesheetSignOffDate) {
      params.set(
        "checkin_signoff_date",
        shiftsFiltersState.timesheetSignOffDate,
      );
    }
    if (shiftsFiltersState.checkInNote) {
      params.set("checkInNote", shiftsFiltersState.checkInNote);
    }
    if (
      Array.isArray(shiftsFiltersState.checkInNoteType) &&
      shiftsFiltersState.checkInNoteType.length
    ) {
      params.set(
        "checkInNoteType",
        shiftsFiltersState.checkInNoteType.join(","),
      );
    }
    return params;
  };

  // Helper: initials and avatar color (copied from client dashboard/personnel)
  const getInitials = (name = "") => {
    const safeName = (name || "").toString();
    const parts = safeName.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0]?.slice(0, 2).toUpperCase() || "NA";
  };

  const getAvatarColorClass = (name) => {
    const palette = [
      "bg-red-600",
      "bg-red-500",
      "bg-red-400",
      "bg-green-600",
      "bg-green-500",
      "bg-blue-600",
      "bg-blue-500",
      "bg-yellow-500",
      "bg-purple-500",
      "bg-gray-600",
    ];
    const str = (name || "").toString();
    if (!str) return "bg-slate-600 text-white";
    let hash = 0;
    for (let i = 0; i < str.length; i++)
      hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
    return palette[hash % palette.length];
  };

  const AvatarSmall = ({ name, src, sizeClass = "w-9 h-9" }) => {
    const resolvedSrc = resolveBackendUrl(src);
    return (
      <div
        className={`${sizeClass} flex-shrink-0 rounded-full flex items-center justify-center overflow-hidden mr-2 border border-gray-200 bg-white`}
        title={name}
      >
        <img
          src={resolvedSrc || defaultProfilePic}
          alt={name || "Staff"}
          className="w-full h-full object-cover"
          onError={(e) => {
            if (e.currentTarget.src !== defaultProfilePic) {
              e.currentTarget.src = defaultProfilePic;
            }
          }}
        />
      </div>
    );
  };

  const PlaceholderAvatar = ({ sizeClass = "w-9 h-9" }) => (
    <div className={`${sizeClass} flex-shrink-0 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 mr-2`}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        className="w-5 h-5"
        fill="currentColor"
      >
        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
      </svg>
    </div>
  );

  // Helpers: format time range and duration similar to ClientDashboard
  const formatTime = (value) => {
    if (!value) return "";
    try {
      const formatted = formatAppTime(value, { emptyValue: "" });
      if (formatted) {
        return formatted.replace(":", ".").toLowerCase();
      }
      const parts = String(value).match(/(\d{1,2}):(\d{2})/);
      if (parts) {
        const hh = parseInt(parts[1], 10);
        const mm = parts[2];
        const tmp = parseDbDateTime(
          `2000-01-01 ${String(hh).padStart(2, "0")}:${mm}:00Z`,
        );
        const t = formatAppTime(tmp, { emptyValue: "" });
        return t ? t.replace(":", ".").toLowerCase() : value;
      }
      return value;
    } catch (e) {
      return value;
    }
  };

  const formatTimeRange = (from, to) => {
    if (!from && !to) return "";
    const f = formatTime(from);
    const t = formatTime(to);
    if (f && t) return `${f} - ${t}`;
    if (f) return f;
    return to || "";
  };

  const formatStaffingRequestShiftTime = (startTime, endTime) => {
    const formatValue = (val) =>
      val ? formatAppDateTime(val, { emptyValue: "" }) : "";

    const start = formatValue(startTime);
    const end = formatValue(endTime);
    if (!start && !end) return "—";
    if (!end) return start;
    if (!start) return end;

    const startParts = start.split(/\s+/);
    const endParts = end.split(/\s+/);
    if (
      startParts.length >= 4 &&
      endParts.length >= 4 &&
      startParts[0] === endParts[0] &&
      startParts[1] === endParts[1] &&
      startParts[2] === endParts[2]
    ) {
      const dayLabel = `${startParts[0]} ${startParts[1].replace(",", "")}`;
      const startClock = startParts.slice(3).join(" ");
      const endClock = endParts.slice(3).join(" ");
      return `${dayLabel} ${startClock}–${endClock}`;
    }

    return `${start}–${end}`;
  };

  const formatScheduledLine1 = (shift) => {
    const startVal = shift.start_date || shift.startDateIso;
    const endVal = shift.end_date || shift.endDateIso;

    if (!startVal) return shift.scheduledLine1 || shift.scheduled || "—";

    try {
      const startDate = parseDbDateTime(startVal);
      if (!startDate) {
        return shift.scheduledLine1 || shift.scheduled || "—";
      }

      const dayLabel = formatAppIntlLabel(startDate, {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
      const startTimeStr = formatAppTime(startDate, { emptyValue: "" });

      if (!endVal) {
        return `${dayLabel} · ${startTimeStr}`;
      }

      const endDate = parseDbDateTime(endVal);
      if (!endDate) {
        return `${dayLabel} · ${startTimeStr}`;
      }

      const endTimeStr = formatAppTime(endDate, { emptyValue: "" });
      return `${dayLabel} · ${startTimeStr} – ${endTimeStr}`;
    } catch {
      return shift.scheduledLine1 || shift.scheduled || "—";
    }
  };

  const formatStaffingRequestTimeRange = (req) => {
    const startVal = req.min_start_date;
    const endVal = req.max_end_date;
    const createVal = req.create_date;

    const startDate = parseDbDateTime(startVal);
    const endDate = parseDbDateTime(endVal);

    if (startDate && endDate) {
      return `${formatAppDateTime(startDate)} – ${formatAppDateTime(endDate)}`;
    }

    const createDate = parseDbDateTime(createVal);
    if (createDate) {
      return formatAppDateTime(createDate);
    }

    return req.time || "—";
  };

  const formatStaffingRequestShifts = (req) => {
    const hours = Number(
      req?.scheduledHours ?? req?.scheduled_hours ?? req?.total_hours ?? req?.hours ?? 0,
    );
    const count = Number(
      req?.shifts ?? req?.shift_count ?? req?.shiftsCount ?? 0,
    );
    const hoursText = Number.isFinite(hours) ? `${hours}h` : "0h";
    const shiftWord = count === 1 ? "shift" : "shifts";
    return `${hoursText} (${count} ${shiftWord})`;
  };

  const formatCreatedDate = (req) => {
    const val = req?.create_date || req?.time;
    if (!val) return req?.created || "—";

    const d = parseDbDateTime(val);
    if (!d) return req?.created || req?.time || "—";

    return formatAppDateTime(d);
  };

  const formatDuration = (val) => {
    if (val === null || val === undefined || val === "") return "";
    // Accept numeric hours (e.g., 7.75), strings like '7.5h', or '7h 45m'
    if (typeof val === "number") {
      const hours = Math.floor(val);
      const mins = Math.round((val - hours) * 60);
      return mins ? `${hours}h ${mins}m` : `${hours}h`;
    }
    let s = String(val).trim();
    // already human readable
    if (/h|m/.test(s) && !/^\d+(?:\.\d+)?$/.test(s)) return s;
    // numeric string
    const num = parseFloat(s.replace(/[^0-9.]/g, ""));
    if (!isNaN(num)) {
      const hours = Math.floor(num);
      const mins = Math.round((num - hours) * 60);
      return mins ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return s;
  };

  // Try common field names for start/end times in returned timesheet objects
  const getStartField = (t) =>
    t?.start_date ||
    t?.startDateIso ||
    t?.actualStartDate ||
    t?.scheduledStart ||
    t?.scheduled_start ||
    t?.actualStart ||
    t?.actual_start ||
    t?.start_time ||
    t?.from ||
    t?.start ||
    t?.startTime ||
    t?.shift_start ||
    "";
  const getEndField = (t) =>
    t?.scheduledEnd ||
    t?.scheduled_end ||
    t?.actualEnd ||
    t?.actual_end ||
    t?.end_time ||
    t?.to ||
    t?.end ||
    t?.endTime ||
    t?.shift_end ||
    "";

  // Mock timesheet data when backend isn't available (match ClientDashboard format)
  const mockTimesheets = [
    {
      date: null,
      slots: [
        {
          time: "12:00am - 1:00am",
          duration: "0.5h",
          positions: [{ role: "Bar Back 1", temp: "Unassigned" }],
        },
        {
          time: "9:00am - 10:00am",
          duration: "0.5h",
          positions: [{ role: "Bar Back 1", temp: "ceylonx qa" }],
        },
        {
          time: "12:00pm - 1:00pm",
          duration: "24.5h",
          positions: [{ role: "Bar Back 1", temp: "Unassigned" }],
        },
        {
          time: "1:00pm - 2:00pm",
          duration: "1h",
          positions: [
            { role: "Cleaner 1", temp: "Test Ceylonx qa" },
            { role: "Cleaner 2", temp: "Unassigned" },
            { role: "Cleaner 3", temp: "Unassigned" },
          ],
        },
        {
          time: "3:00pm - 4:00pm",
          duration: "0h",
          positions: [{ role: "Bar Back 1", temp: "Unassigned" }],
        },
      ],
    },
  ];



  const [selectedTab, setSelectedTab] = useState("STAFFINGREQUESTS");
  const filterPanel = useFilterPanel();
  const staffingRequestShiftsFilterPanel = useFilterPanel();
  const jobsFilterPanelOpen = filterPanel.isOpen;
  const shiftsFilterPanelOpen =
    (filterPanel.isOpen && selectedTab === "SHIFTS") ||
    staffingRequestShiftsFilterPanel.isOpen;
  const [data, setData] = useState([]);
  /** Which tab produced the current `data` rows (avoids showing staffing rows in the Shifts table). */
  const [dataTab, setDataTab] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [activeSidebarTab, setActiveSidebarTab] = useState("Details");
  // select request
  const [editableRequest, setEditableRequest] = useState({});
  const cleanId = (val) => {
    if (val === 0 || val === "0") return "0";
    if (val === null || val === undefined) return "";
    const normalized = String(val).trim();
    if (
      !normalized ||
      normalized === "undefined" ||
      normalized === "null"
    ) {
      return "";
    }
    return normalized;
  };

useEffect(() => {
  if (!selectedRequest) return;

  setEditableRequest({
    ...selectedRequest,
    contact_account_id: cleanId(selectedRequest.contact_account_id),
    supervisor_contact_account_id: cleanId(
      selectedRequest.supervisor_contact_account_id,
    ),
    division_id: cleanId(selectedRequest.division_id),
    client_location_id: cleanId(selectedRequest.client_location_id),
  });
}, [selectedRequest]);

  const [searchTermByTab, setSearchTermByTab] = usePersistentPageState(
    "page_search_jobs",
    {
      STAFFINGREQUESTS: "",
      SHIFTS: "",
      TIMESHEETS: "",
      SHIFTASSIGNMENT: "",
    }
  );
  const searchTerm = searchTermByTab[selectedTab] ?? "";
  const setSearchTerm = (value) => {
    setSearchTermByTab((prev) => ({
      ...prev,
      [selectedTab]: value,
    }));
  };
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState(null);
  const [shiftsData, setShiftsData] = useState(null);
  const [showCreateShiftModal, setShowCreateShiftModal] = useState(false);
  const [duplicateShiftData, setDuplicateShiftData] = useState(null);
  /** Kebab Duplicate: open Duplicate Shift only (no Overview modal) */
  const [standaloneDuplicateShiftId, setStandaloneDuplicateShiftId] =
    useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [showShiftAssignmentPanel, setShowShiftAssignmentPanel] = useState(false);
  const [shiftAssignmentPanelShift, setShiftAssignmentPanelShift] = useState(null);
  const [shiftAssignmentPanelEntry, setShiftAssignmentPanelEntry] = useState(null);
  const [shiftAssignmentPanelAssignment, setShiftAssignmentPanelAssignment] =
    useState(null);
  const [showPersonnelModal, setShowPersonnelModal] = useState(false);
  const [selectedPersonnel, setSelectedPersonnel] = useState(null);
  const [unassignModal, setUnassignModal] = useState({
    open: false,
    shiftId: null,
    accountId: null,
    staffName: "",
    countTowardsReliability: true,
    reason: "Other",
    notice24h: "no",
    availability: "available",
    note: "",
    submitting: false,
  });
  const [selectedShiftIds, setSelectedShiftIds] = useState([]);
  const [showShiftActionsMenu, setShowShiftActionsMenu] = useState(false);
  const [showBulkShiftUpdateModal, setShowBulkShiftUpdateModal] =
    useState(false);
  const [bulkShiftScope, setBulkShiftScope] = useState("request"); // request | main
  const [showBulkDivisionPositionModal, setShowBulkDivisionPositionModal] =
    useState(false);
  const shiftActionsMenuRef = useRef(null);
  // options for the bulk-update status dropdown; originally the same set
  // used by the individual shift editor. Values correspond to the 'state'
  // column stored in the database (some are derived by queries).
  const BULK_STATUS_OPTIONS = [
    { value: "unassigned", label: "Unassigned" },
    { value: "tentative", label: "Tentative" },
    { value: "confirmed", label: "Confirmed" },
    { value: "cancelled", label: "Cancelled" },
    { value: "declined", label: "Unfilled" },
    { value: "deleted", label: "Deleted" },
  ];

  const [bulkShiftUpdateForm, setBulkShiftUpdateForm] = useState({
    applyStatus: false,
    state: "",
    applyTimeRange: false,
    start_date: "",
    end_date: "",
    applyBreak: false,
    unpaid_break: false, // boolean: break yes/no
    notify_staff_change: false,
    // track whether the notify checkbox has been touched so we know if we should
    // include it in the update payload. this replaces the old `applyNotify` flag.
    notifyTouched: false,
  });
  const [bulkDivisionPositionForm, setBulkDivisionPositionForm] = useState({
    client_id: "", // newly added client selector value
    division_id: "",
    position_id: "",
  });
  // options shown in the modal
  const [bulkClientOptions, setBulkClientOptions] = useState([]);
  const [bulkModalDivisions, setBulkModalDivisions] = useState([]);
  const [bulkModalPositions, setBulkModalPositions] = useState([]);
  const [bulkModalAllPositions, setBulkModalAllPositions] = useState([]);

  // fetch helper used by bulk modal
  const fetchBulkClients = async () => {
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      const res = await fetch(
        `${BASE_URL}/api/clients/clients?all=1&search=`,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      );
      if (!res.ok) throw new Error(`Clients fetch failed: ${res.status}`);
      const data = await res.json();
      const rows = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [];
      const mapped = rows
        .map((item) => {
          const value = item?.value ?? item?.id ?? item?.client_id;
          const label = item?.label ?? item?.name;
          if (!value || !label) return null;
          return { value: String(value), label: String(label) };
        })
        .filter(Boolean);
      setBulkClientOptions(mapped);
    } catch (err) {
      console.error("Failed to fetch bulk modal clients", err);
      setBulkClientOptions([]);
    }
  };

  const fetchBulkDivisions = async (clientId) => {
    if (!clientId) {
      setBulkModalDivisions([]);
      return;
    }
    try {
      let divisionsData = [];
      try {
        const resp = await fetch(
          `${BASE_URL}/api/clients/divisions/by-client/${clientId}`,
        );
        const json = await resp.json();
        divisionsData = json.data || [];
      } catch {
        // fallback to alternate endpoint
        const resp = await fetch(
          `${BASE_URL}/api/clients/${clientId}/divisions`,
        );
        const json = await resp.json();
        divisionsData = json.data || [];
      }
      setBulkModalDivisions(Array.isArray(divisionsData) ? divisionsData : []);
    } catch (err) {
      console.error("Error fetching divisions for client", err);
      setBulkModalDivisions([]);
    }
  };

  const fetchBulkPositions = async (clientId, divisionId) => {
    if (!clientId) {
      setBulkModalPositions([]);
      setBulkModalAllPositions([]);
      return;
    }
    try {
      const params = divisionId ? `?division_id=${divisionId}` : "";
      const resp = await fetch(
        `${BASE_URL}/api/clients/${clientId}/positions${params}`,
      );
      const json = await resp.json();
      const rows = json.data || [];
      // dedup by id
      const seen = new Set();
      const list = rows.filter((p) => {
        const id = p?.position_id || p?.id || p?.posId;
        if (!id) return false;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      if (!divisionId) {
        setBulkModalAllPositions(list);
      }
      setBulkModalPositions(list);
    } catch (err) {
      console.error("Error fetching positions for client", err);
      setBulkModalPositions([]);
      if (!divisionId) setBulkModalAllPositions([]);
    }
  };

  // load clients when modal opens
  useEffect(() => {
    if (showBulkDivisionPositionModal) {
      fetchBulkClients();
      setBulkModalDivisions([]);
      setBulkModalPositions([]);
      setBulkModalAllPositions([]);
      setBulkDivisionPositionForm({
        client_id: "",
        division_id: "",
        position_id: "",
      });
    }
  }, [showBulkDivisionPositionModal]);

  // when client changes, fetch new divisions and positions
  useEffect(() => {
    const cid = bulkDivisionPositionForm.client_id;
    if (!cid) {
      setBulkModalDivisions([]);
      setBulkModalPositions([]);
      setBulkModalAllPositions([]);
      return;
    }
    fetchBulkDivisions(cid);
    fetchBulkPositions(cid);
  }, [bulkDivisionPositionForm.client_id]);

  // when division changes, refine positions
  useEffect(() => {
    if (!bulkDivisionPositionForm.client_id) return;
    if (bulkDivisionPositionForm.division_id) {
      fetchBulkPositions(
        bulkDivisionPositionForm.client_id,
        bulkDivisionPositionForm.division_id,
      );
    } else {
      setBulkModalPositions(bulkModalAllPositions);
    }
  }, [bulkDivisionPositionForm.division_id, bulkModalAllPositions]);

  // State for shifts search and filters
  const [shiftsSearchQuery, setShiftsSearchQuery] = useState("");
  const createDefaultShiftsFilters = () => ({
    shiftStatus: [],
    clients: [],
    client: "",
    staff: "",
    assignmentType: "",
    fromDate: getAppTodayIso(),
    toDate: "",
    outsourcedAgency: "",
    clientLocation: "",
    clientLocations: [],
    shiftSupervisor: "",
    shiftSupervisors: [],
    actualTimeStatus: [],
    actualTimeConflict: "",
    travelTime: "",
    clientPosition: "",
    timesheetSignOffDate: "",
    checkInNote: "",
    checkInNoteType: [],
    time: "",
    sortBy: "",
    sortDirection: "asc",
  });
  const [actualTimeConflict, setActualTimeConflict] = useState("");
  const [shiftsFilters, setShiftsFilters] = usePersistentPageState(
    "page_shifts_filters_jobs",
    () => createDefaultShiftsFilters(),
  );
  const [pendingShiftsFilters, setPendingShiftsFilters] = useState(() =>
    createDefaultShiftsFilters(),
  );
  const { showEasterEgg: showAdminEasterEgg } = useDeleteKeyEasterEgg();
  const [legacyRepairLoading, setLegacyRepairLoading] = useState(false);
  const [legacyRepairAfterShiftId, setLegacyRepairAfterShiftId] = useState(0);
  const [legacyRepairPendingApply, setLegacyRepairPendingApply] =
    useState(false);

  useEffect(() => {
    const shiftId = location?.state?.shiftId;
    if (!shiftId) return;
    setSelectedTab("SHIFTS");
    setStandaloneDuplicateShiftId(null);
    setSelectedShift({ id: Number(shiftId) || shiftId });
  }, [location?.state?.shiftId]);

  // Dropdown options for shifts filter (must be declared before filteredShifts useMemo)
  const [staffFilterOptions, setStaffFilterOptions] = useState([]);
  const [staffFilterLoading, setStaffFilterLoading] = useState(false);
  const [staffFilterQuery, setStaffFilterQuery] = useState("");
  const [officeStaffFilterOptions, setOfficeStaffFilterOptions] = useState([]);
  const [officeStaffFilterLoading, setOfficeStaffFilterLoading] = useState(false);
  const [officeStaffFilterQuery, setOfficeStaffFilterQuery] = useState("");
  const staffLabelCacheRef = useRef(new Map());
  const officeStaffLabelCacheRef = useRef(new Map());
  const [shiftsOutsourcedAgencyOptions, setShiftsOutsourcedAgencyOptions] =
    useState([]);
  const [shiftsClientLocationOptions, setShiftsClientLocationOptions] =
    useState([]);
  const [shiftsClientOptions, setShiftsClientOptions] = useState([]);
  const [shiftClientLocationOptions, setShiftClientLocationOptions] = useState(
    [],
  );
  const [shiftsSupervisorOptions, setShiftsSupervisorOptions] = useState([]);
  const [shiftsClientPositionOptions, setShiftsClientPositionOptions] =
    useState([]);
  const [staffOptions, setStaffOptions] = useState([]);

  const cacheStaffFilterLabels = React.useCallback((options = []) => {
    for (const option of options) {
      if (option?.value) {
        staffLabelCacheRef.current.set(
          String(option.value),
          option.label || String(option.value),
        );
      }
    }
  }, []);

  const cacheOfficeStaffFilterLabels = React.useCallback((options = []) => {
    for (const option of options) {
      if (option?.value) {
        officeStaffLabelCacheRef.current.set(
          String(option.value),
          option.label || String(option.value),
        );
      }
    }
  }, []);

  const mergeStaffFilterOptions = React.useCallback(
    (selectedIds = []) => {
      const merged = new Map();
      for (const option of staffFilterOptions) {
        merged.set(String(option.value), option);
      }
      for (const id of selectedIds) {
        const key = String(id);
        if (!key || merged.has(key)) continue;
        merged.set(key, {
          value: key,
          label: staffLabelCacheRef.current.get(key) || `Staff #${key}`,
        });
      }
      return Array.from(merged.values());
    },
    [staffFilterOptions],
  );

  const mergeOfficeStaffFilterOptions = React.useCallback(
    (selectedIds = []) => {
      const merged = new Map();
      for (const option of officeStaffFilterOptions) {
        merged.set(String(option.value), option);
      }
      for (const id of selectedIds) {
        const key = String(id);
        if (!key || merged.has(key)) continue;
        merged.set(key, {
          value: key,
          label: officeStaffLabelCacheRef.current.get(key) || `Staff #${key}`,
        });
      }
      return Array.from(merged.values());
    },
    [officeStaffFilterOptions],
  );

  const [shiftStates, setShiftStates] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);

  const handleApplyStatusChange = (e) => {
    setBulkShiftUpdateForm((prev) => ({
      ...prev,
      applyStatus: e.target.checked,
    }));
  };

  // Filtered shifts based on current filter state (syncs with filter drawer)
  const filteredShifts = React.useMemo(() => {
    // Main Jobs → Shifts tab: rows and filters are fully server-side.
    if (
      selectedTab === "SHIFTS" &&
      dataTab === "SHIFTS" &&
      Array.isArray(data)
    ) {
      return data;
    }

    const sourceShifts = Array.isArray(shiftsData?.shifts)
      ? shiftsData.shifts
      : [];
    if (!sourceShifts.length) return [];

    let filtered = [...sourceShifts];

    const skipClientTextSearch = false;
    const searchQuery = skipClientTextSearch
      ? ""
      : (shiftsSearchQuery || "").toLowerCase().trim();
    if (searchQuery) {
      filtered = filtered.filter((shift) => {
        const searchFields = [
          // Shift ID
          String(shift.shiftId || shift.shift_id || shift.id || ""),
          // Shift Position
          String(shift.position || shift.position_name || ""),
          // Client Name
          String(
            shift.client ||
              shift.client_name ||
              shift.clientName ||
              shift.client_name_text ||
              "",
          ),
          // Staff Request ID
          String(shift.staff_request_id || shift.staffRequestId || ""),
          // Staff First Name
          String(shift.assigned_staff_first || shift.staff_first_name || ""),
          // Staff Last Name
          String(shift.assigned_staff_last || shift.staff_last_name || ""),
          // Staff Full Name
          String(
            shift.assigned_staff ||
              shift.staffDisplay ||
              shift.staff ||
              shift.staff_name ||
              "",
          ),
        ];

        return searchFields.some((field) =>
          field.toLowerCase().includes(searchQuery),
        );
      });
    }

    // Shift Status filter
    if (shiftsFilters.shiftStatus && shiftsFilters.shiftStatus.length > 0) {
      const selectedStatuses = shiftsFilters.shiftStatus
        .map((value) => normalizeShiftStatusToken(value))
        .filter(Boolean);
      filtered = filtered.filter(
        (shift) => {
          const normalizedShiftState = normalizeShiftStatusToken(
            shift.state || shift.shift_state || shift.status || "",
          );
          const normalizedCheckinState = normalizeShiftStatusToken(
            shift.checkInState || shift.checkinState || shift.check_in_state || "",
          );
          const normalizedActualState = normalizeShiftStatusToken(
            shift.actualState ||
              shift.actual_state ||
              shift.actual_time_status ||
              "",
          );
          return (
            (normalizedShiftState && selectedStatuses.includes(normalizedShiftState)) ||
            (normalizedCheckinState &&
              selectedStatuses.includes(normalizedCheckinState)) ||
            (normalizedActualState && selectedStatuses.includes(normalizedActualState))
          );
        },
      );
    }

    // Staff filter (dropdown value is staff id; shift has assigned_staff name)
    if (shiftsFilters.staff) {
      const selectedStaffId = String(shiftsFilters.staff);
      const staffLabel =
        staffOptions.find((o) => String(o.value) === selectedStaffId)?.label ||
        staffFilterOptions.find((o) => String(o.value) === selectedStaffId)
          ?.label ||
        staffLabelCacheRef.current.get(selectedStaffId) ||
        "";
      const staffNameMatch = String(staffLabel || "").toLowerCase();

      filtered = filtered.filter((shift) => {
        const assignedName = String(
          shift.assigned_staff || shift.staff_name || shift.staff || shift.staffDisplayName || "",
        ).toLowerCase();
        const assignedId = String(
          shift.assigned_staff_id ||
            shift.staff_id ||
            shift.account_id ||
            shift.accountId ||
            shift.personnel_account_id ||
            "",
        );
        if (assignedId) return assignedId === selectedStaffId;
        if (staffNameMatch) return assignedName.includes(staffNameMatch);
        return false;
      });
    }

    // Assignment Type filter (single block)
    if (shiftsFilters.assignmentType) {
      const isField = ["Field Staff", "field"].includes(
        shiftsFilters.assignmentType,
      );
      const isOutsourced = [
        "Outsourced Staff",
        "Outsourced Agency",
        "OutSourced Agency",
        "outsourced",
      ].includes(shiftsFilters.assignmentType);
      if (isField) {
        filtered = filtered.filter((shift) => {
          const assignedId = String(
            shift.assigned_staff_id || shift.accountId || shift.account_id || "",
          ).trim();
          const assignedLabel = String(
            shift.assigned_staff ||
              shift.staffDisplay ||
              shift.staff ||
              shift.staff_name ||
              "",
          ).trim();
          return (
            assignedId !== "" &&
            assignedLabel !== "" &&
            assignedLabel.toLowerCase() !== "unassigned"
          );
        });
      } else if (isOutsourced) {
        filtered = filtered.filter(
          (shift) =>
            String(
              shift.outsourced_payer_id ||
                shift.outsourcedPayerId ||
                shift.outsourcedAgencyId ||
                "",
            ).trim() !== "",
        );
      }
    }

    // Date range filters
    if (shiftsFilters.fromDate || shiftsFilters.toDate) {
      filtered = filtered.filter((shift) => {
        const rawDate =
          shift.start_date ||
          shift.startDateIso ||
          shift.startDate ||
          shift.actualStartDate ||
          shift.scheduledStart ||
          shift.start_time ||
          shift.shift_start;
        if (!rawDate) return false;
        const shiftDate = new Date(rawDate);
        if (isNaN(shiftDate)) return false;

        if (shiftsFilters.fromDate) {
          const fromDate = new Date(shiftsFilters.fromDate);
          fromDate.setHours(0, 0, 0, 0);
          if (shiftDate < fromDate) return false;
        }
        if (shiftsFilters.toDate) {
          const toDate = new Date(shiftsFilters.toDate);
          toDate.setHours(23, 59, 59, 999);
          if (shiftDate > toDate) return false;
        }
        return true;
      });
    }

    // Client filter (supports multi-select)
    const selectedClients = Array.isArray(shiftsFilters.clients)
      ? shiftsFilters.clients
      : [];
    const clientValues = selectedClients.length
      ? selectedClients
      : shiftsFilters.client
        ? [shiftsFilters.client]
        : [];

    if (clientValues.length) {
      const clientMetaList = clientValues
        .map((value) => {
          const clientLabel = shiftsClientOptions.find(
            (o) => String(o.value) === String(value),
          )?.label;
          return {
            id: String(value),
            label: String(clientLabel || "").toLowerCase(),
          };
        })
        .filter((item) => item.id || item.label);

      if (clientMetaList.length) {
        filtered = filtered.filter((shift) => {
          const shiftClientName = String(
            shift.client ||
              shift.client_name ||
              shift.clientName ||
              shift.client_name_text ||
              "",
          ).toLowerCase();
          const shiftClientId = String(
            shift.client_id || shift.clientId || shift.setting_client_id || "",
          );
          return clientMetaList.some(
            (item) =>
              (shiftClientId && item.id && shiftClientId === item.id) ||
              (item.label && shiftClientName.includes(item.label)),
          );
        });
      }
    }

    // Client Location filter (dropdown value may be id; shift has location string)
    const selectedLocations = Array.isArray(shiftsFilters.clientLocations)
      ? shiftsFilters.clientLocations
      : [];
    const locationValues = selectedLocations.length
      ? selectedLocations
      : shiftsFilters.clientLocation
        ? [shiftsFilters.clientLocation]
        : [];

    if (locationValues.length) {
      const locationMetaList = locationValues
        .map((value) => {
          const matched =
            shiftClientLocationOptions.find(
              (o) => String(o.value) === String(value),
            ) ||
            shiftsClientLocationOptions.find(
              (o) => String(o.value) === String(value),
            );
          return {
            id: String(value),
            label: String(matched?.label || "").toLowerCase(),
          };
        })
        .filter((item) => item.id || item.label);

      if (locationMetaList.length) {
        filtered = filtered.filter((shift) => {
          const locationName = String(
            shift.location ||
              shift.client_location ||
              shift.clientLocationName ||
              "",
          ).toLowerCase();
          const locationId = String(
            shift.client_location_id ||
              shift.location_id ||
              shift.setting_client_location_id ||
              "",
          );
          return locationMetaList.some(
            (item) =>
              (locationId && item.id && locationId === item.id) ||
              (item.label && locationName.includes(item.label)),
          );
        });
      }
    }

    // Shift Supervisor filter (supports multi-select)
    const selectedSupervisors = Array.isArray(shiftsFilters.shiftSupervisors)
      ? shiftsFilters.shiftSupervisors
      : [];
    const supervisorValues = selectedSupervisors.length
      ? selectedSupervisors
      : shiftsFilters.shiftSupervisor
        ? [shiftsFilters.shiftSupervisor]
        : [];

    if (supervisorValues.length) {
      const supervisorMatchList = supervisorValues
        .map((value) => {
          const supervisorLabel = shiftsSupervisorOptions.find(
            (o) => String(o.value) === String(value),
          )?.label;
          return String(supervisorLabel || value || "").toLowerCase();
        })
        .filter(Boolean);

      filtered = filtered.filter((shift) => {
        const name = String(shift.supervisor_name || "").toLowerCase();
        const id = String(
          shift.supervisor_id || shift.supervisor_contact_account_id || "",
        );
        return (
          supervisorValues.some((value) => id === String(value)) ||
          supervisorMatchList.some((matchStr) => name.includes(matchStr))
        );
      });
    }

    // Client Position filter
    if (shiftsFilters.clientPosition) {
      filtered = filtered.filter(
        (shift) =>
          shift.position_id != null &&
          String(shift.position_id) === String(shiftsFilters.clientPosition),
      );
    }

    // Outsourced Agency (when shift has outsourced_payer_id or agency name)
    if (shiftsFilters.outsourcedAgency) {
      const selectedAgencyId = String(shiftsFilters.outsourcedAgency);
      const agencyLabel = shiftsOutsourcedAgencyOptions.find(
        (o) => String(o.value) === selectedAgencyId,
      )?.label;
      const matchStr = (agencyLabel || shiftsFilters.outsourcedAgency || "")
        .toString()
        .toLowerCase();
      if (matchStr) {
        filtered = filtered.filter((shift) => {
          const agency = String(
            shift.outsourced_agency_name || shift.outsourced_payer_id || "",
          ).toLowerCase();
          const agencyId = String(
            shift.outsourced_agency_id ||
              shift.outsourced_payer_id ||
              shift.outsourcedAgencyId ||
              "",
          );
          return (
            (agencyId && agencyId === selectedAgencyId) ||
            (agency && agency.includes(matchStr))
          );
        });
      }
    }

    // Actual Time Status filter (when shift has actual_state / actual_time_status)
    if (
      shiftsFilters.actualTimeStatus &&
      shiftsFilters.actualTimeStatus.length > 0
    ) {
      filtered = filtered.filter((shift) => {
        const status = (
          shift.actual_state ||
          shift.actual_time_status ||
          shift.actualState ||
          ""
        ).toString();
        return status && shiftsFilters.actualTimeStatus.includes(status);
      });
    }

    // Actual Time Conflict filter
    if (shiftsFilters.actualTimeConflict === "yes") {
      filtered = filtered.filter(
        (shift) => Number(shift.actual_time_conflict) === 1,
      );
    } else if (shiftsFilters.actualTimeConflict === "no") {
      filtered = filtered.filter(
        (shift) => Number(shift.actual_time_conflict) === 0,
      );
    }

    // Timesheet Sign Off Date filter (when shift has timesheet_sign_off_date)
    if (shiftsFilters.timesheetSignOffDate) {
      const targetDate = shiftsFilters.timesheetSignOffDate.replace(/-/g, "");
      filtered = filtered.filter((shift) => {
        const d = shift.timesheet_sign_off_date || shift.signOffDate;
        if (!d) return false;
        const sd = (d instanceof Date ? d.toISOString() : String(d))
          .slice(0, 10)
          .replace(/-/g, "");
        return sd === targetDate;
      });
    }

    // Check In Note filter (when shift has check-in note text)
    if (shiftsFilters.checkInNote && shiftsFilters.checkInNote.trim()) {
      const selectedValue = shiftsFilters.checkInNote.trim();
      const selectedNoteId = Number(selectedValue);
      const hasNoteId = Number.isFinite(selectedNoteId) && selectedNoteId > 0;
      const q = selectedValue.toLowerCase();
      filtered = filtered.filter((shift) => {
        if (hasNoteId) {
          const shiftNoteId = Number(
            shift.checkin_note_id ||
              shift.check_in_note_id ||
              shift.checkInNoteId ||
              0,
          );
          return Number.isFinite(shiftNoteId) && shiftNoteId === selectedNoteId;
        }
        const note = (
          shift.check_in_note ||
          shift.checkInNote ||
          shift.notes ||
          ""
        )
          .toString()
          .toLowerCase();
        return note.includes(q);
      });
    }

    // Check In Note Type filter
    if (
      shiftsFilters.checkInNoteType &&
      shiftsFilters.checkInNoteType.length > 0
    ) {
      const selectedTypes = shiftsFilters.checkInNoteType
        .map((v) => String(v))
        .filter(Boolean)
        .map((v) => v.trim().toLowerCase());
      filtered = filtered.filter((shift) => {
        const noteType = (
          shift.check_in_note_type ||
          shift.checkInNoteType ||
          ""
        )
          .toString()
          .toLowerCase();
        return noteType && selectedTypes.includes(noteType);
      });
    }

    // Travel Time: optional filter when shift has travel_time
    if (shiftsFilters.travelTime === "yes") {
      filtered = filtered.filter(
        (shift) => Number(shift.travel_time_add) === 1,
      );
    }

    if (shiftsFilters.travelTime === "no") {
      filtered = filtered.filter(
        (shift) => Number(shift.travel_time_add) !== 1,
      );
    }

    // Sort the results - (sorting is handled in sortedMainShiftsData)
    /*
      ? selectedLocations
      : shiftsFilters.clientLocation
        ? [shiftsFilters.clientLocation]
        : [];

    if (locationValues.length) {
      const locationMetaList = locationValues
        .map((value) => {
          const matched =
            shiftClientLocationOptions.find(
              (o) => String(o.value) === String(value),
            ) ||
            shiftsClientLocationOptions.find(
              (o) => String(o.value) === String(value),
            );
          return {
            id: String(value),
            label: String(matched?.label || "").toLowerCase(),
          };
        })
        .filter((item) => item.id || item.label);

      if (locationMetaList.length) {
        filtered = filtered.filter((shift) => {
          const locationName = String(
            shift.location ||
              shift.client_location ||
              shift.clientLocationName ||
              "",
          ).toLowerCase();
          const locationId = String(
            shift.client_location_id ||
              shift.location_id ||
              shift.setting_client_location_id ||
              "",
          );
          return locationMetaList.some(
            (item) =>
              (locationId && item.id && locationId === item.id) ||
              (item.label && locationName.includes(item.label)),
          );
        });
      }
    }

    // Shift Supervisor filter (supports multi-select)
    const selectedSupervisors = Array.isArray(shiftsFilters.shiftSupervisors)
      ? shiftsFilters.shiftSupervisors
      : [];
    const supervisorValues = selectedSupervisors.length
      ? selectedSupervisors
      : shiftsFilters.shiftSupervisor
        ? [shiftsFilters.shiftSupervisor]
        : [];

    if (supervisorValues.length) {
      const supervisorMatchList = supervisorValues
        .map((value) => {
          const supervisorLabel = shiftsSupervisorOptions.find(
            (o) => String(o.value) === String(value),
          )?.label;
          return String(supervisorLabel || value || "").toLowerCase();
        })
        .filter(Boolean);

      filtered = filtered.filter((shift) => {
        const name = String(shift.supervisor_name || "").toLowerCase();
        const id = String(
          shift.supervisor_id || shift.supervisor_contact_account_id || "",
        );
        return (
          supervisorValues.some((value) => id === String(value)) ||
          supervisorMatchList.some((matchStr) => name.includes(matchStr))
        );
      });
    }

    // Client Position filter
    if (shiftsFilters.clientPosition) {
      filtered = filtered.filter(
        (shift) =>
          shift.position_id != null &&
          String(shift.position_id) === String(shiftsFilters.clientPosition),
      );
    }

    // Outsourced Agency (when shift has outsourced_payer_id or agency name)
    if (shiftsFilters.outsourcedAgency) {
      const selectedAgencyId = String(shiftsFilters.outsourcedAgency);
      const agencyLabel = shiftsOutsourcedAgencyOptions.find(
        (o) => String(o.value) === selectedAgencyId,
      )?.label;
      const matchStr = (agencyLabel || shiftsFilters.outsourcedAgency || "")
        .toString()
        .toLowerCase();
      if (matchStr) {
        filtered = filtered.filter((shift) => {
          const agency = String(
            shift.outsourced_agency_name || shift.outsourced_payer_id || "",
          ).toLowerCase();
          const agencyId = String(
            shift.outsourced_agency_id ||
              shift.outsourced_payer_id ||
              shift.outsourcedAgencyId ||
              "",
          );
          return (
            (agencyId && agencyId === selectedAgencyId) ||
            (agency && agency.includes(matchStr))
          );
        });
      }
    }

    // Actual Time Status filter (when shift has actual_state / actual_time_status)
    if (
      shiftsFilters.actualTimeStatus &&
      shiftsFilters.actualTimeStatus.length > 0
    ) {
      filtered = filtered.filter((shift) => {
        const status = (
          shift.actual_state ||
          shift.actual_time_status ||
          shift.actualState ||
          ""
        ).toString();
        return status && shiftsFilters.actualTimeStatus.includes(status);
      });
    }

    // Actual Time Conflict filter
    if (shiftsFilters.actualTimeConflict === "yes") {
      filtered = filtered.filter(
        (shift) => Number(shift.actual_time_conflict) === 1,
      );
    } else if (shiftsFilters.actualTimeConflict === "no") {
      filtered = filtered.filter(
        (shift) => Number(shift.actual_time_conflict) === 0,
      );
    }

    // Timesheet Sign Off Date filter (when shift has timesheet_sign_off_date)
    if (shiftsFilters.timesheetSignOffDate) {
      const targetDate = shiftsFilters.timesheetSignOffDate.replace(/-/g, "");
      filtered = filtered.filter((shift) => {
        const d = shift.timesheet_sign_off_date || shift.signOffDate;
        if (!d) return false;
        const sd = (d instanceof Date ? d.toISOString() : String(d))
          .slice(0, 10)
          .replace(/-/g, "");
        return sd === targetDate;
      });
    }

    // Check In Note filter (when shift has check-in note text)
    if (shiftsFilters.checkInNote && shiftsFilters.checkInNote.trim()) {
      const selectedValue = shiftsFilters.checkInNote.trim();
      const selectedNoteId = Number(selectedValue);
      const hasNoteId = Number.isFinite(selectedNoteId) && selectedNoteId > 0;
      const q = selectedValue.toLowerCase();
      filtered = filtered.filter((shift) => {
        if (hasNoteId) {
          const shiftNoteId = Number(
            shift.checkin_note_id ||
              shift.check_in_note_id ||
              shift.checkInNoteId ||
              0,
          );
          return Number.isFinite(shiftNoteId) && shiftNoteId === selectedNoteId;
        }
        const note = (
          shift.check_in_note ||
          shift.checkInNote ||
          shift.notes ||
          ""
        )
          .toString()
          .toLowerCase();
        return note.includes(q);
      });
    }

    // Check In Note Type filter
    if (
      shiftsFilters.checkInNoteType &&
      shiftsFilters.checkInNoteType.length > 0
    ) {
      const selectedTypes = shiftsFilters.checkInNoteType
        .map((v) => String(v))
        .filter(Boolean)
        .map((v) => v.trim().toLowerCase());
      filtered = filtered.filter((shift) => {
        const noteType = (
          shift.check_in_note_type ||
          shift.checkInNoteType ||
          ""
        )
          .toString()
          .toLowerCase();
        return noteType && selectedTypes.includes(noteType);
      });
    }

    // Travel Time: optional filter when shift has travel_time
    if (shiftsFilters.travelTime === "yes") {
      filtered = filtered.filter(
        (shift) => Number(shift.travel_time_add) === 1,
      );
    }

    if (shiftsFilters.travelTime === "no") {
      filtered = filtered.filter(
        (shift) => Number(shift.travel_time_add) !== 1,
      );
    }

    */

    // Sorting:
    // - For the main Jobs → Shifts tab, sorting is handled elsewhere (server-side / separate view logic).
    // - For Staffing Request → Shifts, we must sort this local list to make Sort By/Direction work.
    if (selectedTab !== "SHIFTS" && filtered.length > 1) {
      const sortBy = String(shiftsFilters.sortBy || "scheduled").toLowerCase();
      const sortDirection = String(shiftsFilters.sortDirection || "asc").toLowerCase() === "desc"
        ? "desc"
        : "asc";

      const parseShiftDate = (shift) => {
        const raw =
          shift?.start_date ||
          shift?.startDateIso ||
          shift?.startDate ||
          shift?.scheduledStart ||
          shift?.start_time ||
          shift?.shift_start ||
          "";
        const d = raw ? new Date(raw) : null;
        return d && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
      };

      const getNameParts = (shift) => {
        const firstRaw =
          shift?.assigned_staff_first ||
          shift?.staff_first_name ||
          "";
        const lastRaw =
          shift?.assigned_staff_last ||
          shift?.staff_last_name ||
          "";
        const full =
          shift?.assigned_staff ||
          shift?.staffDisplay ||
          shift?.staff ||
          shift?.staff_name ||
          "";
        const fullStr = String(full || "").trim();
        const parts = fullStr ? fullStr.split(/\s+/) : [];
        const first = String(firstRaw || parts[0] || "").trim();
        const last = String(lastRaw || (parts.length ? parts[parts.length - 1] : "") || "").trim();
        return { first: first.toLowerCase(), last: last.toLowerCase() };
      };

      const cmp = (a, b) => {
        let av;
        let bv;
        switch (sortBy) {
          case "shift":
            av = Number(a?.shift_id ?? a?.shiftId ?? a?.id ?? 0);
            bv = Number(b?.shift_id ?? b?.shiftId ?? b?.id ?? 0);
            break;
          case "staff_first_name": {
            const ap = getNameParts(a);
            const bp = getNameParts(b);
            av = ap.first;
            bv = bp.first;
            break;
          }
          case "staff_last_name": {
            const ap = getNameParts(a);
            const bp = getNameParts(b);
            av = ap.last;
            bv = bp.last;
            break;
          }
          case "actuals_submission_status":
            av = String(a?.actual_state || a?.actual_time_status || a?.actualState || "").toLowerCase();
            bv = String(b?.actual_state || b?.actual_time_status || b?.actualState || "").toLowerCase();
            break;
          case "schedule_view":
          case "scheduled":
          default:
            av = parseShiftDate(a);
            bv = parseShiftDate(b);
            break;
        }

        if (typeof av === "string" && typeof bv === "string") {
          return av.localeCompare(bv);
        }
        return av === bv ? 0 : av < bv ? -1 : 1;
      };

      filtered.sort((a, b) => (sortDirection === "desc" ? -cmp(a, b) : cmp(a, b)));
    }

    return filtered;
  }, [
    data,
    selectedTab,
    shiftsData,
    shiftsFilters,
    shiftsClientOptions,
    shiftClientLocationOptions,
    staffFilterOptions,
    shiftsClientLocationOptions,
    shiftsSupervisorOptions,
    shiftsOutsourcedAgencyOptions,
    shiftsSearchQuery,
    staffOptions,
  ]);

  // Note: For SHIFTS tab, totalCount is driven by server-side pagination totals.
  // Avoid overriding it with client-side filtered counts, which can cause KPI/table
  // mismatches and "random" appearing counts.

  const selectedShiftRows = React.useMemo(() => {
    if (!filteredShifts?.length || !selectedShiftIds?.length) return [];
    const selected = new Set(selectedShiftIds.map((id) => String(id)));
    return filteredShifts.filter((shift) =>
      selected.has(String(shift?.shift_id ?? shift?.id ?? "")),
    );
  }, [filteredShifts, selectedShiftIds]);

  const allVisibleShiftsSelected =
    filteredShifts.length > 0 &&
    filteredShifts.every((shift) =>
      selectedShiftIds.includes(String(shift?.shift_id ?? shift?.id ?? "")),
    );

  const [defaultTodayFilterActive, setDefaultTodayFilterActive] =
    useState(false); // Shifts (legacy toggle hidden)

  // Shift Assignment: no "default filters" toggle; we always start with a 7-day window.
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [showPageSizeDropdown, setShowPageSizeDropdown] = useState(false);

  // Create Staffing Request Modal State
  const [showCreateRequestModal, setShowCreateRequestModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [createClientId, setCreateClientId] = useState("");
  const [clientLocations, setClientLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [createClientLocations, setCreateClientLocations] = useState([]);
  const [createSelectedLocationIds, setCreateSelectedLocationIds] = useState(
    [],
  );
  const [locationSearchTerm, setLocationSearchTerm] = useState("");
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [createLocationSearchTerm, setCreateLocationSearchTerm] = useState("");
  const [showCreateLocationDropdown, setShowCreateLocationDropdown] =
    useState(false);

  // Refs for click-outside handling
  const locationDropdownRef = useRef(null);
  const createLocationDropdownRef = useRef(null);
  const [contacts, setContacts] = useState([]);
  const [createContacts, setCreateContacts] = useState([]);
  const [createContactId, setCreateContactId] = useState("");
  const [supervisors, setSupervisors] = useState([]);
  const [createSupervisors, setCreateSupervisors] = useState([]);
  const [createSupervisorId, setCreateSupervisorId] = useState("");
  const [createNotes, setCreateNotes] = useState("");
  const [divisions, setDivisions] = useState([]);
  const [createDivisions, setCreateDivisions] = useState([]);
  const [createDivisionId, setCreateDivisionId] = useState("");
  const [createDefaultsLoading, setCreateDefaultsLoading] = useState(false);
  const [createErrors, setCreateErrors] = useState({
    client: "",
    location: "",
    contact: "",
    supervisor: "",
    division: "",
  });
  const [createRequestDirty, setCreateRequestDirty] = useState(false);
  const [showCreateRequestUnsavedPopup, setShowCreateRequestUnsavedPopup] =
    useState(false);
  const [showCreateRequestValidationAlert, setShowCreateRequestValidationAlert] =
    useState(false);
  const createRequestValidationTimerRef = useRef(null);
  const createRequestSubmitButtonRef = useRef(null);
  const createClientDefaultsRef = useRef({
    contactId: "",
    supervisorId: "",
  });
  const resetCreateRequestForm = () => {
    setCreateClientId("");
    setCreateClientLocations([]);
    setCreateSelectedLocationIds([]);
    setLocationSearchTerm("");
    setShowLocationDropdown(false);
    setCreateLocationSearchTerm("");
    setShowCreateLocationDropdown(false);
    setCreateContacts([]);
    setCreateContactId("");
    setCreateSupervisors([]);
    setCreateSupervisorId("");
    setCreateDivisions([]);
    setCreateDivisionId("");
    setCreateNotes("");
    setCreateDefaultsLoading(false);
    createClientDefaultsRef.current = { contactId: "", supervisorId: "" };
    setCreateErrors({
      client: "",
      location: "",
      contact: "",
      supervisor: "",
      division: "",
    });
    setCreateRequestDirty(false);
    setShowCreateRequestUnsavedPopup(false);
    setShowCreateRequestValidationAlert(false);
  };
  const triggerCreateRequestValidationAlert = () => {
    setShowCreateRequestValidationAlert(true);
    if (createRequestValidationTimerRef.current) {
      clearTimeout(createRequestValidationTimerRef.current);
    }
    createRequestValidationTimerRef.current = setTimeout(() => {
      setShowCreateRequestValidationAlert(false);
      createRequestValidationTimerRef.current = null;
    }, 4000);
  };
  const validateCreateRequestForm = () => {
    const errors = {};
    if (!createClientId) errors.client = "Please select a client";
    if (createSelectedLocationIds.length === 0) {
      errors.location = "Please select a location";
    }
    if (!createContactId) {
      errors.contact = "Please select a Default Shift Contact";
    }
    if (!createSupervisorId) {
      errors.supervisor = "Please select a Default Shift Supervisor";
    }
    if (!createDivisionId) {
      errors.division = "Please select a Default Shift Division";
    }

    setCreateErrors(errors);

    if (Object.keys(errors).length > 0) {
      triggerCreateRequestValidationAlert();
      return false;
    }

    setShowCreateRequestValidationAlert(false);
    return true;
  };
  const closeCreateRequestModal = () => {
    setShowCreateRequestModal(false);
    resetCreateRequestForm();
  };
  const attemptCloseCreateRequestModal = () => {
    if (createRequestDirty) {
      setShowCreateRequestUnsavedPopup(true);
      return;
    }
    closeCreateRequestModal();
  };
  const handleCreateRequestDiscardChanges = () => {
    setShowCreateRequestUnsavedPopup(false);
    closeCreateRequestModal();
  };
  const handleCreateRequestReviewChanges = () => {
    setShowCreateRequestUnsavedPopup(false);
  };
  const handleCreateRequestSaveContinue = () => {
    setShowCreateRequestUnsavedPopup(false);
    createRequestSubmitButtonRef.current?.click();
  };
  const getAccountOptionId = (item) =>
    item?.id ||
    item?.account_id ||
    item?.accountId ||
    item?.contactId ||
    item?.contact_id ||
    null;

  // staff_requests.contact_* columns are account_ids; API rows may use id OR account_id
  const getClientContactRowAccountId = (row) =>
    row?.account_id ??
    row?.accountId ??
    row?.id ??
    row?.contactId ??
    row?.contact_id ??
    null;
  const getSafeDisplayText = (...values) => {
    for (const value of values) {
      const normalized = String(value ?? "").trim();
      if (
        normalized &&
        normalized.toLowerCase() !== "undefined" &&
        normalized.toLowerCase() !== "null"
      ) {
        return normalized;
      }
    }
    return "";
  };
  const getAccountOptionLabel = (item, fallbackPrefix) => {
    if (!item) return "";
    const label = getSafeDisplayText(
      item.name,
      `${item.first_name || ""} ${item.last_name || ""}`.trim(),
      item.label,
    );
    if (label) return label;
    const id = getAccountOptionId(item);
    return id ? `${fallbackPrefix} #${id}` : "";
  };

  const [reloadKey, setReloadKey] = useState(0);

  // Refresh functions for each tab
  const fetchStaffingRequests = () => {
    setCurrentPage(1);
    setReloadKey((prev) => prev + 1);
  };

  const fetchShifts = () => {
    setCurrentPage(1);
    setReloadKey((prev) => prev + 1);
  };

  const requestLegacyShiftAssignmentRepair = async ({
    dryRun,
    afterShiftId = 0,
  }) => {
    const token =
      localStorage.getItem("token") || localStorage.getItem("authToken");
    const dateFrom = shiftsFilters.fromDate || getAppTodayIso();
    const dateTo = shiftsFilters.toDate || dateFrom;
    const resp = await fetch(
      `${BASE_URL}/api/jobs/shifts/repair-legacy-assignments`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          date_from: dateFrom,
          date_to: dateTo,
          dryRun: !!dryRun,
          after_shift_id: afterShiftId,
        }),
      },
    );
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok || json.success === false) {
      throw new Error(json.message || "Legacy shift repair failed");
    }
    return json;
  };

  const handleLegacyShiftAssignmentRepair = async () => {
    const dateFrom = shiftsFilters.fromDate || getAppTodayIso();
    const dateTo = shiftsFilters.toDate || dateFrom;

    if (
      !window.confirm(
        `Repair legacy shift assignments for ${dateFrom} to ${dateTo}? All shifts in this range will be reconciled in one run.`,
      )
    ) {
      return;
    }

    setLegacyRepairLoading(true);
    try {
      let afterShiftId = 0;
      let totalRepaired = 0;
      let totalSynced = 0;
      let totalScanned = 0;
      let totalConflicts = 0;
      let batches = 0;

      do {
        const result = await requestLegacyShiftAssignmentRepair({
          dryRun: false,
          afterShiftId,
        });
        batches += 1;
        totalRepaired += Number(result.repaired || 0);
        totalSynced += Number(result.synced || 0);
        totalScanned += Number(result.scanned || 0);
        totalConflicts += Number(result.conflicts || 0);
        afterShiftId =
          result.truncated && result.nextAfterShiftId != null
            ? Number(result.nextAfterShiftId)
            : 0;
      } while (afterShiftId > 0);

      setLegacyRepairAfterShiftId(0);
      setLegacyRepairPendingApply(false);

      showSuccess(
        `Repair complete: repaired ${totalRepaired}, synced ${totalSynced}, conflicts ${totalConflicts}, scanned ${totalScanned} (${batches} batch${batches === 1 ? "" : "es"}).`,
        "Repair legacy shifts",
      );
      fetchShifts();
      window.dispatchEvent(new CustomEvent("atlas:legacy-shift-repair-complete"));
    } catch (err) {
      console.error(err);
      showError(
        err.message || "Legacy shift repair failed",
        "Repair legacy shifts",
      );
    } finally {
      setLegacyRepairLoading(false);
    }
  };

  const fetchTimesheets = () => {
    setCurrentPage(1);
    setReloadKey((prev) => prev + 1);
  };

  const { openReplicateRequestModal, ReplicateModal } =
    useStaffingRequestReplicate({
      overlayZIndex: embedMode ? 50550 : 50400,
      onSuccess: () => {
        setCurrentPage(1);
        setReloadKey((prev) => prev + 1);
      },
      onOpenNewRequest: (detailRequest) => {
        setSelectedRequest(detailRequest);
        setActiveSidebarTab("Details");
      },
    });

  // FILTER DATA STATES — client / location scoped per Jobs tab (not shared across tabs)
  const [clientLocByTab, setClientLocByTab] = usePersistentPageState("page_client_loc_tab_jobs", initialJobsClientLocByTab);

  const patchClientLocTab = (tab, patch) => {
    setClientLocByTab((prev) => ({
      ...prev,
      [tab]: { ...(prev[tab] || emptyJobTabClientLoc()), ...patch },
    }));
  };

  const curClientLoc = clientLocByTab[selectedTab] || emptyJobTabClientLoc();
  const filterClient = curClientLoc.filterClient;
  const clientSearch = curClientLoc.clientSearch;
  const filterClientList = curClientLoc.filterClientList;
  const filterLocation = curClientLoc.filterLocation;
  const filterLocations = curClientLoc.filterLocations;
  const filterLocationList = curClientLoc.filterLocationList;
  const locationSearch = curClientLoc.locationSearch;
  const locations = curClientLoc.locations;

  const setFilterClient = (v) =>
    patchClientLocTab(selectedTab, { filterClient: v });
  const setClientSearch = (v) =>
    patchClientLocTab(selectedTab, { clientSearch: v });
  const setFilterLocation = (v) =>
    patchClientLocTab(selectedTab, { filterLocation: v });
  const setFilterLocationList = (v) =>
    patchClientLocTab(selectedTab, { filterLocationList: v });
  const setLocationSearch = (v) =>
    patchClientLocTab(selectedTab, { locationSearch: v });
  const setFilterLocations = (updater) => {
    setClientLocByTab((prev) => {
      const tab = selectedTab;
      const cur = prev[tab] || emptyJobTabClientLoc();
      const next =
        typeof updater === "function"
          ? updater(cur.filterLocations)
          : updater;
      return { ...prev, [tab]: { ...cur, filterLocations: next } };
    });
  };
  const setLocations = (v) =>
    patchClientLocTab(selectedTab, { locations: v });

  const [filterMode, setFilterMode] = useState("day"); // day / week (day is default)
  const [filterStartDate, setFilterStartDate] = useState(() => getAppTodayIso()); // YYYY-MM-DD
  const [filterRange, setFilterRange] = useState("");

  // Day mode: print orientation dropdown (always prints landscape)
  const [timesheetPrintOrientation, setTimesheetPrintOrientation] = useState("landscape");
  const [showTimesheetPrintDropdown, setShowTimesheetPrintDropdown] = useState(false);
  const timesheetPrintDropdownRef = useRef(null);

  const getTimesheetPrintTitle = () => {
    const titleParts = ["Sign in"];
    if (clientSearch) {
      titleParts.push(clientSearch);
    }

    const baseDate = filterStartDate || getAppTodayIso();
    const rangeDays = filterRange
      ? parseInt(filterRange, 10)
      : filterMode === "week"
        ? 7
        : 7;
    const endDateIso = addAppCalendarDays(baseDate, Math.max(rangeDays, 1) - 1);
    const startLabel = formatAppIntlLabel(
      parseDbDateTime(`${baseDate}T12:00:00Z`),
      { month: "short", day: "numeric" },
    );
    const endLabel = formatAppIntlLabel(
      parseDbDateTime(`${endDateIso}T12:00:00Z`),
      { month: "short", day: "numeric" },
    );

    if (startLabel) {
      titleParts.push(endLabel && baseDate !== endDateIso ? `${startLabel} to ${endLabel}` : startLabel);
    }

    return titleParts.join(" - ");
  };

  //date and time component 
  const [bulkPickerType, setBulkPickerType] = useState(null);

  // ref used to detect clicks outside the client dropdown in the timesheet panel
  const tsClientDropdownRef = useRef(null);
  const [showTimesheetClientDropdown, setShowTimesheetClientDropdown] = useState(false);

  useEffect(() => {
    const handleDocClick = (e) => {
      if (
        tsClientDropdownRef.current &&
        !tsClientDropdownRef.current.contains(e.target)
      ) {
        setShowTimesheetClientDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleDocClick);
    return () => document.removeEventListener("mousedown", handleDocClick);
  }, []);

  const [clientLoading, setClientLoading] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [selectedClientForModal, setSelectedClientForModal] = useState(null);

  const [filterSortBy, setFilterSortBy] = usePersistentPageState("page_staffing_sort_by_jobs", ""); // request | client | time
  const [filterSortDirection, setFilterSortDirection] = usePersistentPageState("page_staffing_sort_direction_jobs", "asc"); // asc | desc

  const [filterFromDate, setFilterFromDate] = usePersistentPageState("page_staffing_from_date_jobs", "");
  const [filterToDate, setFilterToDate] = usePersistentPageState("page_staffing_to_date_jobs", "");
  const [staffingShowDeleted, setStaffingShowDeleted] = usePersistentPageState("page_staffing_show_deleted_jobs", false);
  const [activeStaffingFilterField, setActiveStaffingFilterField] =
    useState("");
  const staffingSearchInputRef = useRef(null);
  const staffingClientFilterInputRef = useRef(null);
  const staffingLocationFilterInputRef = useRef(null);
  const staffingFromDateInputRef = useRef(null);
  const staffingToDateInputRef = useRef(null);
  const staffingSortBySelectRef = useRef(null);
  const staffingSortDirectionSelectRef = useRef(null);
  const staffingClientDropdownWrapRef = useRef(null);
  const staffingLocationDropdownWrapRef = useRef(null);
  const shiftsDefaultDateSetRef = useRef(false);
  const staffingDefaultDateSetRef = useRef(false);
  const shiftAssignmentDefaultSetRef = useRef(false);
  const dataFetchIdRef = useRef(0);
  /** Last successful Jobs → Shifts page (instant restore when switching back from another tab). */
  const shiftsPageCacheRef = useRef(null);

  const normalizeText = (text = "") => {
    return String(text)
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "") // ✅ preserve spaces for multi-term search
      .trim();
  };

  // helper function
  // const matchesStaffingRequestSearch = (req, search) => {
  //   if (!search) return true;

  //   const q = normalizeText(search);

  //   const requestId = normalizeText(req?.id);
  //   const client = normalizeText(req?.clientName);
  //   const location = normalizeText(req?.clientLocationName);
  //   const contact = normalizeText(req?.contact);
  //   const notes = normalizeText(req?.notes);
  //   const time = normalizeText(req?.time);

  //   return (
  //     requestId.includes(q) ||
  //     client.includes(q) ||
  //     location.includes(q) ||
  //     contact.includes(q) ||
  //     notes.includes(q) ||
  //     time.includes(q)
  //   );
  // };

  // const matchesClientFilter = (req, clientId) => {
  //   if (!clientId) return true;
  //   return String(req?.client_id || "") === String(clientId);
  // };

  // const matchesLocationFilter = (req, locationId) => {
  //   if (!locationId) return true;
  //   return String(req?.client_location_id || "") === String(locationId);
  // };

  const applyFilters = () => {
    setCurrentPage(1);
    filterPanel.close();
  };

  const staffingSortLabels = {
    request: "Request",
    client: "Client",
    contact: "Contact",
    time: "Time",
    created: "Created",
  };

  const getStaffingSortKey = () => {
    const key = String(filterSortBy || "").toLowerCase();
    return key && key !== "all" ? key : "created";
  };

  const getStaffingSortDirection = () =>
    String(filterSortDirection || "").toLowerCase() === "asc"
      ? "Ascending"
      : "Descending";

  // Stats are now sourced from the backend stats endpoint.

  const sortedMainShiftsData = React.useMemo(() => {
    // Main Jobs -> Shifts must honor server-side ordering across the whole dataset.
    // Keep row order exactly as returned by the API.
    if (!Array.isArray(filteredShifts) || selectedTab !== "SHIFTS")
      return filteredShifts || [];
    return filteredShifts;
  }, [filteredShifts, selectedTab]);

  const mainShiftsSummary = React.useMemo(() => {
    const shiftsCount = Number(
      stats?.activeShiftsCount ?? totalCount ?? stats?.shiftsCount ?? 0,
    );
    const submittedNonConflict = Number(
      stats?.shiftActualsSubmittedNonconflictCount ?? 0,
    );
    return {
      shiftsCount,
      actualHours: Number(stats?.shiftActualsHours ?? 0),
      submittedPercent:
        shiftsCount > 0 ? (submittedNonConflict / shiftsCount) * 100 : 0,
      conflictCount: Number(stats?.shiftActualsConflictCount ?? 0),
    };
  }, [stats, totalCount]);

  const staffingRequestShiftsSummary = React.useMemo(() => {
    const shiftsCount = Number(
      shiftsData?.stats?.totalShifts ??
        stats?.activeShiftsCount ??
        stats?.shiftsCount ??
        0,
    );
    let submittedPercent = 0;
    if (
      shiftsData?.stats?.submittedPercentage !== undefined &&
      shiftsData?.stats?.submittedPercentage !== null
    ) {
      submittedPercent = Number(shiftsData.stats.submittedPercentage || 0);
    } else {
      const activeShifts = Number(
        stats?.activeShiftsCount ?? stats?.shiftsCount ?? 0,
      );
      const submittedNonConflict = Number(
        stats?.shiftActualsSubmittedNonconflictCount ?? 0,
      );
      submittedPercent =
        activeShifts > 0 ? (submittedNonConflict / activeShifts) * 100 : 0;
    }
    return {
      shiftsCount,
      actualHours: Number(
        shiftsData?.stats?.totalHours ?? stats?.shiftActualsHours ?? 0,
      ),
      submittedPercent,
      conflictCount: Number(
        shiftsData?.stats?.conflictCount ?? stats?.shiftActualsConflictCount ?? 0,
      ),
    };
  }, [shiftsData, stats]);

  const StaffingSortArrow = ({ colKey }) => (
    <button
      onClick={() => {
        if (filterSortBy === colKey) {
          setFilterSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
          setFilterSortBy(colKey);
          setFilterSortDirection("asc");
        }
      }}
      className="float-right mr-1 text-sm text-white/90 hover:text-white"
      title="Sort"
    >
      {filterSortBy === colKey
        ? filterSortDirection === "asc"
          ? "▲"
          : "▼"
        : ""}
    </button>
  );

  const getStaffingActiveFilters = () => {
    const active = [];
    if (searchTerm && searchTerm.trim()) {
      active.push({ key: "search", label: `Search: ${searchTerm.trim()}` });
    }
    if (filterClient) {
      const selectedClient = (filterClientList || []).find(
        (c) => String(c.id) === String(filterClient),
      );
      const label = selectedClient?.name || clientSearch || `#${filterClient}`;
      active.push({ key: "client", label: `Client: ${label}` });
    }
    if (filterLocation) {
      const selectedLocation = (filterLocationList || []).find(
        (l) => String(l.id) === String(filterLocation),
      );
      const label = selectedLocation?.name || `#${filterLocation}`;
      active.push({ key: "location", label: `Location: ${label}` });
    }
    if (filterFromDate)
      active.push({ key: "fromDate", label: `From: ${filterFromDate}` });
    if (filterToDate)
      active.push({ key: "toDate", label: `To: ${filterToDate}` });
    if (defaultTodayFilterActive)
      active.push({ key: "today", label: "Today filter" });
    if (staffingShowDeleted)
      active.push({ key: "showDeleted", label: "Show deleted" });
    return active;
  };

  const openStaffingFilterField = (key) => {
    if (key === "search") {
      setTimeout(() => {
        staffingSearchInputRef.current?.focus?.();
      }, 0);
      return;
    }
    setActiveStaffingFilterField(key);
    filterPanel.focusField(key);
  };

  useEffect(() => {
    if (
      !jobsFilterPanelOpen ||
      selectedTab !== "STAFFINGREQUESTS" ||
      !activeStaffingFilterField
    ) {
      return;
    }

    const timer = setTimeout(() => {
      const fieldRefs = {
        client: staffingClientFilterInputRef,
        location: staffingLocationFilterInputRef,
        fromDate: staffingFromDateInputRef,
        toDate: staffingToDateInputRef,
        sortBy: staffingSortBySelectRef,
        sortDirection: staffingSortDirectionSelectRef,
      };
      const target = fieldRefs[activeStaffingFilterField]?.current;
      target?.focus?.();
      target?.scrollIntoView?.({ block: "center", behavior: "smooth" });
    }, 60);

    return () => clearTimeout(timer);
  }, [jobsFilterPanelOpen, selectedTab, activeStaffingFilterField]);

  useEffect(() => {
    if (!jobsFilterPanelOpen || selectedTab !== "STAFFINGREQUESTS") return;

    const handleOutsideClick = (event) => {
      const clientWrap = staffingClientDropdownWrapRef.current;
      const locationWrap = staffingLocationDropdownWrapRef.current;

      if (clientWrap && !clientWrap.contains(event.target)) {
        setShowClientDropdown(false);
      }
      if (locationWrap && !locationWrap.contains(event.target)) {
        setShowLocationDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [jobsFilterPanelOpen, selectedTab]);

  // Staffing Requests filter panel: debounced client search for dropdown
  useEffect(() => {
    if (!jobsFilterPanelOpen || selectedTab !== "STAFFINGREQUESTS") return;

    const timer = setTimeout(async () => {
      setClientLoading(true);
      try {
        const search = (clientSearch || "").trim();
        const url = `${BASE_URL}/api/clients/clients?all=1&search=${encodeURIComponent(search)}`;
        const token =
          localStorage.getItem("token") || localStorage.getItem("authToken");
        const res = await fetch(
          url,
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
        );
        const data = await res.json();
        const rows = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : [];
        patchClientLocTab("STAFFINGREQUESTS", { filterClientList: rows });
      } catch (err) {
        console.error("Staffing client fetch error:", err);
        patchClientLocTab("STAFFINGREQUESTS", { filterClientList: [] });
      } finally {
        setClientLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [clientSearch, jobsFilterPanelOpen, selectedTab]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        showShiftActionsMenu &&
        shiftActionsMenuRef.current &&
        !shiftActionsMenuRef.current.contains(event.target)
      ) {
        setShowShiftActionsMenu(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showShiftActionsMenu]);

  useEffect(() => {
    setSelectedShiftIds([]);
    setShowShiftActionsMenu(false);
  }, [selectedRequest?.id, activeSidebarTab]);

  useEffect(() => {
    const visibleIds = new Set(
      (filteredShifts || []).map((shift) => String(shift.shift_id)),
    );
    setSelectedShiftIds((prev) =>
      prev.filter((shiftId) => visibleIds.has(String(shiftId))),
    );
  }, [filteredShifts]);



  const removeStaffingFilter = (key) => {
    if (key === "search") setSearchTerm("");
    if (key === "client") {
      patchClientLocTab("STAFFINGREQUESTS", {
        filterClient: "",
        clientSearch: "",
        filterLocation: "",
        filterLocationList: [],
        locationSearch: "",
        locations: [],
      });
      setSelectedLocation("");
      setShowClientDropdown(false);
      setShowLocationDropdown(false);
    }
    if (key === "location") {
      patchClientLocTab("STAFFINGREQUESTS", {
        filterLocation: "",
        locationSearch: "",
      });
      setSelectedLocation("");
      setShowLocationDropdown(false);
    }
    if (key === "fromDate") setFilterFromDate("");
    if (key === "toDate") setFilterToDate("");
    if (key === "today") setDefaultTodayFilterActive(false);
    if (key === "showDeleted") setStaffingShowDeleted(false);
    setCurrentPage(1);
  };

  const handleEditShift = (shift) => {
    // TODO: Implement edit shift functionality
    console.log("Edit shift:", shift);
  };

  const openShiftOverview = (shift) => {
    if (!shift) return;
    const rawShiftId = shift?.id ?? shift?.shift_id;
    if (!rawShiftId) return;
    const numericShiftId = Number(rawShiftId);
    const rowClosedWeek =
      shift?.isClosedWeek === true ||
      shift?.isClosedWeek === 1 ||
      shift?.isClosedWeek === "1";
    setStandaloneDuplicateShiftId(null);
    setSelectedShift({
      id:
        Number.isFinite(numericShiftId) && numericShiftId > 0
          ? numericShiftId
          : rawShiftId,
      isClosedWeek: rowClosedWeek,
    });
  };

  const handleDeleteShift = async (shiftId) => {
    if (!(await showConfirm("Are you sure you want to delete this shift?")))
      return;

    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      const res = await fetch(`${BASE_URL}/api/jobs/shifts/${shiftId}`, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const data = await res.json();
      if (res.ok) {
        showSuccess("Shift deleted successfully");
        // Refresh shifts data
        const refreshRes = await fetch(
          `${BASE_URL}/api/jobs/staffingrequests/${selectedRequest.id}/shifts`,
        );
        const refreshData = await refreshRes.json();
        if (refreshRes.ok) {
          setShiftsData(refreshData);
        }
        setReloadKey((prev) => prev + 1);
      } else {
        showError(data.message || "Failed to delete shift");
      }
    } catch (err) {
      console.error("Error deleting shift:", err);
      showError("Failed to delete shift");
    }
  };

  const toggleShiftSelection = (shiftId, checked) => {
    const normalized = String(shiftId);
    setSelectedShiftIds((prev) => {
      if (checked) {
        if (prev.includes(normalized)) return prev;
        return [...prev, normalized];
      }
      return prev.filter((id) => id !== normalized);
    });
  };

  const toggleSelectAllVisibleShifts = (checked) => {
    if (!checked) {
      setSelectedShiftIds([]);
      return;
    }
    setSelectedShiftIds(
      filteredShifts
        .map((shift) => String(shift?.shift_id ?? shift?.id ?? ""))
        .filter(Boolean),
    );
  };

  const mainVisibleShiftIds = React.useMemo(() => {
    if (selectedTab !== "SHIFTS") return [];
    if (!Array.isArray(sortedMainShiftsData)) return [];
    return sortedMainShiftsData
      .map((s) => String(s?.id ?? s?.shift_id ?? ""))
      .filter(Boolean);
  }, [selectedTab, sortedMainShiftsData]);

  const allVisibleMainShiftsSelected =
    mainVisibleShiftIds.length > 0 &&
    mainVisibleShiftIds.every((id) => selectedShiftIds.includes(String(id)));

  const toggleSelectAllMainShifts = (checked) => {
    if (!checked) {
      setSelectedShiftIds([]);
      return;
    }
    setSelectedShiftIds(mainVisibleShiftIds);
  };

  const resetBulkShiftUpdateForm = () => {
    setBulkShiftUpdateForm({
      applyStatus: false,
      state: "",
      applyTimeRange: false,
      start_date: "",
      end_date: "",
      applyBreak: false,
      unpaid_break: false,
      notify_staff_change: false,
      notifyTouched: false,
    });
  };

  useEffect(() => {
    if (!showBulkShiftUpdateModal) return;
    const first = selectedShiftRows[0];
    if (!first) return;
    const start = parseDbDateTime(first.start_date);
    const end = parseDbDateTime(first.end_date);
    setBulkShiftUpdateForm((prev) => ({
      ...prev,
      start_date: start || prev.start_date,
      end_date: end || prev.end_date,
    }));
  }, [showBulkShiftUpdateModal, selectedShiftRows]);

  const handleBulkShiftUpdate = async () => {
    if (!selectedShiftIds.length) {
      showError("Please select at least one shift");
      return;
    }

    const updates = {};
    if (bulkShiftUpdateForm.applyStatus) {
      if (!bulkShiftUpdateForm.state) {
        showError("Select a shift status");
        return;
      }
      updates.state = bulkShiftUpdateForm.state;
    }
    if (bulkShiftUpdateForm.applyTimeRange) {
      if (!bulkShiftUpdateForm.start_date || !bulkShiftUpdateForm.end_date) {
        showError("Select start and end time");
        return;
      }
      updates.start_date = toMysqlUtcWallString(bulkShiftUpdateForm.start_date);
      updates.end_date = toMysqlUtcWallString(bulkShiftUpdateForm.end_date);
    }
    if (bulkShiftUpdateForm.applyBreak) {
      // we now collect a boolean yes/no instead of a numeric value
      if (typeof bulkShiftUpdateForm.unpaid_break !== "boolean") {
        showError("Select yes or no for break");
        return;
      }
      updates.unpaid_break = bulkShiftUpdateForm.unpaid_break ? 1 : 0;
    }
    // notify flag: only include in updates if user actually interacted with it
    if (bulkShiftUpdateForm.notifyTouched) {
      updates.notify_staff_change = bulkShiftUpdateForm.notify_staff_change
        ? 1
        : 0;
    }
    if (Object.keys(updates).length === 0) {
      showError("Select at least one field to update");
      return;
    }

    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      const url =
        bulkShiftScope === "main"
          ? `${BASE_URL}/api/jobs/shifts/bulk-update`
          : `${BASE_URL}/api/jobs/staffingrequests/${selectedRequest.id}/shifts/bulk-update`;
      const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            shiftIds: selectedShiftIds,
            updates,
          }),
        });
      const data = await res.json();
      if (!res.ok) {
        showError(data.message || "Failed to bulk update shifts");
        return;
      }
      showSuccess("Bulk shift update completed");
      setShowBulkShiftUpdateModal(false);
      resetBulkShiftUpdateForm();
      setSelectedShiftIds([]);
      setShowShiftActionsMenu(false);
      if (bulkShiftScope === "main") {
        fetchShifts();
      } else {
        if (!selectedRequest?.id) return;
        await fetchShiftsForRequest(selectedRequest.id);
      }
    } catch (err) {
      console.error("Bulk shift update error:", err);
      showError("Failed to bulk update shifts");
    }
  };

  const handleBulkDivisionPositionUpdate = async () => {
    if (!selectedShiftIds.length) {
      showError("Please select at least one shift");
      return;
    }
    if (!bulkDivisionPositionForm.client_id) {
      showError("Client is required");
      return;
    }
    if (!bulkDivisionPositionForm.division_id) {
      showError("Division is required");
      return;
    }
    if (!bulkDivisionPositionForm.position_id) {
      showError("Position is required");
      return;
    }

    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      const url =
        bulkShiftScope === "main"
          ? `${BASE_URL}/api/jobs/shifts/bulk-division-position-update`
          : `${BASE_URL}/api/jobs/staffingrequests/${selectedRequest.id}/shifts/bulk-division-position-update`;
      const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            shiftIds: selectedShiftIds,
            division_id: Number(bulkDivisionPositionForm.division_id),
            position_id: Number(bulkDivisionPositionForm.position_id),
          }),
        });
      const data = await res.json();
      if (!res.ok) {
        showError(
          data.message || "Failed to bulk update shift division and position",
        );
        return;
      }
      showSuccess("Bulk shift division and position update completed");
      setShowBulkDivisionPositionModal(false);
      setBulkDivisionPositionForm({ division_id: "", position_id: "" });
      setSelectedShiftIds([]);
      setShowShiftActionsMenu(false);
      if (bulkShiftScope === "main") {
        fetchShifts();
      } else {
        if (!selectedRequest?.id) return;
        await fetchShiftsForRequest(selectedRequest.id);
      }
    } catch (err) {
      console.error("Bulk division/position update error:", err);
      showError("Failed to bulk update shift division and position");
    }
  };

  // Duplicate shift: same Duplicate Shift dialog as Shift detail footer (Simple / Advanced)
  const handleDuplicateShift = (shift) => {
    if (!shift) return;
    const rawShiftId = shift.shift_id ?? shift.id;
    if (!rawShiftId) {
      showError("Cannot duplicate: missing shift id.");
      return;
    }
    const numericShiftId = Number(rawShiftId);
    const rowClosedWeek =
      shift?.isClosedWeek === true ||
      shift?.isClosedWeek === 1 ||
      shift?.isClosedWeek === "1";
    setStandaloneDuplicateShiftId(
      Number.isFinite(numericShiftId) && numericShiftId > 0
        ? numericShiftId
        : rawShiftId,
    );
  };

  // Helper functions for shifts filters
  function handleShiftsFilterChange(key, value) {
    setPendingShiftsFilters((prev) => {
      const commit = (next) => {
        setCurrentPage(1);
        setShiftsFilters(next);
        return next;
      };
      if (key === "clients") {
        const normalized = Array.isArray(value)
          ? value.map((v) => String(v))
          : [];
        return commit({
          ...prev,
          clients: normalized,
          client: normalized[0] || "",
        });
      }
      if (key === "clientLocation") {
        const normalized = value ? [String(value)] : [];
        return commit({
          ...prev,
          clientLocation: value,
          clientLocations: normalized,
        });
      }
      if (key === "clientLocations") {
        const normalized = Array.isArray(value)
          ? value.map((v) => String(v))
          : [];
        return commit({
          ...prev,
          clientLocations: normalized,
          clientLocation: normalized[0] || "",
        });
      }
      if (key === "shiftSupervisors") {
        const normalized = Array.isArray(value)
          ? value.map((v) => String(v))
          : [];
        return commit({
          ...prev,
          shiftSupervisors: normalized,
          shiftSupervisor: normalized[0] || "",
        });
      }
      if (key === "checkInNoteType") {
        const normalized = Array.isArray(value)
          ? value
              .map((v) =>
                v && typeof v === "object" ? String(v.value || "") : String(v),
              )
              .filter(Boolean)
          : [];
        return commit({ ...prev, checkInNoteType: normalized });
      }
      if (key === "fromDate") {
        const nextFilters = { ...prev, fromDate: value };
        if (value && prev.toDate && prev.toDate < value) {
          nextFilters.toDate = "";
        }
        return commit(nextFilters);
      }
      if (key === "toDate") {
        const nextFilters = { ...prev, toDate: value };
        if (value && prev.fromDate && prev.fromDate > value) {
          nextFilters.fromDate = "";
        }
        return commit(nextFilters);
      }
      return commit({ ...prev, [key]: value });
    });
  }

  function resetShiftsFilters() {
    const cleared = createDefaultShiftsFilters();
    setPendingShiftsFilters(cleared);
    setShiftsFilters(cleared);
    setActualTimeConflict("all");
    setTravelTimeValue("all");
    setShiftsSearchQuery("");
    setSelectedShiftClients([]);
    setSelectedShiftClientLocations([]);
    setSelectedClientPositions([]);
    setCurrentPage(1);
  }

  function handleShiftsFilterClear() {
    resetShiftsFilters();
    filterPanel.close();
  }

  function getShiftsSortBy() {
    const key = String(shiftsFilters.sortBy || "scheduled").toLowerCase();
    return JOBS_SHIFTS_SORT_OPTIONS.some((option) => option.value === key)
      ? key
      : "scheduled";
  }

  function openShiftsFilterField(fieldKey) {
    if (fieldKey === "today") return;
    const panel =
      selectedRequest && activeSidebarTab === "Shifts"
        ? staffingRequestShiftsFilterPanel
        : filterPanel;
    panel.focusField(fieldKey);
  }

  function removeShiftsFilter(fieldKey) {
    const resetByKey = {
      today: false,
      shiftStatus: [],
      clients: [],
      client: "",
      staff: "",
      assignmentType: "",
      fromDate: "",
      toDate: "",
      outsourcedAgency: "",
      clientLocation: "",
      clientLocations: [],
      shiftSupervisor: "",
      shiftSupervisors: [],
      actualTimeStatus: [],
      actualTimeConflict: "",
      travelTime: "",
      clientPosition: "",
      timesheetSignOffDate: "",
      checkInNote: "",
      checkInNoteType: [],
      sortBy: "",
      sortDirection: "asc",
    };
    if (!Object.prototype.hasOwnProperty.call(resetByKey, fieldKey)) return;
    const applyReset = (prev) => {
      if (fieldKey === "clientLocations" || fieldKey === "clientLocation") {
        return { ...prev, clientLocations: [], clientLocation: "" };
      }
      if (fieldKey === "clients" || fieldKey === "client") {
        return { ...prev, clients: [], client: "" };
      }
      if (fieldKey === "shiftSupervisors" || fieldKey === "shiftSupervisor") {
        return { ...prev, shiftSupervisors: [], shiftSupervisor: "" };
      }
      return { ...prev, [fieldKey]: resetByKey[fieldKey] };
    };
    setPendingShiftsFilters((prev) => applyReset(prev));
    setShiftsFilters((prev) => applyReset(prev));
    if (fieldKey === "clients" || fieldKey === "client") {
      setSelectedShiftClients([]);
      setSelectedShiftClientLocations([]);
    }
    if (fieldKey === "clientLocations" || fieldKey === "clientLocation") {
      setSelectedShiftClientLocations([]);
    }
    if (fieldKey === "clientPosition") {
      setSelectedClientPositions([]);
    }
    setCurrentPage(1);
  }


  function getShiftAssignmentActiveFilters() {
    const labels = [];
    const fmtDate = (v) =>
      v ? formatAppDate(`${v}T12:00:00Z`, { emptyValue: "" }) : "";
    const joinValues = (arr) =>
      Array.isArray(arr) && arr.length ? arr.join(", ") : "";
    const resolveLabel = (options, values) => {
      const arr = Array.isArray(values) ? values : [];
      const names = arr
        .map((v) => options.find((o) => String(o.value) === String(v))?.label)
        .filter(Boolean);
      return joinValues(names.length ? names : arr.map(String));
    };

    if (shiftAssignmentFilters.lastModifiedProximity) {
      const map = {
        after: "After Shift Start Time",
        "24h_after": "24h or more After Shift Start Date",
      };
      labels.push({
        key: "lastModifiedProximity",
        label: `Last Modified Proximity: ${map[shiftAssignmentFilters.lastModifiedProximity] || shiftAssignmentFilters.lastModifiedProximity}`,
      });
    }
    if (shiftAssignmentFilters.fromShiftStart) {
      labels.push({
        key: "fromShiftStart",
        label: `From Shift Start Time: ${fmtDate(shiftAssignmentFilters.fromShiftStart)}`,
      });
    }
    if (shiftAssignmentFilters.toShiftStart) {
      labels.push({
        key: "toShiftStart",
        label: `To Shift Start Time: ${fmtDate(shiftAssignmentFilters.toShiftStart)}`,
      });
    }
    if (
      Array.isArray(shiftAssignmentFilters.staffAccountIds) &&
      shiftAssignmentFilters.staffAccountIds.length
    ) {
      labels.push({
        key: "staff",
        label: `Staff: ${resolveLabel(staffOptions, shiftAssignmentFilters.staffAccountIds)}`,
      });
    }
    if (
      Array.isArray(shiftAssignmentFilters.lastModifiedByAccountIds) &&
      shiftAssignmentFilters.lastModifiedByAccountIds.length
    ) {
      labels.push({
        key: "lastModifiedByStaff",
        label: `Last Modified by Staff: ${resolveLabel(officeStaffOptions, shiftAssignmentFilters.lastModifiedByAccountIds)}`,
      });
    }

    return labels;
  }

  function openShiftAssignmentFilterField(fieldKey) {
    setSaFilterFocusField(fieldKey);
    filterPanel.focusField(fieldKey);
  }

  function removeShiftAssignmentFilter(fieldKey) {
    setCurrentPage(1);
    setShiftAssignmentFilters((prev) => {
      if (fieldKey === "staff") return { ...prev, staffAccountIds: [] };
      if (fieldKey === "lastModifiedByStaff")
        return { ...prev, lastModifiedByAccountIds: [] };
      if (fieldKey === "fromShiftStart") return { ...prev, fromShiftStart: "" };
      if (fieldKey === "toShiftStart") return { ...prev, toShiftStart: "" };
      if (fieldKey === "lastModifiedProximity")
        return { ...prev, lastModifiedProximity: "" };
      return prev;
    });
  }


  // Shift Assignment (Jobs -> Shift Assignment) filter state
  const [saFilterFocusField, setSaFilterFocusField] = useState("");
  const [shiftAssignmentFilters, setShiftAssignmentFilters] = usePersistentPageState("page_shift_assignment_filters_jobs", {
    lastModifiedProximity: "after", // "", "after", "24h_after"
    fromShiftStart: "", // YYYY-MM-DD
    toShiftStart: "", // YYYY-MM-DD
    staffAccountIds: [], // array of account ids (string)
    lastModifiedByAccountIds: [], // array of account ids (string)
    sortBy: "last_modified_proximity", // shift_account_id | last_modified_proximity | shift_start_date
    sortDirection: "asc", // asc | desc
  });
  // When user clicks CLEAR on Shift Assignment filters, we apply a wide date range
  // for fetching, but keep the UI fields/chips empty (so it "looks cleared").
  const [shiftAssignmentWideDateRange, setShiftAssignmentWideDateRange] =
    useState(null); // { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' } | null


  // Timesheets-specific state
  const [tsStatus, setTsStatus] = usePersistentPageState("page_ts_status_jobs", "");
  const [tsClient, setTsClient] = usePersistentPageState("page_ts_client_jobs", "");
  const [tsDrawerOpen, setTsDrawerOpen] = useState(false);
  const [tsModalOpen, setTsModalOpen] = useState(false);
  const [tsRecord, setTsRecord] = useState(null);

 

  const buildRequestContactOption = () => {
    const value = editableRequest?.contact_account_id
      ? String(editableRequest.contact_account_id)
      : "";
    if (!value) return null;
    const exists = (contacts || []).some((c) => {
      const cid = getClientContactRowAccountId(c);
      return String(cid || "") === value;
    });
    if (exists) return null;
    const label =
      getSafeDisplayText(selectedRequest?.contact, selectedRequest?.contactName) ||
      `Contact #${value}`;
    return { value, label };
  };

  const buildRequestSupervisorOption = () => {
    const value = editableRequest?.supervisor_contact_account_id
    
      ? String(editableRequest.supervisor_contact_account_id)
      : "";
    if (!value) return null;
    const exists = (supervisors || []).some((s) => {
      const sid = getClientContactRowAccountId(s);
      return String(sid || "") === value;
    });
    if (exists) return null;
    const label =
      getSafeDisplayText(
        selectedRequest?.supervisor,
        selectedRequest?.supervisorName,
      ) || `Supervisor #${value}`;
    return { value, label };
  };
  const requestContactOptions = [
    buildRequestContactOption(),
    ...(contacts || []).map((c, idx) => {
      const cid = getClientContactRowAccountId(c);
      const label =
        c.name ||
        `${c.first_name || ""} ${c.last_name || ""}`.trim() ||
        `Contact #${cid ?? idx}`;
      return { value: cid ? String(cid) : "", label };
    }),
  ].filter(Boolean);
  const requestSupervisorSeenLabels = new Set();
  const requestSupervisorOptions = [
    buildRequestSupervisorOption(),
    ...(supervisors || []).map((s, idx) => {
      const sid = getClientContactRowAccountId(s);
      const label =
        s.name ||
        `${s.first_name || ""} ${s.last_name || ""}`.trim() ||
        `Supervisor #${sid ?? idx}`;
      return { value: sid ? String(sid) : "", label };
    }),
  ]
    .filter(Boolean)
    .filter((opt) => {
      // Deduplicate by display label (case-insensitive)
      const normalizedLabel = String(opt.label || "").trim().toLowerCase();
      if (!normalizedLabel) return true;
      if (requestSupervisorSeenLabels.has(normalizedLabel)) return false;
      requestSupervisorSeenLabels.add(normalizedLabel);
      return true;
    });

  const createSupervisorSeenKeys = new Set();
  const dedupedCreateSupervisors = (createSupervisors || []).filter((s) => {
    const rawName =
      `${s?.first_name || ""} ${s?.last_name || ""}`.trim() || s?.name || "";
    const normalizedName = String(rawName).trim().toLowerCase();
    const sid = getAccountOptionId(s);
    const key = normalizedName
      ? `name:${normalizedName}`
      : `id:${String(sid || "")}`;
    if (createSupervisorSeenKeys.has(key)) return false;
    createSupervisorSeenKeys.add(key);
    return true;
  });
  const createClientOptions = (clients || [])
    .map((client) => {
      const value = client?.id ?? client?.client_id ?? client?.value ?? null;
      const label =
        client?.name || client?.label || client?.client_name || "";
      if (value === null || value === undefined || !label) return null;
      return { value: String(value), label: String(label) };
    })
    .filter(Boolean);

  // Debug: log selected and editable request whenever they change
  useEffect(() => {
    try {
      console.log("Jobs debug - selectedRequest:", selectedRequest);
      console.log("Jobs debug - editableRequest:", editableRequest);
    } catch (e) {
      // ignore logging errors
    }
  }, [selectedRequest, editableRequest]);

  useEffect(() => {
    const controller = new AbortController();
    const fetchData = async () => {
      if (embedMode && embedUsesProps) {
        setLoading(false);
        return;
      }
      const tabForFetch = selectedTab;
      const thisFetchId = ++dataFetchIdRef.current;
      setLoading(true);
      try {
        // Timesheets require a client to be selected; do not fetch or show data when none selected
        if (selectedTab === "TIMESHEETS" && !filterClient) {
          setData([]);
          setLoading(false);
          return;
        }

        let endpoint = "";
        if (selectedTab === "STAFFINGREQUESTS") endpoint = "staffingrequests";
        else if (selectedTab === "SHIFTS") endpoint = "shifts";
        else if (selectedTab === "TIMESHEETS") endpoint = "timesheets";
        else if (selectedTab === "SHIFTASSIGNMENT")
          endpoint = "shiftassignments";

        const params = new URLSearchParams();
        params.set("page", String(currentPage));
        params.set("limit", String(rowsPerPage));
        params.set("search", searchTerm || "");
        if (selectedTab === "SHIFTS") {
          const shiftsParams = buildShiftsQueryParams(shiftsFilters, searchTerm, {
            filterClient,
            filterLocation,
          });
          for (const [key, value] of shiftsParams.entries()) {
            params.set(key, value);
          }
        } else if (selectedTab === "STAFFINGREQUESTS") {
          if (filterClient) params.set("client_id", String(filterClient));
          if (filterLocation) params.set("location_id", String(filterLocation));
          if (filterFromDate) params.set("fromDate", filterFromDate);
          if (filterToDate) params.set("toDate", filterToDate);
          if (filterSortBy && filterSortBy !== "all")
            params.set("sortBy", filterSortBy);
          params.set("sortDirection", filterSortDirection || "asc");
          // disable legacy today filter: always fetch all and rely on date filters
          params.set("all", "1");
          params.set("showAllHistory", "1");
          if (staffingShowDeleted) {
            params.set("includeDeleted", "1");
          }
        } else if (selectedTab === "SHIFTASSIGNMENT") {
          if (shiftAssignmentFilters.lastModifiedProximity) {
            params.set(
              "lastModifiedProximity",
              shiftAssignmentFilters.lastModifiedProximity,
            );
          }
          const effectiveSaFrom =
            shiftAssignmentFilters.fromShiftStart ||
            shiftAssignmentWideDateRange?.from ||
            "";
          const effectiveSaTo =
            shiftAssignmentFilters.toShiftStart ||
            shiftAssignmentWideDateRange?.to ||
            "";
          if (effectiveSaFrom) params.set("shiftDateFrom", effectiveSaFrom);
          if (effectiveSaTo) params.set("shiftDateTo", effectiveSaTo);
          if (
            Array.isArray(shiftAssignmentFilters.staffAccountIds) &&
            shiftAssignmentFilters.staffAccountIds.length
          ) {
            params.set(
              "accountId",
              shiftAssignmentFilters.staffAccountIds.join(","),
            );
          }
          if (
            Array.isArray(shiftAssignmentFilters.lastModifiedByAccountIds) &&
            shiftAssignmentFilters.lastModifiedByAccountIds.length
          ) {
            params.set(
              "lastModifiedByAccountId",
              shiftAssignmentFilters.lastModifiedByAccountIds.join(","),
            );
          }

          const orderField =
            shiftAssignmentFilters.sortBy || "last_modified_proximity";
          const orderDir =
            String(shiftAssignmentFilters.sortDirection || "asc").toLowerCase() ===
            "desc"
              ? "desc"
              : "asc";
          params.set("order", `${orderField},${orderDir}`);
        }
        let fullUrl = `${BASE_URL}/api/jobs/${endpoint}?${params.toString()}`;
        if (selectedTab === "TIMESHEETS") {
          const tsParams = new URLSearchParams();
          tsParams.set("search", searchTerm || "");
          if (filterClient) tsParams.set("client_id", filterClient);
          if (selectedRequest?.id) {
            tsParams.set("staff_request_id", String(selectedRequest.id));
          }
          if (filterLocations.length > 0) tsParams.set("location_id", filterLocations.join(","));
          // Always send a date range: use filterStartDate or default to today (day) / this week (week)
          const startDateForApi =
            filterStartDate ||
            (filterMode === "week"
              ? getAppWeekStartIso(getAppTodayIso())
              : getAppTodayIso());
          tsParams.set("start_date", startDateForApi);
          let rangeDays = 0;
          if (filterRange) {
            rangeDays = parseInt(filterRange, 10);
          } else {
            rangeDays = 7; // default to 7 days for both day and week mode
          }
          if (rangeDays > 0) {
            tsParams.set(
              "end_date",
              addAppCalendarDays(startDateForApi, rangeDays - 1),
            );
          }
          if (filterMode) tsParams.set("mode", filterMode);
          fullUrl = `${BASE_URL}/api/admin/timesheets?${tsParams.toString()}`;
        }
        console.log("Fetching URL:", fullUrl);
        const token =
          localStorage.getItem("token") || localStorage.getItem("authToken");
        const res = await fetch(
          fullUrl,
          token
            ? {
                headers: { Authorization: `Bearer ${token}` },
                signal: controller.signal,
              }
            : { signal: controller.signal },
        );
        const result = await res.json();
        if (!res.ok) {
          throw new Error(result?.message || `Request failed (${res.status})`);
        }
        // Debug: log timesheets response shape to help map fields
        if (
          endpoint === "timesheets" ||
          fullUrl.includes("/api/admin/timesheets")
        ) {
          try {
            console.log(
              "DEBUG timesheets fetch result sample:",
              Array.isArray(result.data) ? result.data[0] : result.data,
            );
          } catch (e) {
            console.log(
              "DEBUG timesheets fetch - unable to log result shape",
              e,
            );
          }
        }
        // Ignore response if a newer request was already sent (avoids race where stale response overwrites correct data)
        if (thisFetchId !== dataFetchIdRef.current) return;
        console.log("Response total count:", result.total);
        setData(result.data || []);
        setDataTab(tabForFetch);
        setTotalPages(result.totalPages || 1);
        setTotalCount(result.total || 0);
        if (tabForFetch === "SHIFTS") {
          // KPI stats are returned with the shifts list (avoids duplicate /shifts/stats round-trip)
          const shiftStats = result.stats || null;
          setStats(shiftStats);
          shiftsPageCacheRef.current = {
            data: result.data || [],
            totalCount: result.total || 0,
            totalPages: result.totalPages || 1,
            stats: shiftStats,
            ok: true,
          };
        }
      } catch (err) {
        if (err?.name === "AbortError") return;
        if (thisFetchId !== dataFetchIdRef.current) return;
        console.error(err);
        setData([]);
        setDataTab(null);
      }
      if (thisFetchId !== dataFetchIdRef.current) return;
      setLoading(false);
    };
    fetchData();
    return () => controller.abort();
  }, [
    selectedTab,
    searchTerm,
    currentPage,
    filterStartDate,
    filterClient,
    filterLocation,
    filterLocations,
    filterRange,
    filterMode,
    filterFromDate,
    filterToDate,
    filterSortBy,
    filterSortDirection,
    staffingShowDeleted,
    reloadKey,
    // include stringified shiftsFilters so changes reload data
    JSON.stringify(shiftsFilters),
    JSON.stringify(shiftAssignmentFilters),
    rowsPerPage,
  ]);

  useEffect(() => setCurrentPage(1), [selectedTab, searchTerm]);

  // Drop stale rows when switching tabs (restore Shifts cache when returning to SHIFTS).
  useEffect(() => {
    if (selectedTab === "SHIFTS" && shiftsPageCacheRef.current?.ok) {
      const cached = shiftsPageCacheRef.current;
      setData(cached.data);
      setDataTab("SHIFTS");
      setTotalCount(cached.totalCount);
      setTotalPages(cached.totalPages);
      setStats(cached.stats);
      return;
    }
    setData([]);
    setDataTab(null);
    setTotalCount(0);
    setTotalPages(1);
    setStats(null);
  }, [selectedTab]);

  // Apply default "today" date filters on first visit to tabs.
  useEffect(() => {
    const today = getAppTodayIso();

    if (selectedTab === "STAFFINGREQUESTS") {
      if (staffingDefaultDateSetRef.current) return;
      staffingDefaultDateSetRef.current = true;
      setFilterFromDate((prev) =>
        String(prev || "").trim() ? prev : today,
      );
      return;
    }

    if (selectedTab === "SHIFTASSIGNMENT") {
      if (shiftAssignmentDefaultSetRef.current) return;
      shiftAssignmentDefaultSetRef.current = true;

      // Match reference behavior: default = "After Shift Start Time" + 7-day window.
      setShiftAssignmentFilters((prev) => {
        const from = prev.fromShiftStart || today;
        const endIso = addAppCalendarDays(from, 8);
        return {
          ...prev,
          lastModifiedProximity: prev.lastModifiedProximity || "after",
          fromShiftStart: from,
          toShiftStart: endIso,
          sortBy: prev.sortBy || "last_modified_proximity",
          sortDirection: prev.sortDirection || "asc",
        };
      });
      setShiftAssignmentWideDateRange(null);
    }
  }, [selectedTab, filterFromDate, filterToDate]);

  // Helper to refresh the current staffing requests page (used after closing detail sidebar)
  const refreshCurrentTab = async () => {
    try {
      if (selectedTab === "STAFFINGREQUESTS") {
        // Always refresh to page 1 on close to avoid stale filters/pagination
        setCurrentPage(1);
        const params = new URLSearchParams();
        params.set("page", "1");
        params.set("limit", String(rowsPerPage));
        params.set("search", searchTerm || "");
        if (filterClient) params.set("client_id", String(filterClient));
        if (filterLocation) params.set("location_id", String(filterLocation));
        if (filterFromDate) params.set("fromDate", filterFromDate);
        if (filterToDate) params.set("toDate", filterToDate);
        if (filterSortBy && filterSortBy !== "all")
          params.set("sortBy", filterSortBy);
        params.set("sortDirection", filterSortDirection || "asc");
        params.set("all", "1");
        params.set("showAllHistory", "1");
        if (staffingShowDeleted) params.set("includeDeleted", "1");
        console.log(
          "refreshCurrentTab - params:",
          params.toString(),
          "staffingShowDeleted:",
          staffingShowDeleted,
        );
        const token =
          localStorage.getItem("token") || localStorage.getItem("authToken");
        const resp = await fetch(
          `${BASE_URL}/api/jobs/staffingrequests?${params.toString()}`,
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
        );
        if (!resp.ok) {
          const err = await resp.text();
          console.error("Failed to refresh staffing requests:", err);
          showError("Failed to refresh staffing requests");
          return;
        }
        const result = await resp.json();
        console.log(
          "refreshCurrentTab: fetched",
          (result.data || []).length,
          "rows",
        );
        setData(result.data || []);
        setTotalPages(result.totalPages || 1);
        setTotalCount(result.total || 0);
      } else {
        // Trigger the main fetch useEffect by nudging currentPage (safe no-op if already 1)
        setCurrentPage((p) => Math.max(1, p));
      }
    } catch (err) {
      console.error("Failed to refresh tab data:", err);
      showError("Failed to refresh staffing requests");
    }
  };

  const staffRequestIdParam = searchParams.get("staffRequestId");
  const staffingRequestPreview = location.state?.staffingRequestPreview;

  useEffect(() => {
    if (!embedUsesProps || !embedStaffRequestId) return;
    const requestId = Number(embedStaffRequestId);
    if (!Number.isFinite(requestId) || requestId <= 0) return;

    setSelectedTab("STAFFINGREQUESTS");
    setActiveSidebarTab("Details");

    const previewRow =
      embedPreviewRow &&
      Number(embedPreviewRow.id || embedPreviewRow.staff_request_id) ===
        requestId
        ? embedPreviewRow
        : null;
    setSelectedRequest(
      previewRow || { id: requestId, staff_request_id: requestId },
    );

    let cancelled = false;
    (async () => {
      try {
        const token =
          localStorage.getItem("token") || localStorage.getItem("authToken");
        const res = await fetch(
          `${BASE_URL}/api/jobs/staffingrequests/${requestId}`,
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
        );
        const json = await res.json();
        if (!res.ok || cancelled) return;
        const fullRequest = json?.data || json;
        if (!fullRequest || cancelled) return;
        setSelectedRequest(fullRequest);
        const locationId = fullRequest.client_location_id;
        if (locationId) {
          setSelectedLocations([String(locationId)]);
        } else {
          setSelectedLocations([]);
        }
      } catch (err) {
        console.error("Embed staffing request load failed:", err);
        showError("Failed to open staffing request");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [embedUsesProps, embedStaffRequestId, embedPreviewRow]);

  useEffect(() => {
    if (embedUsesProps) return;
    if (!staffRequestIdParam) return;
    const requestId = Number(staffRequestIdParam);
    if (!Number.isFinite(requestId) || requestId <= 0) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("staffRequestId");
          return next;
        },
        { replace: true },
      );
      return;
    }

    setSelectedTab("STAFFINGREQUESTS");
    setActiveSidebarTab("Details");

    const previewRow =
      staffingRequestPreview &&
      Number(staffingRequestPreview.id || staffingRequestPreview.staff_request_id) ===
        requestId
        ? staffingRequestPreview
        : null;
    if (previewRow) {
      setSelectedRequest(previewRow);
    } else {
      setSelectedRequest({ id: requestId, staff_request_id: requestId });
    }

    let cancelled = false;
    (async () => {
      try {
        const token =
          localStorage.getItem("token") || localStorage.getItem("authToken");
        const res = await fetch(
          `${BASE_URL}/api/jobs/staffingrequests/${requestId}`,
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
        );
        const json = await res.json();
        if (!res.ok || cancelled) return;
        const fullRequest = json?.data || json;
        if (!fullRequest || cancelled) return;

        setSelectedRequest(fullRequest);
        const locationId = fullRequest.client_location_id;
        if (locationId) {
          setSelectedLocations([String(locationId)]);
        } else {
          setSelectedLocations([]);
        }
      } catch (err) {
        console.error("Deep link staffing request load failed:", err);
        showError("Failed to open staffing request");
      } finally {
        if (!cancelled) {
          const nextParams = new URLSearchParams(searchParams);
          nextParams.delete("staffRequestId");
          const qs = nextParams.toString();
          navigate(
            {
              pathname: location.pathname,
              search: qs ? `?${qs}` : "",
            },
            { replace: true, state: {} },
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once per staffRequestId in URL
  }, [staffRequestIdParam]);

  // Fetch shifts when Shifts tab is active
  useEffect(() => {
    if (selectedRequest && activeSidebarTab === "Shifts") {
      fetchShiftsForRequest(selectedRequest.id);
    }
  }, [selectedRequest?.id, activeSidebarTab]);

  // Fetch clients for create request modal
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const token =
          localStorage.getItem("token") || localStorage.getItem("authToken");
        const res = await fetch(
          `${BASE_URL}/api/clients/clients?all=1&status=active`,
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
        );
        const result = await res.json();
        setClients(result.data || []);
      } catch (err) {
        console.error(err);
        setClients([]);
      }
    };

    if (showCreateRequestModal) {
      fetchClients();
    }
  }, [showCreateRequestModal]);

  useEffect(() => {
    if (!showCreateRequestModal || !createRequestDirty) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () =>
      window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [showCreateRequestModal, createRequestDirty]);

  useEffect(() => {
    return () => {
      if (createRequestValidationTimerRef.current) {
        clearTimeout(createRequestValidationTimerRef.current);
      }
    };
  }, []);

  // Close location dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        locationDropdownRef.current &&
        !locationDropdownRef.current.contains(event.target)
      ) {
        setShowLocationDropdown(false);
      }
      if (
        createLocationDropdownRef.current &&
        !createLocationDropdownRef.current.contains(event.target)
      ) {
        setShowCreateLocationDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Load client locations and divisions when client is selected
  useEffect(() => {
    let isCancelled = false;

    const fetchClientLocationsAndDivisions = async () => {
      if (!createClientId) {
        setCreateClientLocations([]);
        setCreateDivisions([]);
        setCreateSelectedLocationIds([]);
        setCreateContactId("");
        setCreateSupervisorId("");
        setCreateDivisionId("");
        setCreateDefaultsLoading(false);
        return;
      }

      try {
        setCreateDefaultsLoading(true);
        setCreateClientLocations([]);
        setCreateDivisions([]);
        setCreateSelectedLocationIds([]);
        setCreateContactId("");
        setCreateSupervisorId("");
        setCreateDivisionId("");

        const token =
          localStorage.getItem("token") || localStorage.getItem("authToken");
        const authOpts = token
          ? { headers: { Authorization: `Bearer ${token}` } }
          : undefined;

        const [locationsRes, divisionsRes] = await Promise.all([
          fetch(
            `${BASE_URL}/api/clients/${createClientId}/locations`,
            authOpts,
          ),
          fetch(
            `${BASE_URL}/api/clients/divisions/by-client/${createClientId}`,
            authOpts,
          ),
        ]);

        const [locationsData, divisionsData] = await Promise.all([
          locationsRes.json(),
          divisionsRes.json(),
        ]);

        if (isCancelled) return;

        const availableLocations = locationsData?.data || [];
        const availableDivisions = divisionsData?.data || [];

        setCreateClientLocations(availableLocations);
        setCreateDivisions(availableDivisions);
        setCreateDivisionId(
          availableDivisions[0]?.id ? String(availableDivisions[0].id) : "",
        );

        try {
          const detailsRes = await fetch(
            `${BASE_URL}/api/clients/${createClientId}/details`,
            authOpts,
          );
          const detailsJson = await detailsRes.json().catch(() => ({}));
          if (!isCancelled) {
            const defaultContactId =
              detailsJson?.data?.default_contact_account_id;
            const defaultSupervisorId =
              detailsJson?.data?.default_supervisor_account_id;
            createClientDefaultsRef.current = {
              contactId: defaultContactId ? String(defaultContactId) : "",
              supervisorId: defaultSupervisorId
                ? String(defaultSupervisorId)
                : "",
            };
          }
        } catch (detailsErr) {
          console.warn("Failed to load client defaults for staffing request:", detailsErr);
          if (!isCancelled) {
            createClientDefaultsRef.current = { contactId: "", supervisorId: "" };
          }
        }
      } catch (err) {
        console.error("Error fetching client locations and divisions:", err);
        if (isCancelled) return;
        setCreateClientLocations([]);
        setCreateDivisions([]);
        setCreateSelectedLocationIds([]);
      } finally {
        if (!isCancelled) setCreateDefaultsLoading(false);
      }
    };

    fetchClientLocationsAndDivisions();
    return () => {
      isCancelled = true;
    };
  }, [createClientId]);

  // Load location-scoped contact/supervisor options when client + location are set.
  useEffect(() => {
    let isCancelled = false;
    const locationId =
      createSelectedLocationIds.length > 0
        ? String(createSelectedLocationIds[0])
        : "";

    const loadOptions = async () => {
      if (!createClientId || !locationId) {
        setCreateContacts([]);
        setCreateSupervisors([]);
        if (!createClientId) {
          setCreateContactId("");
          setCreateSupervisorId("");
        }
        setCreateDefaultsLoading(false);
        return;
      }

      try {
        setCreateDefaultsLoading(true);
        const { contacts, supervisors } = await fetchStaffRequestContactOptions(
          createClientId,
          locationId,
        );
        if (isCancelled) return;

        setCreateContacts(contacts);
        setCreateSupervisors(supervisors);

        const contactInList = (id) =>
          id &&
          contacts.some(
            (c) => String(getClientContactRowAccountId(c)) === String(id),
          );
        const supervisorInList = (id) =>
          id &&
          supervisors.some(
            (s) => String(getClientContactRowAccountId(s)) === String(id),
          );

        setCreateContactId((prev) => {
          if (contactInList(prev)) return String(prev);
          const def = createClientDefaultsRef.current.contactId;
          if (contactInList(def)) return def;
          return "";
        });
        setCreateSupervisorId((prev) => {
          if (supervisorInList(prev)) return String(prev);
          const def = createClientDefaultsRef.current.supervisorId;
          if (supervisorInList(def)) return def;
          return "";
        });
      } catch (err) {
        console.error("Error loading staffing request contact options:", err);
        if (isCancelled) return;
        setCreateContacts([]);
        setCreateSupervisors([]);
        setCreateContactId("");
        setCreateSupervisorId("");
      } finally {
        if (!isCancelled) setCreateDefaultsLoading(false);
      }
    };

    loadOptions();
    return () => {
      isCancelled = true;
    };
  }, [createClientId, createSelectedLocationIds]);

  // Timesheet filter panel: fetch clients with search so dropdown shows filtered list when user types
  useEffect(() => {
    if (!jobsFilterPanelOpen || selectedTab !== "TIMESHEETS") return;

    const timer = setTimeout(async () => {
      setClientLoading(true);
      try {
        const search = (clientSearch || "").trim();
        const url = `${BASE_URL}/api/clients/clients?all=1&search=${encodeURIComponent(search)}`;
        const res = await fetch(url);
        const data = await res.json();
        const rows = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : [];
        setClientLocByTab((prev) => ({
          ...prev,
          TIMESHEETS: {
            ...(prev.TIMESHEETS || emptyJobTabClientLoc()),
            filterClientList: rows,
          },
        }));
      } catch (err) {
        console.error("Client fetch error:", err);
        setClientLocByTab((prev) => ({
          ...prev,
          TIMESHEETS: {
            ...(prev.TIMESHEETS || emptyJobTabClientLoc()),
            filterClientList: [],
          },
        }));
      } finally {
        setClientLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [clientSearch, jobsFilterPanelOpen, selectedTab]);

  const fetchFilterClientLocations = async (clientId) => {
    try {
      if (!clientId) return [];

      const res = await fetch(
        `${BASE_URL}/api/jobs/client-locations/${clientId}`,
      );
      const data = await res.json();

      return data; // [{ id, address_id, state }]
    } catch (err) {
      console.error("Filter client locations fetch error:", err);
      return [];
    }
  };

  // Fetch dropdown options for shifts filter
  const fetchShiftsStaffOptions = async (staffRequestId) => {
    try {
      const res = await fetch(`${BASE_URL}/api/jobs/staff`);
      const data = await res.json();
      const staffArray = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [];
      const options = staffArray.map((staff) => ({
        value: String(staff.account_id || staff.id || ""),
        label:
          staff.name ||
          `${staff.first_name || ""} ${staff.last_name || ""}`.trim(),
      }));
      cacheStaffFilterLabels(options);
      setStaffFilterOptions(options);
      setStaffOptions(options);
    } catch (err) {
      console.error("Staff options fetch error:", err);
      setStaffFilterOptions([]);
    }
  };

  // const fetchShiftsOutsourcedAgencyOptions = async (staffRequestId) => {
  //   console.log(
  //     "🔍 fetchShiftsOutsourcedAgencyOptions called with staffRequestId:",
  //     staffRequestId,
  //   );
  //   if (!staffRequestId) {
  //     console.log("❌ No staffRequestId provided");
  //     setShiftsOutsourcedAgencyOptions([{ value: "", label: "All agencies" }]);
  //     return;
  //   }
  //   try {
  //     const url = `${BASE_URL}/api/personnel/outsourced-agencies/${staffRequestId}`;
  //     console.log("🌐 Fetching outsourced agencies from:", url);
  //     const res = await fetch(url);
  //     console.log("📡 Response status:", res.status);
  //     const data = await res.json();
  //     console.log("📦 Outsourced agencies data:", data);
  //     const agencyArray = Array.isArray(data)
  //       ? data
  //       : Array.isArray(data?.data)
  //         ? data.data
  //         : [];
  //     const options = agencyArray.map((agency) => ({
  //       value: agency.id.toString(),
  //       label: agency.name,
  //     }));
  //     console.log(
  //       "✅ Setting outsourced agency options:",
  //       options.length,
  //       "items",
  //     );
  //     setShiftsOutsourcedAgencyOptions([
  //       { value: "", label: "All agencies" },
  //       ...options,
  //     ]);
  //   } catch (err) {
  //     console.error("❌ Outsourced agency options fetch error:", err);
  //     setShiftsOutsourcedAgencyOptions([{ value: "", label: "All agencies" }]);
  //   }
  // };

  const fetchShiftsOutsourcedAgencyOptions = async () => {
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      const url = `${BASE_URL}/api/jobs/outsourced-agencies`;
      const res = await fetch(
        url,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      );
      if (!res.ok)
        throw new Error(`Outsourced agencies fetch failed: ${res.status}`);
      const data = await res.json();
      const agencyArray = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [];
      const options = agencyArray.map((agency) => ({
        value: String(agency.id || agency.payer_id),
        label: String(agency.name),
      }));
      setShiftsOutsourcedAgencyOptions([
        { value: "", label: "All agencies" },
        ...options,
      ]);
    } catch (err) {
      console.error("Outsourced agency options fetch error:", err);
      setShiftsOutsourcedAgencyOptions([{ value: "", label: "All agencies" }]);
    }
  };

  const fetchShiftsSupervisorOptions = async (staffRequestId) => {
    console.log(
      "🔍 fetchShiftsSupervisorOptions called with staffRequestId:",
      staffRequestId,
    );
    if (!staffRequestId) {
      console.log("❌ No staffRequestId provided");
      setShiftsSupervisorOptions([{ value: "", label: "All supervisors" }]);
      return;
    }
    try {
      const url = `${BASE_URL}/api/personnel/supervisors/${staffRequestId}`;
      console.log("🌐 Fetching supervisors from:", url);
      const res = await fetch(url);
      console.log("📡 Response status:", res.status);
      const data = await res.json();
      console.log("📦 Supervisors data:", data);
      const supervisorArray = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [];
      const options = supervisorArray.map((supervisor) => ({
        value: supervisor.id.toString(),
        label: supervisor.name,
      }));
      console.log("✅ Setting supervisor options:", options.length, "items");
      setShiftsSupervisorOptions([
        { value: "", label: "All supervisors" },
        ...options,
      ]);
    } catch (err) {
      console.error("❌ Shift supervisor options fetch error:", err);
      setShiftsSupervisorOptions([{ value: "", label: "All supervisors" }]);
    }
  };

  const fetchShiftsClientPositionOptions = async (staffRequestId) => {
    console.log(
      "🔍 fetchShiftsClientPositionOptions called with staffRequestId:",
      staffRequestId,
    );
    if (!staffRequestId) {
      console.log("❌ No staffRequestId provided");
      setShiftsClientPositionOptions([{ value: "", label: "All positions" }]);
      return;
    }
    try {
      const url = `${BASE_URL}/api/clients/positions/${staffRequestId}`;
      console.log("🌐 Fetching client positions from:", url);
      const res = await fetch(url);
      console.log("📡 Response status:", res.status);
      const data = await res.json();
      console.log("📦 Client positions data:", data);
      const positionArray = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [];
      const options = positionArray.map((position) => ({
        value: position.id.toString(),
        label: position.name,
      }));
      console.log(
        "✅ Setting client position options:",
        options.length,
        "items",
      );
      setShiftsClientPositionOptions([
        { value: "", label: "All positions" },
        ...options,
      ]);
    } catch (err) {
      console.error("❌ Client position options fetch error:", err);
      setShiftsClientPositionOptions([{ value: "", label: "All positions" }]);
    }
  };

  // Fetch shift details for selected staffing request
  const fetchShiftsForRequest = async (requestId) => {
    console.log("🔍 fetchShiftsForRequest called with requestId:", requestId);
    try {
      const url = `${BASE_URL}/api/jobs/staffingrequests/${requestId}/shifts`;
      console.log("🌐 Fetching shifts from:", url);
      const response = await fetch(url);
      console.log("📡 Response status:", response.status);
      const data = await response.json();
      console.log("📦 Shifts data:", data);
      if (data.success) {
        console.log(
          "✅ Setting shifts data with",
          data.data?.length || 0,
          "shifts",
        );
        setShiftsData(data);
      } else {
        console.log("❌ Shifts API returned success=false");
      }
    } catch (error) {
      console.error("❌ Error fetching shifts:", error);
    }
  };


  useEffect(() => {
    // load client locations whenever a client is selected (previously this
    // logic skipped the TIMESHEETS tab, which accidentally removed the
    // location field from the timesheet filter panel). we now always fetch
    // locations when a client id exists; clearing happens when the client is
    // cleared.
    // whenever client changes we clear whatever location was selected
    setFilterLocation("");
    setFilterLocations([]);
    if (filterClient) {
      console.log("Fetching locations for client:", filterClient);
      fetchFilterClientLocations(filterClient).then((locs) => {
        console.log("Received locations:", locs);
        setFilterLocationList(Array.isArray(locs) ? locs : []);
      });
    } else {
      // clear out locations when no client selected
      setFilterLocationList([]);
    }
  }, [filterClient, selectedTab]);

  //! end of the time sheet filter panel sections

  useEffect(() => {
    // Populate option lists for details sidebar when opening ANY request (edit/view or create)
    if (selectedRequest) {
      (async () => {
        try {
          // Ensure we have a clients list available for name -> id fallback
          if (!clients || clients.length === 0) {
            try {
              const cRes = await fetch(
                `${BASE_URL}/api/clients/clients?all=1`,
              );
              const cData = await cRes.json();
              setClients(cData.data || []);
            } catch (err) {
              console.error(
                "Failed to fetch clients for details sidebar fallback:",
                err,
              );
            }
          }

          let clientId = selectedRequest.client_id;
          if (
            !clientId &&
            selectedRequest.clientName &&
            clients &&
            clients.length
          ) {
            const found = clients.find(
              (c) => c.name === selectedRequest.clientName,
            );
            if (found) clientId = found.id;
          }

          if (clientId) {
            const token =
              localStorage.getItem("token") ||
              localStorage.getItem("authToken");
            const authOpts = token
              ? { headers: { Authorization: `Bearer ${token}` } }
              : undefined;

            // fetch locations and divisions (contacts load when location is known)
            const [locRes, divisionsRes] = await Promise.all([
              fetch(`${BASE_URL}/api/clients/${clientId}/locations`, authOpts),
              fetch(
                `${BASE_URL}/api/clients/divisions/by-client/${clientId}`,
                authOpts,
              ),
            ]);

            // Parse each response so we can both set state and use the arrays locally
            let locData = [];
            let divisionsData = [];
            try {
              const d = await locRes.json();
              locData = d.data || [];
              setClientLocations(locData);
            } catch (e) {
              setClientLocations([]);
            }
            try {
              const d = await divisionsRes.json();
              divisionsData = d.data || [];
              setDivisions(divisionsData);
            } catch (e) {
              setDivisions([]);
            }

            // After we've populated the option lists, initialize editableRequest from the selectedRequest
            // Coerce IDs to strings so <select value={...}> matches option values.
            // If the row only contains display names (e.g. clientLocationName, contact), try to map those to ids.
            const initial = {
              notes: selectedRequest.notes || "",
              client_location_id: "",
              contact_account_id: "",
              supervisor_contact_account_id: "",
              division_id: "",
            };

            // client location: prefer explicit id, otherwise match by name
            if (selectedRequest.client_location_id) {
              const locId = String(selectedRequest.client_location_id);
              console.log("DEBUG LOAD - Initializing location:", {
                selectedRequest_id: selectedRequest.id,
                client_location_id: selectedRequest.client_location_id,
                locId,
                setting_selectedLocations: [locId],
              });
              initial.client_location_id = locId;
              setSelectedLocations([locId]);
            } else {
              console.log("DEBUG LOAD - No location to initialize");
              setSelectedLocations([]);
            }

            // Handle location name fallback
            if (selectedRequest.clientLocationName) {
              const found = locData.find((l) => {
                if (!l) return false;
                const name = (l.name || "").trim();
                const street = (l.street || "").trim();
                return (
                  name === selectedRequest.clientLocationName ||
                  street === selectedRequest.clientLocationName
                );
              });
              if (found) {
                const foundLocId = String(
                  found.location_id ?? found.id ?? "",
                );
                initial.client_location_id = foundLocId;
                if (foundLocId) setSelectedLocations([foundLocId]);
              }
            }

            const contactsData = [];
            const supervisorsData = [];

            // contact: prefer explicit id (name match runs after location-scoped options load)
            const normalizedContactAccountId = cleanId(
              selectedRequest.contact_account_id,
            );
            if (normalizedContactAccountId) {
              initial.contact_account_id = normalizedContactAccountId;
            } else if (
              getSafeDisplayText(
                selectedRequest.contact,
                selectedRequest.contactName,
              )
            ) {
              const target = getSafeDisplayText(
                selectedRequest.contact,
                selectedRequest.contactName,
              )
                .trim()
                .toLowerCase();
              const found = contactsData.find((c) => {
                const candName = String(
                  c.name || `${c.first_name || ""} ${c.last_name || ""}`,
                )
                  .trim()
                  .toLowerCase();
                const candFirst = String(c.first_name || "")
                  .trim()
                  .toLowerCase();
                const candLast = String(c.last_name || "")
                  .trim()
                  .toLowerCase();
                const candEmail = String(c.email || "")
                  .trim()
                  .toLowerCase();
                const candPhone = String(c.phone || "")
                  .trim()
                  .toLowerCase();
                if (!target) return false;
                if (
                  candName === target ||
                  candFirst === target ||
                  candLast === target ||
                  candEmail === target ||
                  candPhone === target
                )
                  return true;
                // partial contains match fallback
                if (candName.includes(target) || target.includes(candName))
                  return true;
                return false;
              });
              if (found) {
                const cid = getClientContactRowAccountId(found);
                if (cid) initial.contact_account_id = String(cid);
              }
            }

            // supervisor: prefer explicit id, otherwise flexible name match
            const normalizedSupervisorAccountId = cleanId(
              selectedRequest.supervisor_contact_account_id,
            );
            if (normalizedSupervisorAccountId) {
              initial.supervisor_contact_account_id =
                normalizedSupervisorAccountId;
            } else {
              // the list query aliases supervisor as supervisorName; fall back to either field
              const target = getSafeDisplayText(
                selectedRequest.supervisor,
                selectedRequest.supervisorName,
              )
                .trim()
                .toLowerCase();
              const found = supervisorsData.find((s) => {
                const candName = String(
                  `${s.first_name || ""} ${s.last_name || ""}`,
                )
                  .trim()
                  .toLowerCase();
                const candFirst = String(s.first_name || "")
                  .trim()
                  .toLowerCase();
                const candLast = String(s.last_name || "")
                  .trim()
                  .toLowerCase();
                if (!target) return false;
                if (
                  candName === target ||
                  candFirst === target ||
                  candLast === target
                )
                  return true;
                if (candName.includes(target) || target.includes(candName))
                  return true;
                return false;
              });
              if (found) {
                const sid = getClientContactRowAccountId(found);
                if (sid) initial.supervisor_contact_account_id = String(sid);
              }
            }

            // division
            const normalizedDivisionId = cleanId(selectedRequest.division_id);
            if (normalizedDivisionId) {
              initial.division_id = normalizedDivisionId;
            } else if (
              getSafeDisplayText(
                selectedRequest.divisionName,
                selectedRequest.division,
              )
            ) {
              const targetDivision = getSafeDisplayText(
                selectedRequest.divisionName,
                selectedRequest.division,
              );
              const found = divisionsData.find(
                (d) =>
                  String(d.name || d.label || "").trim().toLowerCase() ===
                  targetDivision.toLowerCase(),
              );
              if (found) initial.division_id = String(found.id);
            }

            // Apply the initial editable request (avoid spreading null).
            // Do not wipe contact/supervisor/division ids if the filtered contact list
            // omitted the selected person but selectedRequest already carried account ids.
            setEditableRequest((prev) => {
              const base = { ...(prev || {}), ...initial };
              const restoreIfCleared = (key) => {
                const nextVal = String(initial[key] ?? "").trim();
                const prevVal = String(prev?.[key] ?? "").trim();
                if (!nextVal && prevVal) base[key] = prev[key];
              };
              restoreIfCleared("contact_account_id");
              restoreIfCleared("supervisor_contact_account_id");
              restoreIfCleared("division_id");
              return base;
            });
          } else {
            // No client id available — still seed editableRequest with any known ids/notes
            setEditableRequest((prev) => ({
              ...prev,
              notes: selectedRequest.notes || "",
              client_location_id: cleanId(selectedRequest.client_location_id),
              contact_account_id: cleanId(selectedRequest.contact_account_id),
              supervisor_contact_account_id: cleanId(
                selectedRequest.supervisor_contact_account_id,
              ),
              division_id: cleanId(selectedRequest.division_id),
            }));
          }
        } catch (err) {
          console.error("Error populating details sidebar options:", err);
        }
      })();
    } else {
      // Clear selectedLocations when no request is selected
      setSelectedLocations([]);
      setContacts([]);
      setSupervisors([]);
    }
  }, [selectedRequest, clients]);

  // Edit staffing request: reload contact/supervisor options when location changes.
  useEffect(() => {
    if (!selectedRequest?.id) return;

    let clientId =
      selectedRequest.client_id ?? selectedRequest.clientId ?? null;
    if (!clientId && selectedRequest.clientName && clients?.length) {
      const found = clients.find((c) => c.name === selectedRequest.clientName);
      if (found) clientId = found.id ?? found.client_id ?? null;
    }

    const locationId = String(
      (selectedLocations.length > 0 ? selectedLocations[0] : "") ||
        editableRequest?.client_location_id ||
        selectedRequest.client_location_id ||
        "",
    ).trim();

    if (!clientId || !locationId) {
      setContacts([]);
      setSupervisors([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { contacts, supervisors } = await fetchStaffRequestContactOptions(
          clientId,
          locationId,
        );
        if (cancelled) return;
        setContacts(contacts);
        setSupervisors(supervisors);

        const contactInList = (id) =>
          id &&
          contacts.some(
            (c) => String(getClientContactRowAccountId(c)) === String(id),
          );
        const supervisorInList = (id) =>
          id &&
          supervisors.some(
            (s) => String(getClientContactRowAccountId(s)) === String(id),
          );

        setEditableRequest((r) => {
          if (!r) return r;
          const next = {
            ...r,
            client_location_id: locationId,
          };

          let contactId = String(r.contact_account_id || "").trim();
          if (contactId && !contactInList(contactId)) contactId = "";
          if (!contactId) {
            const fromRequest = cleanId(selectedRequest.contact_account_id);
            if (contactInList(fromRequest)) contactId = fromRequest;
            else if (
              getSafeDisplayText(
                selectedRequest.contact,
                selectedRequest.contactName,
              )
            ) {
              const target = getSafeDisplayText(
                selectedRequest.contact,
                selectedRequest.contactName,
              )
                .trim()
                .toLowerCase();
              const found = contacts.find((c) => {
                const candName = String(
                  c.name || `${c.first_name || ""} ${c.last_name || ""}`,
                )
                  .trim()
                  .toLowerCase();
                return (
                  candName === target ||
                  candName.includes(target) ||
                  target.includes(candName)
                );
              });
              const cid = found ? getClientContactRowAccountId(found) : null;
              if (cid) contactId = String(cid);
            }
          }
          next.contact_account_id = contactId;

          let supervisorId = String(
            r.supervisor_contact_account_id || "",
          ).trim();
          if (supervisorId && !supervisorInList(supervisorId)) supervisorId = "";
          if (!supervisorId) {
            const fromRequest = cleanId(
              selectedRequest.supervisor_contact_account_id,
            );
            if (supervisorInList(fromRequest)) supervisorId = fromRequest;
            else if (
              getSafeDisplayText(
                selectedRequest.supervisor,
                selectedRequest.supervisorName,
              )
            ) {
              const target = getSafeDisplayText(
                selectedRequest.supervisor,
                selectedRequest.supervisorName,
              )
                .trim()
                .toLowerCase();
              const found = supervisors.find((s) => {
                const candName = String(
                  s.name || `${s.first_name || ""} ${s.last_name || ""}`,
                )
                  .trim()
                  .toLowerCase();
                return (
                  candName === target ||
                  candName.includes(target) ||
                  target.includes(candName)
                );
              });
              const sid = found ? getClientContactRowAccountId(found) : null;
              if (sid) supervisorId = String(sid);
            }
          }
          next.supervisor_contact_account_id = supervisorId;

          return next;
        });
      } catch (err) {
        console.error(
          "Error loading staffing request contact options for edit:",
          err,
        );
        if (!cancelled) {
          setContacts([]);
          setSupervisors([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    selectedRequest?.id,
    selectedRequest?.client_id,
    selectedRequest?.clientName,
    selectedRequest?.contact_account_id,
    selectedRequest?.contact,
    selectedRequest?.contactName,
    selectedRequest?.supervisor_contact_account_id,
    selectedRequest?.supervisor,
    selectedRequest?.supervisorName,
    selectedRequest?.client_location_id,
    selectedLocations,
    editableRequest?.client_location_id,
    clients,
  ]);

  const formatShiftDateTime = (value, includeDate = true) =>
    formatAppDateTime(value, { includeDate, emptyValue: "" });

  const shiftRequiresNotifyChange = (shift) =>
    shift?.notify_staff_change === 1 ||
    shift?.notify_staff_change === true ||
    shift?.notifyStaffChange === true;

  const formatDurationAfterBreak = (
    start,
    end,
    unpaidBreakMinutes,
    fallback,
  ) => {
    const fallbackValue = String(fallback || "").trim();
    const from = start ? new Date(start) : null;
    const to = end ? new Date(end) : null;
    if (
      !from ||
      !to ||
      Number.isNaN(from.getTime()) ||
      Number.isNaN(to.getTime())
    )
      return fallbackValue;
    let breakMinutes = Number(unpaidBreakMinutes || 0);
    if (
      unpaidBreakMinutes === true ||
      unpaidBreakMinutes === 1 ||
      unpaidBreakMinutes === "1"
    ) {
      breakMinutes = 30;
    }
    const minutes = Math.max(
      Math.round((to.getTime() - from.getTime()) / 60000) - breakMinutes,
      0,
    );
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (breakMinutes > 0) {
      return `${hours}h ${String(mins).padStart(2, "0")}m after ${breakMinutes}m break`;
    }
    return `${hours}h ${String(mins).padStart(2, "0")}m`;
  };

  const resolveFirstPositiveBreakMinutes = (...values) => {
    for (const value of values) {
      const minutes = normalizeBreakMinutes(value);
      if (minutes > 0) return minutes;
    }
    return 0;
  };

  const resolveScheduledBreakMinutes = (shift) =>
    resolveFirstPositiveBreakMinutes(
      shift?.unpaid_break,
      shift?.unpaidBreak,
    );

  const resolveActualBreakMinutes = (shift) => {
    const rawActual =
      shift?.actualUnpaidBreakMinutes ?? shift?.actual_unpaid_break_minutes;
    if (rawActual !== undefined && rawActual !== null && rawActual !== "") {
      const n = Number(rawActual);
      if (Number.isFinite(n) && n >= 0) {
        return n;
      }
    }
    return resolveFirstPositiveBreakMinutes(
      shift?.checkinUnpaidBreakMinutes,
      shift?.checkin_unpaid_break_minutes,
      shift?.unpaidBreakMinutes,
      shift?.unpaid_break_minutes,
      shift?.unpaid_break,
      shift?.unpaidBreak,
    );
  };

  const renderTable = () => {
    switch (selectedTab) {
      case "STAFFINGREQUESTS":
        // Filter the data: when "Show deleted" is active, only include deleted rows
        // otherwise show everything.  We still derive deletion state per-row for actions below.
        const displayData = staffingShowDeleted
          ? data.filter((req) => {
              const st = String(req.state || req.status || "").toLowerCase();
              return st === "deleted";
            })
          : data;

        return (
          <table className="w-full min-w-[1000px] table-fixed border-collapse">
            <thead className="bg-[#dc1f0080] text-white sticky top-0 z-20">
              <tr>
                <th className="py-3 px-2 text-left text-sm font-medium text-white">
                  Request <StaffingSortArrow colKey="request" />
                </th>
                <th className="py-3 px-2 text-left text-sm font-medium text-white">
                  Client <StaffingSortArrow colKey="client" />
                </th>
                <th className="py-3 px-2 text-left text-sm font-medium text-white">
                  Contact <StaffingSortArrow colKey="contact" />
                </th>
                <th className="py-3 px-2 text-left text-sm font-medium text-white">
                  Time <StaffingSortArrow colKey="time" />
                </th>
                <th className="py-3 px-2 text-left text-sm font-medium text-white">
                  Shifts
                </th>
                <th className="py-3 px-2 text-left text-sm font-medium text-white">
                  Notes
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {displayData.map((req) => (
                <tr
                  key={req.id}
                  className="border-t border-border hover:bg-table-row-hover transition-colors"
                >
                  <td
                    className="py-3 px-2 break-words text-red-600 cursor-pointer font-medium"
                    onClick={async () => {
                      console.log("DEBUG MODAL OPEN - Clicking request:", {
                        req_id: req.id,
                        req_client_location_id: req.client_location_id,
                        req_object: req,
                      });

                      setSelectedRequest(req);

                      // Fetch full request details to ensure we have complete data
                      try {
                        console.log(
                          "DEBUG MODAL OPEN - Fetching full request details for ID:",
                          req.id,
                        );
                        const response = await fetch(
                          `${BASE_URL}/api/jobs/staffingrequests/${req.id}`,
                        );
                        const data = await response.json();
                        const fullRequest = data?.data || data;

                        console.log(
                          "DEBUG MODAL OPEN - Received full request data:",
                          {
                            fullRequest,
                            client_location_id: fullRequest?.client_location_id,
                            client_location_id_type:
                              typeof fullRequest?.client_location_id,
                          },
                        );

                        if (fullRequest) {
                          setSelectedRequest(fullRequest);
                          // Initialize selected locations from the full request data
                          const locationId = fullRequest.client_location_id;
                          if (locationId) {
                            console.log(
                              "DEBUG MODAL OPEN - Setting selectedLocations:",
                              [String(locationId)],
                            );
                            setSelectedLocations([String(locationId)]);
                          } else {
                            console.log(
                              "DEBUG MODAL OPEN - No location found, clearing selectedLocations",
                            );
                            setSelectedLocations([]);
                          }
                        }
                      } catch (err) {
                        console.error(
                          "DEBUG MODAL OPEN - Failed to fetch full request details:",
                          err,
                        );
                        // Fallback to using the data from the table
                        const locationId = req.client_location_id;
                        if (locationId) {
                          setSelectedLocations([String(locationId)]);
                        } else {
                          setSelectedLocations([]);
                        }
                      }
                    }}
                  >
                    {req.id}
                  </td>
                  <td className="py-3 px-2">
                    <div className="leading-tight">
                      <div>{req.clientName || "—"}</div>
                      <div className="text-xs text-muted-foreground">
                        {req.clientLocationName || ""}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    <div className="max-w-[260px] break-words whitespace-normal">
                      {req.contact || "—"}
                    </div>
                  </td>
                  <td className="py-3 px-2">{formatStaffingRequestTimeRange(req)}</td>
                  <td className="py-3 px-2">{formatStaffingRequestShifts(req)}</td>
                  <td className="py-3 px-2">{req.notes}</td>
                  <td className="py-1 px-1">
                    <ActionDropdown
                      actions={
                        // determine per-row deletion state (some APIs use 'state', others 'status')
                        (() => {
                          const stateVal = String(
                            req.state || req.status || "",
                          ).toLowerCase();
                          const isDeletedRow = stateVal === "deleted";
                          if (isDeletedRow) {
                            return [
                              {
                                label: "Restore",
                                onClick: async () => {
                                  try {
                                    const res = await fetch(
                                      `${BASE_URL}/api/jobs/staffingrequests/${req.id}/restore`,
                                      { method: "PUT" },
                                    );
                                    const data = await res.json();
                                    if (!res.ok)
                                      throw new Error(
                                        data.message || "Restore failed",
                                      );
                                    showSuccess("Staffing request restored");
                                    setReloadKey((k) => k + 1);
                                    setCurrentPage(1);
                                  } catch (err) {
                                    console.error(err);
                                    showError(err.message || "Server error");
                                  }
                                },
                              },
                            ];
                          } else {
                            return [
                              {
                                label: "Replicate",
                                onClick: () => openReplicateRequestModal(req),
                              },
                              {
                                label: "Delete",
                                onClick: async () => {
                                  try {
                                    const res = await fetch(
                                      `${BASE_URL}/api/jobs/staffingrequests/${req.id}`,
                                      { method: "DELETE" },
                                    );
                                    const data = await res.json();
                                    if (!res.ok)
                                      throw new Error(
                                        data.message || "Delete failed",
                                      );
                                    showSuccess("Deleted");
                                    // refresh the list so the deleted row disappears immediately
                                    setReloadKey((k) => k + 1);
                                    setCurrentPage(1);
                                  } catch (err) {
                                    console.error(err);
                                    showError(err.message || "Server error");
                                  }
                                },
                                danger: true,
                              },
                            ];
                          }
                        })()
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      case "SHIFTS":
        return (
          <table className="w-full min-w-[1200px] table-fixed border-collapse">
            <thead className="bg-[#dc1f0080] text-white sticky top-0 z-20">
              <tr>
                <th className="py-3 px-2 text-left text-sm font-medium text-white w-10">
                  <input
                    type="checkbox"
                    checked={allVisibleMainShiftsSelected}
                    onChange={(e) =>
                      toggleSelectAllMainShifts(e.target.checked)
                    }
                  />
                </th>
                <th className="py-3 px-2 text-left text-sm font-medium text-white">
                  Shift
                </th>
                <th className="py-3 px-2 text-left text-sm font-medium text-white">
                  Scheduled
                </th>
                <th className="py-3 px-2 text-left text-sm font-medium text-white">
                  Staff Request
                </th>
                <th className="py-3 px-2 text-left text-sm font-medium text-white">
                  Client
                </th>
                <th className="py-3 px-2 text-left text-sm font-medium text-white">
                  Staff
                </th>
                <th className="py-3 px-2 text-left text-sm font-medium text-white">
                  Shift Status
                </th>
                {canReadShiftActual && (
                <th className="py-3 px-2 text-left text-sm font-medium text-white">
                  Actual Time Submission
                </th>
                )}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedMainShiftsData.map((shift) => (
                <tr
                  key={shift.id}
                  className="border-t border-border hover:bg-table-row-hover transition-colors"
                >
                  <td className="py-3 px-2">
                    <input
                      type="checkbox"
                      checked={selectedShiftIds.includes(String(shift.id))}
                      onChange={(e) =>
                        toggleShiftSelection(shift.id, e.target.checked)
                      }
                    />
                  </td>
                  <td
                    className="py-3 px-2 text-red-600 cursor-pointer"
                    onClick={() => openShiftOverview(shift)}
                  >
                    {shift.id}
                  </td>
                  <td className="py-3 px-2">
                    <div className="flex flex-col">
                      <span className="text-foreground">
                        {formatScheduledLine1(shift)}
                      </span>
                      <span className="text-muted-foreground text-xs inline-flex items-center gap-1">
                        {formatDurationAfterBreak(
                          shift?.startDateIso || shift?.start_date || shift?.startDate,
                          shift?.endDateIso || shift?.end_date || shift?.endDate,
                          resolveScheduledBreakMinutes(shift),
                          shift?.scheduledLine2 || "",
                        )}
                        {shiftRequiresNotifyChange(shift) ? (
                          <FaFlag
                            className="text-red-600 shrink-0"
                            size={10}
                            title="Staff requires notification of change."
                            aria-label="Staff requires notification of change"
                          />
                        ) : null}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    <div className="flex flex-col">
                      <span className="text-foreground">
                        {shift.staffRequestLine1 || shift.staffRequest}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {shift.staffRequestLine2}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    <div className="flex flex-col">
                      <span className="text-foreground">
                        {shift.clientLine1 || shift.client}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {shift.clientLine2 || shift.clientLocationName}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    <div className="flex flex-col">
                      <span className="text-foreground">
                        {resolveShiftStaffDisplayName(shift)}
                      </span>
                      {!!String(shift.staffPayrollPayer || "").trim() &&
                        shiftHasRealStaff(shift) && (
                          <span className="text-muted-foreground text-xs">
                            {shift.staffPayrollPayer}
                          </span>
                        )}
                      {(() => {
                        const carpoolName = String(
                          shift.carpool_name ?? shift.carpoolName ?? "",
                        ).trim();
                        const carpoolNote = String(
                          shift.carpool_note ?? shift.carpoolNote ?? "",
                        ).trim();
                        if (!carpoolName && !carpoolNote) return null;
                        const carpoolLine =
                          carpoolName && carpoolNote
                            ? `${carpoolName} • ${carpoolNote}`
                            : carpoolName || carpoolNote;
                        return (
                          <span
                            className="flex items-start gap-1"
                            title={carpoolLine}
                          >
                            <FaCar
                              className="text-red-600 shrink-0 mt-0.5"
                              size={10}
                            />
                            <span className="min-w-0 flex-1 text-left text-xs text-red-600 [overflow-wrap:anywhere] break-all whitespace-normal">
                              {carpoolLine}
                            </span>
                          </span>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    <ShiftStatusCell shift={shift} />
                  </td>
                  {canReadShiftActual && (
                  <td className="py-3 px-2">
                    <ShiftActualTimeSubmissionCell
                      shift={shift}
                      canWrite={canWriteShiftActual}
                      isSubmitting={actualsSubmittingShiftIds.has(
                        String(shift.id),
                      )}
                      onSubmitAsScheduled={() =>
                        submitShiftActuals(shift, {
                          shiftId: shift?.id ?? shift?.shift_id ?? null,
                          actualState: "as_scheduled",
                          actualStartDate:
                            shift?.startDateIso ||
                            shift?.start_date ||
                            shift?.startDate ||
                            shift?.start_time ||
                            null,
                          actualEndDate:
                            shift?.endDateIso ||
                            shift?.end_date ||
                            shift?.endDate ||
                            shift?.end_time ||
                            null,
                          actualUnpaidBreakMinutes:
                            resolveActualBreakMinutes(shift),
                          note: shift?.actualNote || "",
                        })
                      }
                      onSubmitNoShow={() =>
                        submitShiftActuals(shift, {
                          shiftId: shift?.id ?? shift?.shift_id ?? null,
                          actualState: "no_show",
                          actualStartDate:
                            shift?.startDateIso ||
                            shift?.start_date ||
                            shift?.startDate ||
                            shift?.start_time ||
                            null,
                          actualEndDate:
                            shift?.endDateIso ||
                            shift?.end_date ||
                            shift?.endDate ||
                            shift?.end_time ||
                            null,
                          actualUnpaidBreakMinutes:
                            resolveActualBreakMinutes(shift),
                          note: shift?.actualNote || "",
                        })
                      }
                      onOpenDifferent={() =>
                        openShiftActualsModal(shift, "different")
                      }
                      onModifySubmitted={() => openShiftActualsModal(shift)}
                    />
                  </td>
                  )}
                  <td className="py-1 px-1">
                    <ActionDropdown
                      actions={[
                        {
                          label: "Delete",
                          onClick: () => {
                            setConfirmDialog({
                              open: true,
                              title: "Delete Shift",
                              message:
                                "Are you sure you want to delete this shift?",
                              onConfirm: async () => {
                                try {
                                  const token =
                                    localStorage.getItem("token") ||
                                    localStorage.getItem("authToken");
                                  const resp = await fetch(
                                    `${BASE_URL}/api/jobs/shifts/${shift.id}`,
                                    {
                                      method: "DELETE",
                                      headers: token
                                        ? { Authorization: `Bearer ${token}` }
                                        : undefined,
                                    },
                                  );
                                  const data = await resp.json();
                                  if (!resp.ok || !data.success)
                                    throw new Error(
                                      data.message || "Delete failed",
                                    );
                                  setData((prev) =>
                                    prev.filter((r) => r.id !== shift.id),
                                  );
                                  setCurrentPage(1);
                                  setReloadKey((prev) => prev + 1);
                                  showSuccess("Shift deleted");
                                } catch (err) {
                                  showError(err.message || "Delete failed");
                                } finally {
                                  setConfirmDialog({
                                    open: false,
                                    title: "",
                                    message: "",
                                    onConfirm: null,
                                  });
                                }
                              },
                            });
                          },
                          danger: true,
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      case "TIMESHEETS":
        // When no client is selected, show prompt only — no totals, no table data
        if (!filterClient) {
          return (
            <div className="space-y-6">
              <div className="bg-white rounded-lg border border-border p-6">
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <p className="text-muted-foreground text-lg mb-2">
                    Select a client to view timesheets.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Open the filter panel and choose a client to see timesheet data, total hours, and other details.
                  </p>
                </div>
              </div>
            </div>
          );
        }
        return (
          <div className="space-y-6">
            {/* Timesheet Card */}
            <div
              id="printable-timesheet"
              className={`bg-white rounded-lg border border-border p-6 ${
                filterMode === "week" || timesheetPrintOrientation === "landscape"
                  ? "print-landscape-layout print-week-layout"
                  : ""
              }`}
            >
              {/* Schedule Tables - One per day if range is selected */}
              {(() => {
                const selectedLocationIds = new Set(
                  (filterLocations || []).map((id) => String(id).trim()).filter(Boolean),
                );
                const getRowLocationId = (row) =>
                  String(
                    row?.client_location_id ||
                      row?.clientLocationId ||
                      row?.location_id ||
                      row?.locationId ||
                      row?.setting_client_location_id ||
                      "",
                  ).trim();
                const displayRows =
                  selectedLocationIds.size > 0
                    ? (data || []).filter((row) =>
                        selectedLocationIds.has(getRowLocationId(row)),
                      )
                    : data || [];

                // Calculate date range based on start date + mode/range
                const dateRange = [];
                if (filterStartDate) {
                  let rangeDays = 0;
                  if (filterRange && parseInt(filterRange, 10) > 0) {
                    rangeDays = parseInt(filterRange, 10);
                  } else if (filterMode === "week") {
                    rangeDays = 7;
                  } else if (filterMode === "day") {
                    rangeDays = 7;
                  }
                  if (rangeDays > 0) {
                    for (let i = 0; i < rangeDays; i++) {
                      dateRange.push(addAppCalendarDays(filterStartDate, i));
                    }
                  } else {
                    dateRange.push(filterStartDate);
                  }
                }

                // Group timesheets by date
                const timesheetsByDate = new Map();
                displayRows.forEach((timesheet) => {
                  const dateKey = timesheetRowDateKey(
                    timesheet,
                    filterStartDate || "",
                  );

                  if (!timesheetsByDate.has(dateKey)) {
                    timesheetsByDate.set(dateKey, []);
                  }
                  timesheetsByDate.get(dateKey).push(timesheet);
                });

                // Show only dates that have at least one timesheet row.
                const datesWithData = dateRange.filter((dateStr) => {
                  const dateTimesheets = timesheetsByDate.get(dateStr) || [];
                  return dateTimesheets.length > 0;
                });

                const finalDateRange =
                  datesWithData.length > 0
                    ? datesWithData
                    : dateRange.length === 0 && displayRows.length > 0
                      ? Array.from(timesheetsByDate.keys()).sort()
                      : [];

                /* Top print header (logo, client, QR) — only when there are rows to show for current filters */
                const baseDateHdr = filterStartDate || getAppTodayIso();
                const rangeDaysHdr = filterRange
                  ? parseInt(filterRange, 10)
                  : filterMode === "week"
                    ? 7
                    : 7;
                const endDateHdr = addAppCalendarDays(
                  baseDateHdr,
                  rangeDaysHdr - 1,
                );
                let dateRangeLabel;
                if (rangeDaysHdr <= 1 || baseDateHdr === endDateHdr) {
                  dateRangeLabel = formatAppDate(`${baseDateHdr}T12:00:00Z`);
                } else {
                  const startParts = getAppWallDateParts(
                    parseDbDateTime(`${baseDateHdr}T12:00:00Z`),
                  );
                  const endParts = getAppWallDateParts(
                    parseDbDateTime(`${endDateHdr}T12:00:00Z`),
                  );
                  const startShort = formatAppIntlLabel(
                    parseDbDateTime(`${baseDateHdr}T12:00:00Z`),
                    { month: "short", day: "numeric" },
                  );
                  if (
                    startParts &&
                    endParts &&
                    startParts.month === endParts.month &&
                    startParts.year === endParts.year
                  ) {
                    dateRangeLabel = `${startShort} \u2013 ${endParts.day}, ${endParts.year}`;
                  } else {
                    dateRangeLabel = `${startShort} \u2013 ${formatAppDate(`${endDateHdr}T12:00:00Z`)}`;
                  }
                }
                const supKeysHdr = [
                  "supervisor",
                  "shiftSupervisor",
                  "supervisors",
                  "supervisorName",
                ];
                const supervisorsListHdr = (() => {
                  try {
                    const vals = displayRows
                      .map((dd) => {
                        for (const k of supKeysHdr) if (dd?.[k]) return dd[k];
                        return null;
                      })
                      .filter(Boolean);
                    return Array.from(new Set(vals)).join(", ");
                  } catch (_) {
                    return "";
                  }
                })();
                const staffRequestIdsHdr = (() => {
                  try {
                    const ids = displayRows
                      .map((dd) => dd.staff_request_id || dd.staffRequestId)
                      .filter(Boolean);
                    return Array.from(new Set(ids)).join(", ");
                  } catch (_) {
                    return "";
                  }
                })();
                const positionSummaryHdr = (() => {
                  try {
                    const pos = displayRows
                      .map((dd) => dd.position_name || dd.position || dd.role)
                      .filter(Boolean);
                    return Array.from(new Set(pos)).slice(0, 3).join(", ");
                  } catch (_) {
                    return "";
                  }
                })();
                const derivedLocationLabelsHdr = (() => {
                  try {
                    const labels = displayRows
                      .map(
                        (dd) =>
                          dd?.client_location ||
                          dd?.clientLocationName ||
                          dd?.location_name ||
                          dd?.location ||
                          dd?.locationName ||
                          dd?.full_address ||
                          dd?.address ||
                          "",
                      )
                      .map((v) => String(v || "").trim())
                      .filter(Boolean);
                    return Array.from(new Set(labels));
                  } catch (_) {
                    return [];
                  }
                })();
                const locationLabelHdr =
                  derivedLocationLabelsHdr.length > 1
                    ? "Multiple locations (see each day section)"
                    : derivedLocationLabelsHdr[0] ||
                      (filterLocations.length > 0
                        ? filterLocations
                            .map(
                              (id) =>
                                filterLocationList.find((l) => String(l.id) === id)?.name ||
                                `#${id}`,
                            )
                            .join(", ")
                        : "");
                const fallbackSupervisorFromRequestHdr = (() => {
                  const byName = [
                    selectedRequest?.supervisor,
                    selectedRequest?.supervisorName,
                    selectedRequest?.supervisor_name,
                    editableRequest?.supervisor,
                    editableRequest?.supervisorName,
                    editableRequest?.supervisor_name,
                  ]
                    .map((v) => String(v || "").trim())
                    .find(Boolean);
                  if (byName) return byName;

                  const supId = String(
                    editableRequest?.supervisor_contact_account_id ||
                      selectedRequest?.supervisor_contact_account_id ||
                      "",
                  ).trim();
                  if (!supId) return "";

                  const sup = (supervisors || []).find((s) => {
                    const sid = s?.id || s?.account_id || s?.contact_id || s?.contactId;
                    return String(sid || "") === supId;
                  });

                  return (
                    String(
                      sup?.name ||
                        `${sup?.first_name || ""} ${sup?.last_name || ""}`,
                    )
                      .trim() || ""
                  );
                })();
                const supervisorHeaderLabelHdr = (() => {
                  const unique = Array.from(
                    new Set(
                      String(supervisorsListHdr || "")
                        .split(",")
                        .map((v) => v.trim())
                        .filter(Boolean),
                    ),
                  );
                  if (unique.length > 1)
                    return "Multiple supervisors (see each day section)";
                  return unique[0] || fallbackSupervisorFromRequestHdr || "";
                })();

                const timesheetClientAvatarSrc = (() => {
                  const sel = (filterClientList || []).find(
                    (c) => String(c.id) === String(filterClient),
                  );
                  const fromList =
                    sel?.profile_picture && String(sel.profile_picture).trim();
                  if (fromList) return fromList;
                  const fromRow = (displayRows || []).find(
                    (r) =>
                      r?.client_profile_picture &&
                      String(r.client_profile_picture).trim(),
                  );
                  const rowPic = fromRow?.client_profile_picture
                    ? String(fromRow.client_profile_picture).trim()
                    : "";
                  if (rowPic) {
                    if (rowPic.startsWith("http")) return rowPic;
                    if (rowPic.startsWith("/"))
                      return `${String(BASE_URL || "").replace(/\/$/, "")}${rowPic}`;
                    return rowPic;
                  }
                  if (filterClient)
                    return `${BASE_URL}/api/avatars/client/${filterClient}`;
                  return "";
                })();

                const timesheetClientInitialsHdr = (() =>
                  (clientSearch || "CL")
                    .trim()
                    .split(/\s+/)
                    .map((w) => (w[0] || "").toUpperCase())
                    .join("")
                    .substring(0, 2) || "CL")();

                if (loading) {
                  return (
                    <div className="overflow-hidden rounded-lg border mb-6">
                      <div className="px-3 py-4 text-center">Loading…</div>
                    </div>
                  );
                }

                if (filterMode === "week") {
                  const hasFilters =
                    filterStartDate ||
                    filterClient ||
                    filterLocations.length > 0 ||
                    (searchTerm && searchTerm.trim() !== "");

                  const weekDateRange =
                    dateRange.length > 0
                      ? dateRange
                      : displayRows.length > 0
                        ? Array.from(timesheetsByDate.keys()).sort()
                        : [];

                  if (weekDateRange.length === 0) {
                    return (
                      <div className="overflow-hidden rounded-lg border mb-6">
                        <div className="px-3 py-4 text-center text-muted-foreground">
                          {hasFilters
                            ? "No timesheets match the selected filters."
                            : "Please select a filter (date, client, location, or search)."}
                        </div>
                      </div>
                    );
                  }

                  const dataInRange = filterTimesheetsToWeekRange(
                    displayRows,
                    weekDateRange,
                  );
                  let locationGroups = groupTimesheetsByLocation(dataInRange);

                  if (locationGroups.length === 0) {
                    const fallbackLabel =
                      locationLabelHdr &&
                      locationLabelHdr !==
                        "Multiple locations (see each day section)"
                        ? locationLabelHdr
                        : filterLocations.length > 0
                          ? filterLocations
                              .map(
                                (id) =>
                                  filterLocationList.find(
                                    (l) => String(l.id) === id,
                                  )?.name || `#${id}`,
                              )
                              .join(", ")
                          : "Unspecified Location";
                    locationGroups = [
                      {
                        key: "none",
                        label: fallbackLabel,
                        locationId:
                          filterLocations.length === 1 ? filterLocations[0] : "",
                        rows: [],
                      },
                    ];
                  }

                  const endDateIso = addAppCalendarDays(baseDateHdr, rangeDaysHdr - 1);
                  const resolvedClientAvatar =
                    resolveBackendUrl(timesheetClientAvatarSrc) ||
                    timesheetClientAvatarSrc;

                  return (
                    <>
                      {locationGroups.map((locGroup, locIdx) => {
                        const staffRows = buildWeekStaffRows(
                          locGroup.rows,
                          filterStartDate,
                        );
                        const firstRow = locGroup.rows[0];
                        const locPositionTitle = firstRow
                          ? firstRow.position ||
                            firstRow.role ||
                            firstRow.position_name ||
                            positionSummaryHdr
                          : positionSummaryHdr;
                        const locSupervisors =
                          resolveSupervisorsFromRows(locGroup.rows) ||
                          supervisorHeaderLabelHdr;
                        const locStaffRequestIds =
                          resolveStaffRequestIdsFromRows(locGroup.rows) ||
                          staffRequestIdsHdr;

                        return (
                          <TimesheetWeekView
                            key={`week-loc-${locGroup.key}-${locIdx}`}
                            weekDateRange={weekDateRange}
                            staffRows={staffRows}
                            locationLabel={locGroup.label}
                            qrLocationId={locGroup.locationId || ""}
                            clientName={clientSearch || "Client"}
                            clientAvatarSrc={resolvedClientAvatar}
                            clientInitials={timesheetClientInitialsHdr}
                            dateRangeLabel={dateRangeLabel}
                            supervisorsLabel={locSupervisors}
                            staffRequestIds={locStaffRequestIds}
                            positionTitle={locPositionTitle}
                            talentLogoSrc={talentLogo2}
                            filterClient={filterClient}
                            baseDateHdr={baseDateHdr}
                            rangeDaysHdr={rangeDaysHdr}
                            endDateIso={endDateIso}
                            clientSearchName={clientSearch}
                            AvatarSmall={AvatarSmall}
                          />
                        );
                      })}
                    </>
                  );
                }

                if (finalDateRange.length === 0) {
                  return (
                    <div className="overflow-hidden rounded-lg border mb-6">
                      <div className="px-3 py-8 text-center text-muted-foreground">
                        <div className="text-base font-medium mb-1">No Results Found</div>
                        {!filterClient && (
                          <div className="text-sm text-gray-400">Select a client to view timesheets.</div>
                        )}
                      </div>
                    </div>
                  );
                }

                const resolveTimesheetLocationInfo = (timesheet) => {
                  const locationId = String(
                    timesheet?.client_location_id ||
                      timesheet?.location_id ||
                      timesheet?.setting_client_location_id ||
                      timesheet?.locationId ||
                      timesheet?.clientLocationId ||
                      "",
                  ).trim();

                  const locationName = String(
                    timesheet?.client_location ||
                      timesheet?.clientLocationName ||
                      timesheet?.location_name ||
                      timesheet?.location ||
                      timesheet?.locationName ||
                      timesheet?.full_address ||
                      timesheet?.address ||
                      timesheet?.site ||
                      timesheet?.venue ||
                      "",
                  ).trim();

                  // Use id first so same-named locations still split into separate pages.
                  const locationKey = locationId
                    ? `id:${locationId}`
                    : locationName
                      ? `name:${locationName.toLowerCase()}`
                      : "none";

                  return {
                    key: locationKey,
                    label:
                      locationName ||
                      (locationId ? `Location #${locationId}` : "Unspecified Location"),
                    qrLocationId: locationId,
                  };
                };

                const resolveSupervisorsLabel = (rows) => {
                  const values = (rows || [])
                    .map((r) =>
                      r?.supervisor ||
                      r?.supervisors ||
                      r?.supervisorName ||
                      r?.shiftSupervisor ||
                      r?.supervisor_name ||
                      "",
                    )
                    .filter(Boolean)
                    .flatMap((raw) =>
                      Array.isArray(raw)
                        ? raw
                        : String(raw)
                            .split(",")
                            .map((v) => v.trim())
                            .filter(Boolean),
                    );
                  const unique = Array.from(new Set(values));
                  return unique.join(", ");
                };

                const resolveDefaultSupervisorLabel = () => {
                  const byName = [
                    selectedRequest?.supervisor,
                    selectedRequest?.supervisorName,
                    selectedRequest?.supervisor_name,
                    editableRequest?.supervisor,
                    editableRequest?.supervisorName,
                    editableRequest?.supervisor_name,
                  ]
                    .map((v) => String(v || "").trim())
                    .find(Boolean);
                  if (byName) return byName;

                  const supId = String(
                    editableRequest?.supervisor_contact_account_id ||
                      selectedRequest?.supervisor_contact_account_id ||
                      "",
                  ).trim();
                  if (!supId) return "";

                  const sup = (supervisors || []).find((s) => {
                    const sid = s?.id || s?.account_id || s?.contact_id || s?.contactId;
                    return String(sid || "") === supId;
                  });

                  return (
                    String(
                      sup?.name ||
                        `${sup?.first_name || ""} ${sup?.last_name || ""}`,
                    )
                      .trim() ||
                    ""
                  );
                };

                const dateLocationPages = [];
                const hasAnyTimesheets = Array.from(timesheetsByDate.values()).some(
                  (rows) => (rows || []).length > 0,
                );

                if (!hasAnyTimesheets) {
                  finalDateRange.forEach((dateStr, dateIdx) => {
                    dateLocationPages.push({
                      dateStr,
                      dateIdx,
                      locationLabel: "Unspecified Location",
                      qrLocationId:
                        filterLocations.length === 1 ? String(filterLocations[0]) : "",
                      supervisorsLabel: "",
                      timesheets: [],
                    });
                  });
                } else {
                  const orderedLocations = [];
                  const locationGroups = new Map();

                  finalDateRange.forEach((dateStr) => {
                    const dateTimesheets = timesheetsByDate.get(dateStr) || [];
                    dateTimesheets.forEach((ts) => {
                      const loc = resolveTimesheetLocationInfo(ts);
                      if (!locationGroups.has(loc.key)) {
                        locationGroups.set(loc.key, {
                          key: loc.key,
                          label: loc.label,
                          qrLocationId: loc.qrLocationId,
                          byDate: new Map(),
                        });
                        orderedLocations.push(loc.key);
                      }
                      const group = locationGroups.get(loc.key);
                      if (!group.byDate.has(dateStr)) {
                        group.byDate.set(dateStr, []);
                      }
                      group.byDate.get(dateStr).push(ts);
                    });
                  });

                  orderedLocations.forEach((locationKey) => {
                    const group = locationGroups.get(locationKey);
                    finalDateRange.forEach((dateStr, dateIdx) => {
                      const rows = group?.byDate?.get(dateStr) || [];
                      if (rows.length === 0) return;

                      dateLocationPages.push({
                        dateStr,
                        dateIdx,
                        locationLabel: group.label,
                        qrLocationId: group.qrLocationId,
                        supervisorsLabel: resolveSupervisorsLabel(rows),
                        timesheets: rows,
                      });
                    });
                  });
                }

                // Group pages by location
                const pagesByLocation = new Map();
                dateLocationPages.forEach((page) => {
                  if (!pagesByLocation.has(page.locationLabel)) {
                    pagesByLocation.set(page.locationLabel, []);
                  }
                  pagesByLocation.get(page.locationLabel).push(page);
                });

                return (
                  <>
                    {Array.from(pagesByLocation.entries()).map(
                  ([locationLabel, locationPages], locIdx) => {
                    // Get first page's timesheets for location header
                    const locationTimesheets = locationPages.flatMap((p) => p.timesheets);
                    const firstTimesheet = locationTimesheets[0] || {};
                    const locationId =
                      firstTimesheet.client_location_id ||
                      firstTimesheet.clientLocationId ||
                      firstTimesheet.location_id ||
                      firstTimesheet.locationId ||
                      "";
                    const positionTitle =
                      firstTimesheet.position ||
                      firstTimesheet.role ||
                      firstTimesheet.position_name ||
                      selectedRequest?.job_title ||
                      editableRequest?.job_title ||
                      "Position";
                    
                    // Extract supervisors for this location with better fallback
                    let supervisorsForLocation = "";
                    
                    // First try to get from timesheet rows
                    const tsSupvisors = Array.from(
                      new Set(
                        locationTimesheets
                          .map(
                            (ts) =>
                              ts?.supervisor ||
                              ts?.supervisors ||
                              ts?.supervisorName ||
                              ts?.supervisor_name ||
                              "",
                          )
                          .filter(Boolean),
                      ),
                    ).join(", ");
                    
                    if (tsSupvisors) {
                      supervisorsForLocation = tsSupvisors;
                    } else {
                      // Fallback: get from request details
                      const fromRequest = [
                        selectedRequest?.supervisor,
                        selectedRequest?.supervisorName,
                        selectedRequest?.supervisor_name,
                        editableRequest?.supervisor,
                        editableRequest?.supervisorName,
                        editableRequest?.supervisor_name,
                      ]
                        .map((v) => String(v || "").trim())
                        .find(Boolean);
                      
                      if (fromRequest) {
                        supervisorsForLocation = fromRequest;
                      } else {
                        // Try ID lookup
                        const supId = String(
                          editableRequest?.supervisor_contact_account_id ||
                            selectedRequest?.supervisor_contact_account_id ||
                            "",
                        ).trim();
                        if (supId) {
                          const sup = (supervisors || []).find((s) => {
                            const sid = s?.id || s?.account_id || s?.contact_id || s?.contactId;
                            return String(sid || "") === supId;
                          });
                          if (sup) {
                            supervisorsForLocation =
                              sup?.name || `${sup?.first_name || ""} ${sup?.last_name || ""}`;
                          }
                        }
                      }
                    }
                    
                    const staffRequestIdsForLocation = Array.from(
                      new Set(
                        locationTimesheets
                          .map((ts) => ts?.staff_request_id || "")
                          .filter(Boolean),
                      ),
                    ).join(", ");

                    return (
                      <div key={`location-group-${locIdx}`} className="mb-8">
                        {/* Location Header Bar */}
                        <div className="flex items-start justify-between gap-4 rounded-lg border p-4 mb-4 bg-gray-50">
                          {/* Left section - Location info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start gap-4">
                              <div className="flex-shrink-0">
                                <img
                                  src={talentLogo2}
                                  alt="Talent Logo"
                                  className="h-10 w-auto"
                                />
                              </div>

                              <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-[#DC1F00] text-white font-semibold text-lg overflow-hidden border border-gray-200">
                                {timesheetClientAvatarSrc ? (
                                  <>
                                    <img
                                      src={timesheetClientAvatarSrc}
                                      alt=""
                                      className="h-full w-full object-cover"
                                      onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                        const fb = e.currentTarget.nextElementSibling;
                                        if (fb) fb.style.display = "flex";
                                      }}
                                    />
                                    <span
                                      className="flex h-full w-full items-center justify-center"
                                      style={{ display: "none" }}
                                    >
                                      {timesheetClientInitialsHdr}
                                    </span>
                                  </>
                                ) : (
                                  <span>{timesheetClientInitialsHdr}</span>
                                )}
                              </div>

                              <div className="min-w-0">
                                <div className="font-bold text-gray-900 text-lg">
                                  {clientSearch || "Client"}
                                </div>
                                <div className="text-gray-700 text-sm mt-1">
                                  {locationLabel}
                                </div>
                                <div className="text-gray-600 text-xs mt-2">
                                  Supervisor(s):{" "}
                                  <span className="font-medium">
                                    {supervisorsForLocation || "\u2014"}
                                  </span>
                                </div>
                                {staffRequestIdsForLocation && (
                                  <div className="text-gray-600 text-xs mt-1">
                                    Staff Request:{" "}
                                    <span className="font-medium">
                                      {staffRequestIdsForLocation}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Date pages for this location */}
                        {locationPages.map((page, pageIdx) => {
                          const slots = groupTimesheetsIntoSlots(page.timesheets);
                          const dateLabel = formatAppIntlLabel(
                            parseDbDateTime(`${page.dateStr}T12:00:00Z`),
                            { weekday: "long", month: "long", day: "numeric" },
                          );
                          const effectiveSupervisorLabel =
                            page.supervisorsLabel || resolveDefaultSupervisorLabel();

                          return (
                            <div
                              key={`date-location-${page.dateStr}-${page.locationLabel}-${pageIdx}`}
                              className="print-date-page overflow-hidden rounded-lg border mb-6"
                            >
                      {/* Table Header - Red bar (date + total scheduled + QR for this day) */}
                      <div className="print-timesheet-date-bar flex items-center justify-between gap-2 bg-[#DC1F00] px-4 py-3 text-white">
                        <div className="flex flex-col min-w-0">
                          <span className="text-xl font-bold truncate">{dateLabel}</span>
                          <span className="text-xs truncate">{page.locationLabel}</span>
                          {effectiveSupervisorLabel ? (
                            <span className="text-xs truncate">Supervisor(s): {effectiveSupervisorLabel}</span>
                          ) : null}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className="text-sm whitespace-nowrap">
                            {page.timesheets
                              .reduce(
                                (sum, r) => sum + (parseFloat(r.duration) || 0),
                                0,
                              )
                              .toFixed(1)}
                            h total scheduled
                          </span>
                          <a
                            href={(() => {
                              const params = new URLSearchParams();
                              if (filterClient) params.set("clientId", String(filterClient));
                              if (page.dateStr) {
                                params.set("shiftDate", page.dateStr);
                                params.set("from", page.dateStr);
                                params.set("to", page.dateStr);
                              }
                              if (clientSearch) params.set("client", clientSearch);
                              if (page.locationLabel) params.set("location", page.locationLabel);
                              if (page.qrLocationId) params.set("locationId", String(page.qrLocationId));
                              params.set("forceLogin", "1");
                              return `/checkin${params.toString() ? `?${params.toString()}` : ""}`;
                            })()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-shrink-0 h-[7.5rem] w-[7.5rem] bg-white rounded border border-white/30 flex items-center justify-center p-2 hover:border-white transition-colors"
                            aria-label="QR Check-in"
                          >
                            <QRCodeSVG
                              value={(() => {
                                const params = new URLSearchParams();
                                if (filterClient) params.set("clientId", String(filterClient));
                                if (page.dateStr) {
                                  params.set("shiftDate", page.dateStr);
                                  params.set("from", page.dateStr);
                                  params.set("to", page.dateStr);
                                }
                                if (clientSearch) params.set("client", clientSearch);
                                if (page.locationLabel) params.set("location", page.locationLabel);
                                if (page.qrLocationId) params.set("locationId", String(page.qrLocationId));
                                params.set("forceLogin", "1");
                                return `${typeof window !== "undefined" ? window.location.origin : ""}/checkin${params.toString() ? `?${params.toString()}` : ""}`;
                              })()}
                              size={102}
                              level="M"
                            />
                          </a>
                        </div>
                      </div>

                      <div className="print-date-page-main">
                      {/* Table - header row removed per design */}
                      <table className="w-full">
                        <tbody>
                          {slots.length === 0 ? (
                            <tr>
                              <td
                                colSpan={7}
                                className="px-3 py-4 text-center text-muted-foreground"
                              >
                                No timesheets for this date.
                              </td>
                            </tr>
                          ) : (
                            slots.map((slot, si) => {
                              const timeLabel =
                                slot.time ||
                                `${formatTimeRange(slot.start_time, slot.end_time)}`;
                              const durationLabel =
                                typeof slot.hours !== "undefined"
                                  ? formatDuration(slot.hours)
                                  : slot.duration || "";

                              const positions = slot.positions || [];

                              return (
                                <React.Fragment key={`slot-${pageIdx}-${si}`}>
                                  <tr className="bg-gray-200 text-xs text-gray-600">
                                    <td className="px-3 py-2 border font-normal">
                                      {timeLabel}
                                    </td>
                                    <td className="px-3 py-2 border font-normal">
                                      {durationLabel}
                                    </td>
                                    <td className="px-3 py-2 border font-normal">
                                      Check-in
                                    </td>
                                    <td className="px-3 py-2 border font-normal">
                                      Break (min)
                                    </td>
                                    <td className="px-3 py-2 border font-normal">
                                      Check-out
                                    </td>
                                    <td className="px-3 py-2 border font-normal">
                                      Total
                                    </td>
                                    <td className="px-3 py-2 border font-normal">
                                      Signature
                                    </td>
                                  </tr>
                                  {positions.map((p, pi) => {
                                    const printCheckin = formatTime(p.checkin_time) || "";
                                    const printCheckout = formatTime(p.checkout_time) || "";
                                    const bm = p.break_minutes;
                                    const printBreak =
                                      bm != null && bm !== "" && !Number.isNaN(Number(bm))
                                        ? String(Math.round(Number(bm)))
                                        : "";
                                    let printTotal = "";
                                    if (
                                      p.total_after_break != null &&
                                      p.total_after_break !== ""
                                    ) {
                                      const n =
                                        typeof p.total_after_break === "number"
                                          ? p.total_after_break
                                          : parseFloat(
                                              String(p.total_after_break).replace(
                                                /[^0-9.-]/g,
                                                "",
                                              ),
                                            );
                                      if (!Number.isNaN(n) && n >= 0) {
                                        printTotal = formatDuration(n);
                                      }
                                    }
                                    const printCellCls = (text) =>
                                      `px-3 py-2 border text-sm text-center${
                                        !text || !String(text).trim()
                                          ? " print-empty-field"
                                          : ""
                                      }`;

                                    return (
                                    <tr
                                      key={`pos-${pageIdx}-${si}-${pi}`}
                                      className="border-t"
                                    >
                                      <td className="px-3 py-2 border text-foreground font-semibold">
                                        {p.role}
                                      </td>
                                      <td className="px-3 py-2 border text-foreground font-semibold flex items-center">
                                        {(
                                          p.temp ||
                                          p.staff ||
                                          ""
                                        ).toLowerCase() !== "unassigned" &&
                                        (p.temp || p.staff) ? (
                                          <>
                                            <AvatarSmall
                                              name={p.temp || p.staff}
                                              src={
                                                p.avatar_url ||
                                                p.profile_picture
                                              }
                                            />
                                            <span className="text-foreground">
                                              {p.temp || p.staff}
                                            </span>
                                          </>
                                        ) : (
                                          <>
                                            <PlaceholderAvatar />
                                            <span className="text-muted-foreground">
                                              Unassigned
                                            </span>
                                          </>
                                        )}
                                      </td>
                                      <td className={printCellCls(printCheckin)}>
                                        {printCheckin || "\u00a0"}
                                      </td>
                                      <td className={printCellCls(printBreak)}>
                                        {printBreak || "\u00a0"}
                                      </td>
                                      <td className={printCellCls(printCheckout)}>
                                        {printCheckout || "\u00a0"}
                                      </td>
                                      <td className={printCellCls(printTotal)}>
                                        {printTotal || "\u00a0"}
                                      </td>
                                      <td className="px-3 py-2 border print-empty-field"><>&nbsp;</></td>
                                    </tr>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            })
                          )}
                        </tbody>
                      </table>

                      {/* Signature block on every printed page (inside each day section) */}
                      <div className="grid grid-cols-3 gap-6 mt-6 pt-4">
                        <div className="flex items-center text-sm text-gray-700">
                          <span className="whitespace-nowrap">Client Signature</span>
                          <div className="flex-1 border-b border-gray-700 ml-2"></div>
                        </div>
                        <div className="flex items-center text-sm text-gray-700">
                          <span className="whitespace-nowrap">Client Printed Name</span>
                          <div className="flex-1 border-b border-gray-700 ml-2"></div>
                        </div>
                        <div className="flex items-center text-sm text-gray-700">
                          <span className="whitespace-nowrap">Total Hours</span>
                          <div className="flex-1 border-b border-gray-700 ml-2"></div>
                        </div>
                      </div>
                      </div>
                    </div>
                          );
                        })}
                      </div>
                    );
                  },
                )}
                  </>
                );
              })()}
            </div>
          </div>
        );

      case "SHIFTASSIGNMENT":
        return (
          <table className="w-full min-w-[1100px] table-fixed border-collapse">
            <thead className="bg-[#dc1f0080] text-white sticky top-0 z-20">
              <tr>
                <th className="py-3 px-2 text-left text-sm font-medium text-white">
                  Assignment
                </th>
                <th className="py-3 px-2 text-left text-sm font-medium text-white">
                  Shift
                </th>
                <th className="py-3 px-2 text-left text-sm font-medium text-white">
                  Staff
                </th>
                <th className="py-3 px-2 text-left text-sm font-medium text-white">
                  Status
                </th>
                <th className="py-3 px-2 text-left text-sm font-medium text-white">
                  Affects Reliability
                </th>
                <th className="py-3 px-2 text-left text-sm font-medium text-white">
                  Last Modified Proximity
                </th>
                <th className="py-3 px-2 text-left text-sm font-medium text-white">
                  Last Modified
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.map((assign) => {
                const statusValue = String(assign.status || "").trim();
                const reasonLabels = {
                  mia: "MIA/No Show",
                  sick: "Sick",
                  other: "Other",
                };
                // The backend returns either "Currently Assigned" or "Unassigned".
                // Mirror the legacy 4-status contract exactly:
                //   "Currently Assigned"  - active assignment, no sub-line
                //   "MIA/No Show" / "Sick" / "Other"  - unassigned with a reason
                const displayStatus = (() => {
                  if (statusValue === "Currently Assigned") return "Currently Assigned";
                  if (assign.statusReason && reasonLabels[assign.statusReason]) {
                    return reasonLabels[assign.statusReason];
                  }
                  return "Currently Assigned";
                })();

                return (
                <tr
                  key={assign.assignment}
                  className="border-t border-border hover:bg-table-row-hover transition-colors"
                >
                  <td className="py-3 px-2">
                    <span
                      className="text-red-600 cursor-pointer font-medium"
                      onClick={() => {
                        openShiftAssignmentSidePanel(assign);
                      }}
                    >
                      {assign.assignment}
                    </span>
                  </td>
                  <td className="py-3 px-2">
                    <div className="flex flex-col">
                      <span className="text-foreground">
                        {assign.shiftLine1}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {assign.shiftLine2}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    <div className="flex flex-col">
                      <span className="text-foreground">{assign.staff}</span>
                      {assign.staffPayerName && (
                        <span className="text-muted-foreground text-xs">
                          {assign.staffPayerName}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    <div className="flex min-w-0 flex-col">
                      <span
                        className="text-sm text-foreground"
                        title={displayStatus}
                      >
                        {displayStatus}
                      </span>
                      {assign.statusReason && (
                        <span className="text-xs text-muted-foreground">
                          {assign.within24Hours ? "<24 hour notice" : ">24 hour notice"}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        displayStatus === "Currently Assigned" ||
                        assign.affectsReliability
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {displayStatus === "Currently Assigned" ||
                      assign.affectsReliability
                        ? "Yes"
                        : "No"}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-sm text-muted-foreground">
                    {assign.lastModifiedProximity}
                  </td>
                  <td className="py-3 px-2">
                    <div className="flex flex-col">
                      <span className="text-foreground text-sm">
                        {assign.lastModifiedBy || "System"}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {formatAppRelativeShortAgo(assign.lastModified)}
                      </span>
                    </div>
                  </td>
                  <td className="py-1 px-1">
                    <ActionDropdown
                      actions={[
                        { label: "View Related", disabled: true },
                        {
                          label: "Shift",
                          isSubOption: true,
                          onClick: () => openShiftDetailModalForAssignment(assign),
                        },
                        {
                          label: "Client",
                          isSubOption: true,
                          onClick: () => openClientModalForAssignment(assign),
                        },
                        {
                          label: "Staff",
                          isSubOption: true,
                          onClick: () => openStaffModalForAssignment(assign),
                        },
                        {
                          label: "View in Schedule",
                          onClick: () => openViewInScheduleForAssignment(assign),
                        },
                        {
                          label: "Unassign",
                          onClick: () => openUnassignModalForAssignment(assign),
                          danger: true,
                        },
                      ]}
                    />
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        );
      default:
        return null;
    }
  };

  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [client, setClient] = useState([]);
  // const [clientSearch, setClientSearch] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [actualsSubmittingShiftIds, setActualsSubmittingShiftIds] = useState(
    () => new Set(),
  );
  const [showShiftActualsModal, setShowShiftActualsModal] = useState(false);
  const [selectedActualsShift, setSelectedActualsShift] = useState(null);
  const [actualsModalSubmitting, setActualsModalSubmitting] = useState(false);
  const [actualsForm, setActualsForm] = useState({
    actualState: "as_scheduled",
    actualStartDate: "",
    actualEndDate: "",
    actualUnpaidBreakMinutes: 0,
    note: "",
  });

  // ✅ Staffing filter location dropdown uses `locations` / `locationSearch` (per tab via clientLocByTab)

  // Helper for cleaning input values before sending to backend
  const clean = (v) => (v && !isNaN(Number(v)) ? Number(v) : null);

  const normalizeActualState = (raw) =>
    String(raw || "")
      .trim()
      .toLowerCase()
      .replace(/[\s_]+/g, "_");

  const actualStateLabel = (raw) => {
    const state = normalizeActualState(raw);
    if (!isSubmittedActualState(state)) return "Not Submitted";
    if (state === "as_scheduled" || state === "asscheduled")
      return "As Scheduled";
    if (state === "different") return "Different";
    if (state === "no_show" || state === "noshow") return "No Show";
    return "Not Submitted";
  };

  const mergeUpdatedShiftActuals = (baseShift, updatedShift, payload) =>
    mergeShiftRowAfterActualsSubmit(baseShift, updatedShift, payload);

  const updateShiftRowActuals = (shiftId, updatedShift, payload) => {
    const applyMerge = (row) => {
      if (String(row?.id ?? row?.shift_id ?? "") !== String(shiftId)) return row;
      return mergeUpdatedShiftActuals(row, updatedShift, payload);
    };

    setData((prev) =>
      Array.isArray(prev) ? prev.map(applyMerge) : prev,
    );

    const cache = shiftsPageCacheRef.current;
    if (cache?.ok && Array.isArray(cache.data)) {
      shiftsPageCacheRef.current = {
        ...cache,
        data: cache.data.map(applyMerge),
      };
    }
  };

  const openShiftActualsModal = (shift, initialStateOverride = null) => {
    if (!shift || !canWriteShiftActual) return;
    const actualStateNorm = normalizeActualState(
      shift.actualState ||
        shift.actual_state ||
        shift.chosenActualState ||
        shift.actual_time_status ||
        "",
    );
    const submitted = isSubmittedActualState(
      shift.actualState ||
        shift.actual_state ||
        shift.chosenActualState ||
        shift.actual_time_status ||
        "",
    );
    const initialState = initialStateOverride
      ? normalizeActualState(initialStateOverride)
      : submitted
        ? actualStateNorm
        : "as_scheduled";

    setSelectedActualsShift(shift);
    setActualsForm({
      actualState: initialState,
      actualStartDate: toDatetimeLocalValue(
        shift.actualStartDate ||
          shift.actualStartDateIso ||
          shift.actual_start_date ||
          shift.checkinDateIso ||
          shift.checkin_date ||
          shift.startDateIso ||
          shift.start_date ||
          shift.startDate,
      ),
      actualEndDate: toDatetimeLocalValue(
        shift.actualEndDate ||
          shift.actualEndDateIso ||
          shift.actual_end_date ||
          shift.checkoutDateIso ||
          shift.checkout_date ||
          shift.endDateIso ||
          shift.end_date ||
          shift.endDate,
      ),
      actualUnpaidBreakMinutes:
        resolveActualBreakMinutes(shift),
      note: shift.actualNote || shift.note || "",
    });
    setShowShiftActualsModal(true);
  };

  const closeShiftActualsModal = () => {
    if (actualsModalSubmitting) return;
    setShowShiftActualsModal(false);
    setSelectedActualsShift(null);
  };

  useEffect(() => {
    if (!canWriteShiftActual && showShiftActualsModal) {
      setShowShiftActualsModal(false);
      setSelectedActualsShift(null);
    }
  }, [canWriteShiftActual, showShiftActualsModal]);

  const handleJobsActualsFormChange = (field, value) => {
    setActualsForm((prev) => {
      if (
        field === "actualEndDate" &&
        prev.actualStartDate &&
        value &&
        value < prev.actualStartDate
      ) {
        return { ...prev, actualEndDate: prev.actualStartDate };
      }
      return { ...prev, [field]: value };
    });
  };

  const submitShiftActuals = async (shift, payloadOverride = null) => {
    if (!canWriteShiftActual) return false;
    const shiftId = shift?.id ?? shift?.shift_id ?? null;
    if (!shiftId) return false;

    setActualsSubmittingShiftIds((prev) => {
      const next = new Set(prev);
      next.add(String(shiftId));
      return next;
    });

    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      const body = payloadOverride || {
        shiftId,
        actualState: "as_scheduled",
        actualStartDate:
          shift?.startDateIso ||
          shift?.start_date ||
          shift?.startDate ||
          shift?.start_time ||
          null,
        actualEndDate:
          shift?.endDateIso ||
          shift?.end_date ||
          shift?.endDate ||
          shift?.end_time ||
          null,
        actualUnpaidBreakMinutes:
          resolveActualBreakMinutes(shift),
        note: shift?.actualNote || "",
      };

      const resp = await fetch(
        `${BASE_URL}/api/jobs/shifts/${shiftId}/actuals`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(body),
        },
      );
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json?.success) {
        throw new Error(json?.message || "Failed to submit actuals");
      }

      const updatedShift = json?.data?.shift || null;
      updateShiftRowActuals(shiftId, updatedShift, body);

      showSuccess(`Actuals submitted: ${actualStateLabel(body.actualState)}`);
      return true;
    } catch (err) {
      console.error("Submit actuals failed:", err);
      showError(err?.message || "Failed to submit actuals");
      return false;
    } finally {
      setActualsSubmittingShiftIds((prev) => {
        const next = new Set(prev);
        next.delete(String(shiftId));
        return next;
      });
    }
  };

  const handleSubmitShiftActualsModal = async () => {
    if (!selectedActualsShift || actualsModalSubmitting) return;

    const shiftId =
      selectedActualsShift?.id ?? selectedActualsShift?.shift_id ?? null;
    if (!shiftId) return;

    const payload = {
      shiftId,
      actualState: actualsForm.actualState,
      actualStartDate:
        actualsForm.actualState === "different"
          ? toMysqlUtcWallString(fromDatetimeLocalValue(actualsForm.actualStartDate))
          : selectedActualsShift?.startDateIso ||
            selectedActualsShift?.start_date ||
            selectedActualsShift?.startDate ||
            selectedActualsShift?.start_time ||
            null,
      actualEndDate:
        actualsForm.actualState === "different"
          ? toMysqlUtcWallString(fromDatetimeLocalValue(actualsForm.actualEndDate))
          : selectedActualsShift?.endDateIso ||
            selectedActualsShift?.end_date ||
            selectedActualsShift?.endDate ||
            selectedActualsShift?.end_time ||
            null,
      actualUnpaidBreakMinutes:
        actualsForm.actualState === "different"
          ? (actualsForm.actualUnpaidBreakMinutes === "" ||
             actualsForm.actualUnpaidBreakMinutes === null ||
             actualsForm.actualUnpaidBreakMinutes === undefined
              ? 0
              : Math.max(0, Number(actualsForm.actualUnpaidBreakMinutes) || 0))
          : resolveActualBreakMinutes(selectedActualsShift),
      note: actualsForm.note || "",
    };

    if (
      actualsForm.actualState === "different" &&
      (!payload.actualStartDate || !payload.actualEndDate)
    ) {
      showError("Select actual start and end times");
      return;
    }
    if (actualsForm.actualState === "different") {
      if (actualsForm.actualEndDate < actualsForm.actualStartDate) {
        showError("Actual end cannot be before actual start");
        return;
      }
    }

    setActualsModalSubmitting(true);
    try {
      const success = await submitShiftActuals(selectedActualsShift, payload);
      if (!success) return;
      setSelectedActualsShift((prev) =>
        prev ? mergeUpdatedShiftActuals(prev, null, payload) : prev,
      );
      setShowShiftActualsModal(false);
      setSelectedActualsShift(null);
    } finally {
      setActualsModalSubmitting(false);
    }
  };

  const selectedActualsStateNorm = normalizeActualState(
    selectedActualsShift?.actualState ||
      selectedActualsShift?.actual_state ||
      selectedActualsShift?.chosenActualState ||
      "",
  );
  const selectedActualsSubmitted = isSubmittedActualState(
    selectedActualsShift?.actualState ||
      selectedActualsShift?.actual_state ||
      selectedActualsShift?.chosenActualState ||
      "",
  );
  const selectedTimesheetReviewRequired =
    selectedActualsShift?.timesheet_review_required === 1 ||
    selectedActualsShift?.timesheet_review_required === true;
  const selectedSignoffNorm = normalizeActualState(
    selectedActualsShift?.signoff_state || selectedActualsShift?.signoffState || "",
  );
  const selectedActualsPendingReview =
    selectedTimesheetReviewRequired &&
    selectedActualsSubmitted &&
    (selectedSignoffNorm === "pending_signoff_review" ||
      selectedSignoffNorm === "pendingsignoffreview");
  const selectedActualsModalTitle = selectedActualsSubmitted
    ? "Modify Shift Actuals"
    : "Submit Shift Actuals";
  const refreshOpenShiftAssignmentPanel = async () => {
    const sid =
      shiftAssignmentPanelShift?.shift_id ??
      shiftAssignmentPanelShift?.id ??
      shiftAssignmentPanelEntry?.shift_id ??
      shiftAssignmentPanelEntry?.id ??
      null;
    if (!showShiftAssignmentPanel || !sid) return;
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      const res = await fetch(`${BASE_URL}/api/jobs/shifts/${sid}`, {
        ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) return;
      const fresh =
        json?.shift || json?.data || json?.result || json || { id: sid };
      setShiftAssignmentPanelShift(fresh);
      setShiftAssignmentPanelEntry((prev) =>
        prev
          ? {
              ...prev,
              ...fresh,
              rawShift: fresh,
              note: fresh.note ?? fresh.note_to_staff ?? prev.note,
              admin_note: fresh.admin_note ?? prev.admin_note,
            }
          : prev,
      );
    } catch (err) {
      console.error("Failed to refresh assignment panel notes", err);
    }
  };

  const openShiftAssignmentSidePanel = async (assign) => {
    try {
      const shiftId =
        assign?.shiftId ?? assign?.shift_id ?? assign?.shift ?? assign?.id ?? null;
      if (!shiftId) {
        showError("Unable to open assignment (missing shift id)");
        return;
      }

      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      const res = await fetch(`${BASE_URL}/api/jobs/shifts/${shiftId}`, {
        ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.message || "Failed to load shift details";
        showError(msg);
        return;
      }

      const shift =
        json?.shift || json?.data || json?.result || json || { id: shiftId };

      const entry = {
        ...shift,
        rawShift: shift,
        id: shift.shift_id || shift.id || shiftId,
        shift_id: shift.shift_id || shift.id || shiftId,
        // For Shift Assignment side panel header, show Client name (match requested behavior).
        // Fall back to location name when client is missing.
        location:
          shift.client_name ||
          shift.clientName ||
          shift.client ||
          shift.location ||
          shift.location_name ||
          "",
        position:
          shift.position ||
          shift.position_name ||
          shift.positionName ||
          "",
        isClosedWeek: Boolean(shift.isClosedWeek),
      };

      const employeeId =
        assign?.assignedAccountId ||
        assign?.assigned_staff_id ||
        assign?.account_id ||
        null;
      const employeeName =
        assign?.staff ||
        `${assign?.staffFirstName || ""} ${assign?.staffLastName || ""}`.trim();
      const employee =
        employeeId || employeeName
          ? {
              account_id: employeeId ? Number(employeeId) : undefined,
              first_name: assign?.staffFirstName || undefined,
              last_name: assign?.staffLastName || undefined,
              name: employeeName || undefined,
            }
          : null;

      setShiftAssignmentPanelShift(shift);
      setShiftAssignmentPanelEntry(entry);
      setShiftAssignmentPanelAssignment({
        ...assign,
        shift_id: shiftId,
        rawShift: shift,
        status: assign?.status || assign?.state || assign?.rawState || "",
        employee: employee || undefined,
        account_id: employeeId || undefined,
      });
      setShowShiftAssignmentPanel(true);
    } catch (err) {
      console.error("Failed to open assignment panel:", err);
      showError("Failed to open assignment panel");
    }
  };

  const openShiftDetailModalForAssignment = (assign) => {
    const shiftId = assign?.shiftId ?? assign?.shift_id ?? null;
    if (!shiftId) {
      showError("Missing shift id");
      return;
    }
    setStandaloneDuplicateShiftId(null);
    setSelectedShift({ id: shiftId });
  };

  const openClientModalForAssignment = async (assign) => {
    try {
      const clientId = assign?.clientId ?? assign?.client_id ?? null;
      const clientName = assign?.clientName ?? assign?.client_name ?? null;
      if (clientId) {
        setSelectedClientForModal({ id: clientId, name: clientName || "" });
        setShowClientModal(true);
        return;
      }

      // Fallback: load shift details and infer client id if present.
      const shiftId = assign?.shiftId ?? assign?.shift_id ?? null;
      if (!shiftId) {
        showError("Missing client and shift info");
        return;
      }
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      const res = await fetch(`${BASE_URL}/api/jobs/shifts/${shiftId}`, {
        ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
      });
      const json = await res.json().catch(() => ({}));
      const shift = json?.shift || json?.data || json || {};
      const inferredClientId =
        shift?.client_id || shift?.clientId || shift?.staff_request_client_id || null;
      if (!inferredClientId) {
        showError("Unable to determine client for this shift");
        return;
      }
      setSelectedClientForModal({
        id: inferredClientId,
        name: shift?.client_name || shift?.clientName || "",
      });
      setShowClientModal(true);
    } catch (err) {
      console.error("Failed to open client modal:", err);
      showError("Failed to open client");
    }
  };

  const openStaffModalForAssignment = (assign) => {
    const accountId =
      assign?.assignedAccountId ??
      assign?.assigned_staff_id ??
      assign?.account_id ??
      null;
    if (!accountId) {
      showError("No staff assigned to this assignment");
      return;
    }
    setSelectedPersonnel({
      account_id: Number(accountId),
      first_name: assign?.staffFirstName || undefined,
      last_name: assign?.staffLastName || undefined,
      name:
        assign?.staff ||
        `${assign?.staffFirstName || ""} ${assign?.staffLastName || ""}`.trim(),
      type: "fieldstaff",
      staffType: "fieldstaff",
    });
    setShowPersonnelModal(true);
  };

  const openViewInScheduleForAssignment = (assign) => {
    const shiftId = assign?.shiftId ?? assign?.shift_id ?? null;
    const date =
      assign?.shiftStartDate ||
      assign?.shift_start_date ||
      assign?.start_date ||
      "";
    if (!shiftId || !date) {
      showError("Unable to view in Schedule (missing shift id/date)");
      return;
    }
    navigate(
      `/schedule?shiftId=${encodeURIComponent(
        String(shiftId),
      )}&date=${encodeURIComponent(String(date).slice(0, 10))}`,
    );
  };

  const openUnassignModalForAssignment = (assign) => {
    const shiftId = assign?.shiftId ?? assign?.shift_id ?? null;
    const accountId =
      assign?.assignedAccountId ??
      assign?.assigned_staff_id ??
      assign?.account_id ??
      null;
    const staffName =
      assign?.staff ||
      `${assign?.staffFirstName || ""} ${assign?.staffLastName || ""}`.trim();
    if (!shiftId || !accountId) {
      showError("Missing shift/staff info to unassign");
      return;
    }
    setUnassignModal((prev) => ({
      ...prev,
      open: true,
      shiftId: Number(shiftId),
      accountId: Number(accountId),
      staffName: staffName || "",
      countTowardsReliability: true,
      reason: "Other",
      notice24h: "no",
      availability: "available",
      note: "",
      submitting: false,
    }));
  };

  const submitForceUnassignFromShiftAssignment = async () => {
    if (!unassignModal.open || !unassignModal.shiftId || !unassignModal.accountId)
      return;
    setUnassignModal((prev) => ({ ...prev, submitting: true }));

    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      const reasonParts = [];
      if (unassignModal.reason) reasonParts.push(`Reason: ${unassignModal.reason}`);
      if (unassignModal.notice24h)
        reasonParts.push(
          `24h notice: ${unassignModal.notice24h === "yes" ? "Yes" : "No"}`,
        );
      if (unassignModal.availability) {
        const map = {
          available: "Available during shift",
          unavailable_shift: "Unavailable during shift",
          unavailable_day: "Unavailable all day",
          unavailable_range: "Unavailable date range",
        };
        reasonParts.push(`Availability: ${map[unassignModal.availability] || unassignModal.availability}`);
      }
      if (unassignModal.note && unassignModal.note.trim()) {
        reasonParts.push(`Note: ${unassignModal.note.trim()}`);
      }
      const reason = reasonParts.join(" | ");

      const resp = await fetch(
        `${BASE_URL}/api/shifts/${unassignModal.shiftId}/force-unassign`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            accountId: unassignModal.accountId,
            reason,
            affectsReliability: Boolean(unassignModal.countTowardsReliability),
          }),
        },
      );
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json.success) {
        throw new Error(json.message || "Failed to unassign staff");
      }

      showToast("Staff unassigned from shift");
      setUnassignModal((prev) => ({ ...prev, open: false, submitting: false }));
      setReloadKey((k) => k + 1);
    } catch (err) {
      console.error("Force unassign failed:", err);
      showError(err.message || "Failed to unassign staff");
      setUnassignModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  // Pagination helpers: produce a compact page list with ellipses
  const getPageList = () => {
    const total = Number(totalPages) || 1;
    const current = Number(currentPage) || 1;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages = [];
    pages.push(1);
    if (current > 4) pages.push("left-ellipsis");
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let p = start; p <= end; p++) pages.push(p);
    if (current < total - 3) pages.push("right-ellipsis");
    pages.push(total);
    return pages;
  };
  // get client list
  const getClients = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/clients/clients?all=1`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const data = await res.json();
      setClients(Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []); // store array
    } catch (err) {
      console.error("Error fetching clients:", err);
      setClients([]);
    }
  };

  const getClientLocations = async (clientId, tabKey) => {
    const tab = tabKey || selectedTab;
    try {
      const res = await fetch(
        `${BASE_URL}/api/jobs/client-locations/${clientId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      const data = await res.json();
      setClientLocByTab((prev) => ({
        ...prev,
        [tab]: {
          ...(prev[tab] || emptyJobTabClientLoc()),
          locations: Array.isArray(data) ? data : [],
        },
      }));
    } catch (err) {
      console.error("Error fetching locations:", err);
      setClientLocByTab((prev) => ({
        ...prev,
        [tab]: {
          ...(prev[tab] || emptyJobTabClientLoc()),
          locations: [],
        },
      }));
    }
  };

  useEffect(() => {
    getClients();
  }, []);

  // load shifts status for filters
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/jobs/shift-states`);
        const data = await res.json();

        const formatted = data.map((s) => ({
          value: s.state,
          label: s.state,
        }));

        setShiftStates(formatted);
      } catch (err) {
        console.error(err);
      }
    };

    fetchStates();
  }, []);

  // load staff for filter panel
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [officeStaffOptions, setOfficeStaffOptions] = useState([]);

  const staffFilterPanelOpen =
    (jobsFilterPanelOpen &&
      (selectedTab === "SHIFTS" || selectedTab === "SHIFTASSIGNMENT")) ||
    staffingRequestShiftsFilterPanel.isOpen;

  const selectedStaffFilterIds = React.useMemo(() => {
    const ids = [];
    if (shiftsFilters.staff) ids.push(String(shiftsFilters.staff));
    if (Array.isArray(shiftAssignmentFilters.staffAccountIds)) {
      ids.push(...shiftAssignmentFilters.staffAccountIds.map(String));
    }
    return [...new Set(ids.filter(Boolean))];
  }, [shiftsFilters.staff, shiftAssignmentFilters.staffAccountIds]);

  useEffect(() => {
    if (!staffFilterPanelOpen) {
      setStaffFilterQuery("");
      setStaffFilterOptions([]);
      return undefined;
    }

    const search = staffFilterQuery.trim();

    const timer = setTimeout(async () => {
      setStaffFilterLoading(true);
      try {
        const params = new URLSearchParams();
        if (search) params.set("search", search);
        if (selectedStaffFilterIds.length) {
          params.set("ids", selectedStaffFilterIds.join(","));
        }
        const token =
          localStorage.getItem("token") || localStorage.getItem("authToken");
        const res = await fetch(
          `${BASE_URL}/api/jobs/staff?${params.toString()}`,
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
        );
        const data = await res.json();
        const rows = Array.isArray(data) ? data : [];
        const formatted = rows
          .map((s) => ({
            value: String(s.account_id),
            label: (s.name || `${s.first_name || ""} ${s.last_name || ""}`)
              .replace(/\s+/g, " ")
              .trim(),
          }))
          .filter((o) => o.value && o.label);
        cacheStaffFilterLabels(formatted);
        setStaffFilterOptions(formatted);
        setStaffOptions(formatted);
      } catch (err) {
        console.error("Staff filter fetch error:", err);
        setStaffFilterOptions([]);
      } finally {
        setStaffFilterLoading(false);
      }
    }, search ? 250 : 0);

    return () => clearTimeout(timer);
  }, [
    staffFilterPanelOpen,
    staffFilterQuery,
    selectedStaffFilterIds,
    cacheStaffFilterLabels,
  ]);

  useEffect(() => {
    if (!(jobsFilterPanelOpen && selectedTab === "SHIFTASSIGNMENT")) {
      setOfficeStaffFilterQuery("");
      setOfficeStaffFilterOptions([]);
      return undefined;
    }

    const search = officeStaffFilterQuery.trim();
    const selectedOfficeIds = Array.isArray(
      shiftAssignmentFilters.lastModifiedByAccountIds,
    )
      ? shiftAssignmentFilters.lastModifiedByAccountIds.map(String).filter(Boolean)
      : [];

    const timer = setTimeout(async () => {
      setOfficeStaffFilterLoading(true);
      try {
        const params = new URLSearchParams();
        if (search) params.set("search", search);
        if (selectedOfficeIds.length) {
          params.set("ids", selectedOfficeIds.join(","));
        }
        const token =
          localStorage.getItem("token") || localStorage.getItem("authToken");
        const res = await fetch(
          `${BASE_URL}/api/jobs/office-staff?${params.toString()}`,
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
        );
        const data = await res.json();
        const rows = Array.isArray(data) ? data : [];
        const formatted = rows
          .map((s) => ({
            value: String(s.account_id || s.id || s.value || ""),
            label:
              s.name ||
              s.label ||
              `${s.first_name || ""} ${s.last_name || ""}`.trim(),
          }))
          .filter((o) => o.value && o.label);
        cacheOfficeStaffFilterLabels(formatted);
        setOfficeStaffFilterOptions(formatted);
        setOfficeStaffOptions(formatted);
      } catch (err) {
        console.error("Office staff filter fetch error:", err);
        setOfficeStaffFilterOptions([]);
      } finally {
        setOfficeStaffFilterLoading(false);
      }
    }, search ? 250 : 0);

    return () => clearTimeout(timer);
  }, [
    jobsFilterPanelOpen,
    selectedTab,
    officeStaffFilterQuery,
    shiftAssignmentFilters.lastModifiedByAccountIds,
    cacheOfficeStaffFilterLabels,
  ]);

  // load outsourced staff
  const [outsourcedStaffOptions, setOutsourcedStaffOptions] = useState([]);
  const [selectedOutstaff, setSelectedOutStaff] = useState([]);

  useEffect(() => {
    if (!shiftsFilterPanelOpen) return;
    const fetchOutSourced = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/jobs/outsourced-staff`);
        const data = await res.json();
        console.log("Fetched outsourced staff for filters:", data);

        const formatted = data.map((s) => ({
          value: s.account_id,
          label: s.name || `${s.first_name} ${s.last_name}`,
        }));

        setOutsourcedStaffOptions(formatted);
      } catch (err) {
        console.error("Failed to fetch outsourced staff", err);
      }
    };

    fetchOutSourced();
  }, [shiftsFilterPanelOpen]);

  const [assignmentType, setAssignmentType] = useState("all");
  const assignmentTypeOptions = [
    { value: "all", label: "All" },
    { value: "fieldstaff", label: "Field Staff" },
    { value: "outsourcedagency", label: "Outsourced Agency" },
  ];
  const actualTimeStatusOptions = [
    { value: "notSubmitted", label: "Not Submitted" },
    { value: "asScheduled", label: "As Scheduled" },
    { value: "different", label: "Different" },
    { value: "noShow", label: "No Show" },
  ];

  const actualTimeConflictOptions = [
    { label: "All", value: "all" },
    { label: "Yes", value: "Yes" },
    { label: "No", value: "No" },
  ];

  const [travelTimeValue, setTravelTimeValue] = useState("all");

  const travelTimeOptions = [
    { label: "All", value: "all" },
    { label: "Yes", value: "yes" },
    { label: "No", value: "no" },
  ];

  const checkInNoteTypeOptions = [
    { value: "Check in", label: "Check in" },
    { value: "Check in early", label: "Check in early" },
    { value: "Check out", label: "Check out" },
    { value: "Check out late", label: "Check out late" },
  ];

  const shiftsSortByOptions = useMemo(
    () => [
      { value: "shift", label: "Shift" },
      { value: "scheduled", label: "Scheduled" },
      { value: "staff_first_name", label: "Staff First Name" },
      { value: "staff_last_name", label: "Staff Last Name" },
      ...(canReadShiftActual
        ? [
            {
              value: "actuals_submission_status",
              label: "Actuals Submission Status",
            },
          ]
        : []),
      { value: "schedule_view", label: "Schedule View" },
    ],
    [canReadShiftActual],
  );

  const shiftsSortDirectionOptions = [
    { value: "asc", label: "Ascending" },
    { value: "desc", label: "Descending" },
  ];

  const [selectedShiftClients, setSelectedShiftClients] = useState([]);
  const [selectedShiftClientLocations, setSelectedShiftClientLocations] =
    useState([]);
  const [selectedShiftSupervisors, setSelectedShiftSupervisors] = useState([]);

  useEffect(() => {
    setSelectedStates(
      Array.isArray(pendingShiftsFilters.shiftStatus)
        ? pendingShiftsFilters.shiftStatus
        : [],
    );
  }, [pendingShiftsFilters.shiftStatus]);

  useEffect(() => {
    setSelectedStaff(
      pendingShiftsFilters.staff ? [String(pendingShiftsFilters.staff)] : [],
    );
  }, [pendingShiftsFilters.staff]);

  useEffect(() => {
    const reverseMap = {
      "Field Staff": "fieldstaff",
      "OutSourced Agency": "outsourcedagency",
    };
    setAssignmentType(reverseMap[pendingShiftsFilters.assignmentType] || "all");
  }, [pendingShiftsFilters.assignmentType]);

  useEffect(() => {
    const selected = Array.isArray(pendingShiftsFilters.clients)
      ? pendingShiftsFilters.clients
      : [];
    setSelectedShiftClients(selected.map((v) => String(v)));
  }, [pendingShiftsFilters.clients]);

  useEffect(() => {
    const selected = Array.isArray(pendingShiftsFilters.clientLocations)
      ? pendingShiftsFilters.clientLocations
      : [];
    setSelectedShiftClientLocations(selected.map((v) => String(v)));
  }, [pendingShiftsFilters.clientLocations]);

  useEffect(() => {
    const selected =
      Array.isArray(pendingShiftsFilters.shiftSupervisors) &&
      pendingShiftsFilters.shiftSupervisors.length
        ? pendingShiftsFilters.shiftSupervisors
        : pendingShiftsFilters.shiftSupervisor
          ? [String(pendingShiftsFilters.shiftSupervisor)]
          : [];
    setSelectedShiftSupervisors(selected.map((v) => String(v)));
  }, [
    pendingShiftsFilters.shiftSupervisors,
    pendingShiftsFilters.shiftSupervisor,
  ]);

  useEffect(() => {
    if (
      !Array.isArray(selectedShiftSupervisors) ||
      !selectedShiftSupervisors.length ||
      !shiftsSupervisorOptions.length
    )
      return;
    const allowed = new Set(
      (Array.isArray(shiftsSupervisorOptions)
        ? shiftsSupervisorOptions
        : []
      ).map((opt) => String(opt?.value || "")),
    );
    const filtered = selectedShiftSupervisors.filter((value) =>
      allowed.has(String(value)),
    );
    if (filtered.length !== selectedShiftSupervisors.length) {
      setSelectedShiftSupervisors(filtered);
      handleShiftsFilterChange("shiftSupervisors", filtered);
    }
  }, [selectedShiftSupervisors, shiftsSupervisorOptions]);

  useEffect(() => {
    if (selectedTab !== "SHIFTS") return;
    if (!shiftsFilterPanelOpen && shiftsClientOptions.length > 0) return;

    const fetchShiftClients = async () => {
      try {
        const token =
          localStorage.getItem("token") || localStorage.getItem("authToken");
        const res = await fetch(
          `${BASE_URL}/api/clients/clients?all=1&search=${encodeURIComponent("")}`,
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
        );
        if (!res.ok) throw new Error(`Clients fetch failed: ${res.status}`);
        const data = await res.json();
        const rows = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        const mapped = rows
          .map((item) => {
            const value = item?.value ?? item?.id ?? item?.client_id;
            const label = item?.label ?? item?.name;
            if (!value || !label) return null;
            return { value: String(value), label: String(label) };
          })
          .filter(Boolean);
        setShiftsClientOptions(mapped);
      } catch (err) {
        console.error("Failed to fetch shift clients", err);
        setShiftsClientOptions([]);
      }
    };

    fetchShiftClients();
  }, [shiftsFilterPanelOpen, selectedTab, shiftsClientOptions.length]);

  useEffect(() => {
    const clientIds = Array.isArray(selectedShiftClients)
      ? selectedShiftClients.filter(Boolean)
      : [];
    if (!clientIds.length) {
      setShiftClientLocationOptions([]);
      return;
    }

    const fetchSelectedClientLocations = async () => {
      try {
        const token =
          localStorage.getItem("token") || localStorage.getItem("authToken");
        const authOpts = token
          ? { headers: { Authorization: `Bearer ${token}` } }
          : undefined;
        const res = await fetch(
          `${BASE_URL}/api/jobs/selected-client-locations?client_ids=${encodeURIComponent(
            clientIds.join(","),
          )}`,
          authOpts,
        );
        if (res.ok) {
          const data = await res.json();
          const rows = Array.isArray(data)
            ? data
            : Array.isArray(data?.data)
              ? data.data
              : [];
          const mapped = rows
            .map((item) => {
              const value = item?.value ?? item?.id ?? item?.client_location_id;
              const label =
                item?.label ||
                (item?.client_name && (item?.name || item?.location_name)
                  ? `${item.client_name} : ${item.name || item.location_name}`
                  : item?.name || item?.location_name || item?.full_address);
              if (!value || !label) return null;
              return { value: String(value), label: String(label) };
            })
            .filter(Boolean);
          setShiftClientLocationOptions(mapped);
          return;
        }

        // Fallback: fetch per client from jobs client-locations route and prefix with client name.
        const fallbackResponses = await Promise.all(
          clientIds.map(async (clientId) => {
            const clientInfo = shiftsClientOptions.find(
              (c) => String(c.value) === String(clientId),
            );
            const clientName = clientInfo?.label || `Client ${clientId}`;
            const locRes = await fetch(
              `${BASE_URL}/api/jobs/client-locations/${encodeURIComponent(clientId)}`,
              authOpts,
            );
            if (!locRes.ok) return [];
            const locData = await locRes.json();
            const locRows = Array.isArray(locData)
              ? locData
              : Array.isArray(locData?.data)
                ? locData.data
                : [];
            return locRows.map((loc) => {
              const locId = loc?.id ?? loc?.client_location_id;
              const locName = loc?.name || loc?.label || loc?.full_address;
              if (!locId || !locName) return null;
              return {
                value: String(locId),
                label: `${clientName} : ${String(locName)}`,
              };
            });
          }),
        );
        setShiftClientLocationOptions(fallbackResponses.flat().filter(Boolean));
      } catch (err) {
        console.error("Failed to fetch selected client locations", err);
        setShiftClientLocationOptions([]);
      }
    };

    fetchSelectedClientLocations();
  }, [selectedShiftClients, shiftsClientOptions, selectedTab]);

  useEffect(() => {
    if (!shiftsFilterPanelOpen) return;

    const fetchShiftSupervisors = async () => {
      try {
        const token =
          localStorage.getItem("token") || localStorage.getItem("authToken");
        const authOpts = token
          ? { headers: { Authorization: `Bearer ${token}` } }
          : undefined;
        const selectedClientIds = Array.isArray(selectedShiftClients)
          ? selectedShiftClients.filter(Boolean)
          : [];
        const supervisorUrl =
          selectedClientIds.length > 0
            ? `${BASE_URL}/api/jobs/shift-supervisors?client_ids=${encodeURIComponent(selectedClientIds.join(","))}`
            : `${BASE_URL}/api/jobs/shift-supervisors`;
        const res = await fetch(
          supervisorUrl,
          authOpts,
        );
        if (!res.ok) {
          throw new Error(`Shift supervisors fetch failed: ${res.status}`);
        }
        const data = await res.json();
        const rows = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        const mapped = rows
          .map((item) => {
            const value = item?.value ?? item?.id ?? item?.account_id;
            const label = item?.label ?? item?.name;
            if (!value || !label) return null;
            return { value: String(value), label: String(label) };
          })
          .filter(Boolean);
        setShiftsSupervisorOptions(mapped);
      } catch (err) {
        console.error("Failed to fetch shift supervisors", err);
        setShiftsSupervisorOptions([]);
      }
    };

    // Fetch both supervisors and outsourced agencies together
    fetchShiftSupervisors();
    fetchShiftsOutsourcedAgencyOptions();
  }, [shiftsFilterPanelOpen, selectedShiftClients]);

  // load client positions
  const [selectedClientPositions, setSelectedClientPositions] = useState([]);

  useEffect(() => {
    setSelectedClientPositions(
      pendingShiftsFilters.clientPosition
        ? [String(pendingShiftsFilters.clientPosition)]
        : [],
    );
  }, [pendingShiftsFilters.clientPosition]);

  useEffect(() => {
    if (!shiftsFilterPanelOpen) return;

    const fetchClientPositions = async () => {
      try {
        const token =
          localStorage.getItem("token") || localStorage.getItem("authToken");
        const selectedClientIds = Array.isArray(selectedShiftClients)
          ? selectedShiftClients.filter(Boolean)
          : [];
        const positionsUrl =
          selectedClientIds.length > 0
            ? `${BASE_URL}/api/jobs/client-positions?client_ids=${encodeURIComponent(selectedClientIds.join(","))}`
            : `${BASE_URL}/api/jobs/client-positions`;
        const res = await fetch(
          positionsUrl,
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
        );
        if (!res.ok) {
          throw new Error(`Failed to fetch positions: ${res.status}`);
        }
        const data = await res.json();
        const rows = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        const mapped = rows
          .map((item) => {
            const rawValue = item?.value ?? item?.id ?? item?.position_id;
            const value =
              rawValue === null || rawValue === undefined
                ? ""
                : String(rawValue);
            const label =
              item?.label || item?.name || item?.position_name || value;
            return value ? { value, label: String(label) } : null;
          })
          .filter(Boolean);

        setSelectedClientPositions((prev) =>
          prev.filter((value) =>
            mapped.some((option) => String(option.value) === String(value)),
          ),
        );

        // Keep shifts filter usable even without a selected staffing request.
        setShiftsClientPositionOptions((prev) => {
          const hasScopedValues = prev.some(
            (option) => String(option?.value || "") !== "",
          );
          if (selectedRequest?.id && hasScopedValues) return prev;
          return [{ value: "", label: "All positions" }, ...mapped];
        });
      } catch (err) {
        console.error("Failed to fetch client positions", err);
      }
    };

    fetchClientPositions();
  }, [selectedRequest?.id, selectedShiftClients, shiftsFilterPanelOpen]);

  // load all shift supervisors
  const [shiftSupervisors, setShiftSupervisors] = useState([]);
  const [selectedSupervisors, setSelectedSupervisors] = useState([]);

  const loadShiftSupervisors = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/jobs/shift-supervisors`);
      const data = await res.json();

      const options = data.map((s) => ({
        label: s.name,
        value: String(s.account_id),
      }));

      setShiftsSupervisorOptions(options);
    } catch (err) {
      console.error("Failed to load supervisors", err);
    }
  };

  //load checkin note
  const [checkinNoteOptions, setCheckinNoteOptions] = useState([]);
  const [selectedCheckinNotes, setSelectedCheckinNotes] = useState([]);

  const fetchCheckinNoteOptions = async () => {
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      const res = await fetch(
        `${BASE_URL}/api/jobs/checkin-notes`,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      );
      if (!res.ok) throw new Error(`Checkin notes fetch failed: ${res.status}`);
      const data = await res.json();

      const rows = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [];
      const dedup = new Map();
      rows.forEach((n) => {
        const value = String(n?.note_id || "");
        if (!value) return;
        dedup.set(value, {
          label: String(n?.note || `Note #${value}`),
          value,
        });
      });
      setCheckinNoteOptions(Array.from(dedup.values()));
    } catch (err) {
      console.error("Checkin notes fetch error:", err);
      // Fallback: derive options from currently loaded shifts
      const fallback = Array.isArray(shiftsData?.shifts)
        ? shiftsData.shifts
            .map((shift) => {
              const noteId = String(
                shift?.checkin_note_id ||
                  shift?.check_in_note_id ||
                  shift?.checkInNoteId ||
                  "",
              );
              if (!noteId) return null;
              return {
                value: noteId,
                label: String(
                  shift?.check_in_note ||
                    shift?.checkInNote ||
                    `Note #${noteId}`,
                ),
              };
            })
            .filter(Boolean)
        : [];
      const dedup = new Map();
      fallback.forEach((item) => dedup.set(item.value, item));
      setCheckinNoteOptions(Array.from(dedup.values()));
    }
  };

  useEffect(() => {
    if (!shiftsFilterPanelOpen) return;
    fetchCheckinNoteOptions();
  }, [shiftsFilterPanelOpen, shiftsData?.shifts?.length]);

  useEffect(() => {
    setSelectedCheckinNotes(
      Array.isArray(pendingShiftsFilters.checkInNoteType)
        ? pendingShiftsFilters.checkInNoteType.map((v) => String(v))
        : [],
    );
  }, [pendingShiftsFilters.checkInNoteType]);

  useEffect(() => {
    if (!shiftsFilterPanelOpen) return;
    setPendingShiftsFilters({ ...shiftsFilters });
    setActualTimeConflict(
      shiftsFilters.actualTimeConflict === "yes"
        ? "Yes"
        : shiftsFilters.actualTimeConflict === "no"
          ? "No"
          : "all",
    );
    setTravelTimeValue(
      shiftsFilters.travelTime === "yes"
        ? "yes"
        : shiftsFilters.travelTime === "no"
          ? "no"
          : "all",
    );
  }, [shiftsFilterPanelOpen, shiftsFilters]);

  const primaryJobsButtonClass =
    "rounded-lg bg-sidebar-active px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600";

  const resultsStart =
    totalCount > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const resultsEnd =
    totalCount > 0 ? Math.min(currentPage * rowsPerPage, totalCount) : 0;

  const getCurrentSortBy = () => {
    if (selectedTab === "STAFFINGREQUESTS") return filterSortBy || "all";
    if (selectedTab === "SHIFTS") return getShiftsSortBy();
    if (selectedTab === "SHIFTASSIGNMENT")
      return shiftAssignmentFilters.sortBy || "last_modified_proximity";
    return "all";
  };

  const getCurrentSortDirection = () => {
    if (selectedTab === "STAFFINGREQUESTS") return filterSortDirection || "asc";
    if (selectedTab === "SHIFTS") return shiftsFilters.sortDirection || "asc";
    if (selectedTab === "SHIFTASSIGNMENT")
      return shiftAssignmentFilters.sortDirection || "asc";
    return "asc";
  };

  const getCurrentSortOptions = () => {
    if (selectedTab === "STAFFINGREQUESTS") return JOBS_STAFFING_SORT_OPTIONS;
    if (selectedTab === "SHIFTS") {
      return canReadShiftActual
        ? JOBS_SHIFTS_SORT_OPTIONS
        : JOBS_SHIFTS_SORT_OPTIONS.filter(
            (o) => o.value !== "actuals_submission_status",
          );
    }
    if (selectedTab === "SHIFTASSIGNMENT")
      return JOBS_SHIFT_ASSIGNMENT_SORT_OPTIONS;
    return [{ value: "all", label: "All" }];
  };

  const setCurrentSortBy = (value) => {
    if (selectedTab === "STAFFINGREQUESTS") setFilterSortBy(value);
    if (selectedTab === "SHIFTS") handleShiftsFilterChange("sortBy", value);
    if (selectedTab === "SHIFTASSIGNMENT") {
      setShiftAssignmentFilters((prev) => ({ ...prev, sortBy: value }));
    }
  };

  const toggleCurrentSortDirection = () => {
    if (selectedTab === "STAFFINGREQUESTS") {
      setFilterSortDirection((prev) =>
        String(prev || "asc").toLowerCase() === "asc" ? "desc" : "asc",
      );
    }
    if (selectedTab === "SHIFTS") {
      handleShiftsFilterChange(
        "sortDirection",
        String(shiftsFilters.sortDirection || "asc").toLowerCase() === "asc"
          ? "desc"
          : "asc",
      );
    }
    if (selectedTab === "SHIFTASSIGNMENT") {
      setShiftAssignmentFilters((prev) => ({
        ...prev,
        sortDirection:
          String(prev.sortDirection || "asc").toLowerCase() === "asc"
            ? "desc"
            : "asc",
      }));
    }
  };

  const clearAllStaffingFilters = () => {
    setSearchTerm("");
    patchClientLocTab("STAFFINGREQUESTS", {
      filterClient: "",
      clientSearch: "",
      filterClientList: [],
      filterLocation: "",
      filterLocationList: [],
      filterLocations: [],
      locationSearch: "",
      locations: [],
    });
    setFilterMode("day");
    setFilterStartDate(getAppTodayIso());
    setFilterRange("");
    setFilterFromDate("");
    setFilterToDate("");
    setStaffingShowDeleted(false);
    setFilterSortBy("");
    setFilterSortDirection("asc");
    setActiveStaffingFilterField("");
    setShowClientDropdown(false);
    setShowLocationDropdown(false);
    setCurrentPage(1);
  };

  const clearAllTimesheetFilters = () => {
    patchClientLocTab("TIMESHEETS", {
      filterClient: "",
      clientSearch: "",
      filterClientList: [],
      filterLocation: "",
      filterLocationList: [],
      filterLocations: [],
      locationSearch: "",
      locations: [],
    });
    setFilterMode("day");
    setFilterStartDate(getAppTodayIso());
    setFilterRange("");
    setCurrentPage(1);
  };

  const clearAllShiftAssignmentFilters = () => {
    const today = getAppTodayIso();
    const [y, m, d] = today.split("-").map(Number);
    const pad = (n) => String(n).padStart(2, "0");
    setShiftAssignmentWideDateRange({
      from: `${y - 2}-${pad(m)}-${pad(d)}`,
      to: `${y + 1}-${pad(m)}-${pad(d)}`,
    });
    setShiftAssignmentFilters((prev) => ({
      ...prev,
      lastModifiedProximity: "",
      fromShiftStart: "",
      toShiftStart: "",
      staffAccountIds: [],
      lastModifiedByAccountIds: [],
      sortBy: "last_modified_proximity",
      sortDirection: "asc",
    }));
    setCurrentPage(1);
  };

  const shiftsStaffOptionsMerged = React.useMemo(
    () =>
      mergeStaffFilterOptions(
        shiftsFilters.staff ? [shiftsFilters.staff] : [],
      ),
    [mergeStaffFilterOptions, shiftsFilters.staff, staffFilterOptions],
  );

  const shiftAssignmentStaffOptionsMerged = React.useMemo(
    () =>
      mergeStaffFilterOptions(shiftAssignmentFilters.staffAccountIds || []),
    [
      mergeStaffFilterOptions,
      shiftAssignmentFilters.staffAccountIds,
      staffFilterOptions,
    ],
  );

  const shiftAssignmentOfficeStaffOptionsMerged = React.useMemo(
    () =>
      mergeOfficeStaffFilterOptions(
        shiftAssignmentFilters.lastModifiedByAccountIds || [],
      ),
    [
      mergeOfficeStaffFilterOptions,
      shiftAssignmentFilters.lastModifiedByAccountIds,
      officeStaffFilterOptions,
    ],
  );

  const shiftsStaffSelectedLabel =
    shiftsStaffOptionsMerged.find(
      (o) => String(o.value) === String(shiftsFilters.staff || ""),
    )?.label || "";

  const currentFilterChips =
    selectedTab === "STAFFINGREQUESTS"
      ? buildJobsStaffingRequestsFilterChips({
          searchTerm,
          filterClient,
          clientSearch,
          filterClientList,
          filterLocation,
          filterLocationList,
          filterFromDate,
          filterToDate,
          staffingShowDeleted,
        })
      : selectedTab === "SHIFTS"
        ? buildJobsShiftsFilterChips({
            filters: shiftsFilters,
            canReadShiftActual,
            shiftStatusOptions: SHIFTS_CHECKIN_STATUS_OPTIONS,
            shiftsStaffOptions: shiftsStaffOptionsMerged,
            shiftsClientOptions,
            shiftClientLocationOptions,
            shiftsClientLocationOptions,
            shiftsSupervisorOptions,
            shiftsOutsourcedAgencyOptions,
            shiftsClientPositionOptions,
            checkinNoteOptions,
            checkInNoteTypeOptions,
          })
        : selectedTab === "TIMESHEETS"
          ? buildJobsTimesheetsFilterChips({
              clientSearch,
              filterLocations,
              filterLocationList,
              filterMode,
              filterStartDate,
              filterRange,
              today: getAppTodayIso(),
            })
          : selectedTab === "SHIFTASSIGNMENT"
            ? buildJobsShiftAssignmentFilterChips({
                filters: shiftAssignmentFilters,
                staffOptions: shiftAssignmentStaffOptionsMerged,
                officeStaffOptions: shiftAssignmentOfficeStaffOptionsMerged,
              })
            : [];

  const staffingRequestShiftsFilterChips = buildJobsShiftsFilterChips({
    filters: shiftsFilters,
    canReadShiftActual,
    shiftStatusOptions: SHIFTS_CHECKIN_STATUS_OPTIONS,
    shiftsStaffOptions: shiftsStaffOptionsMerged,
    shiftsClientOptions,
    shiftClientLocationOptions,
    shiftsClientLocationOptions,
    shiftsSupervisorOptions,
    shiftsOutsourcedAgencyOptions,
    shiftsClientPositionOptions,
    checkinNoteOptions,
    checkInNoteTypeOptions,
  });

  const removeCurrentFilterChip = (chip) => {
    const key = chip?.key;
    if (selectedTab === "STAFFINGREQUESTS") removeStaffingFilter(key);
    if (selectedTab === "SHIFTS") removeShiftsFilter(key);
    if (selectedTab === "SHIFTASSIGNMENT") removeShiftAssignmentFilter(key);
    if (selectedTab === "TIMESHEETS") {
      if (key === "client") {
        setFilterClient("");
        setClientSearch("");
        setFilterLocation("");
        setFilterLocationList([]);
        setFilterLocations([]);
      }
      if (key === "locations") {
        const value = chip?.value;
        setFilterLocations((prev) =>
          value ? prev.filter((id) => String(id) !== String(value)) : [],
        );
      }
      if (key === "mode") setFilterMode("day");
      if (key === "startDate") setFilterStartDate(getAppTodayIso());
      if (key === "range") setFilterRange("");
    }
    setCurrentPage(1);
  };

  const clearCurrentFilters = () => {
    if (selectedTab === "STAFFINGREQUESTS") clearAllStaffingFilters();
    if (selectedTab === "SHIFTS") handleShiftsFilterClear();
    if (selectedTab === "TIMESHEETS") clearAllTimesheetFilters();
    if (selectedTab === "SHIFTASSIGNMENT") clearAllShiftAssignmentFilters();
  };

  const renderShiftsFilterFields = (panel) => (
    <JobsShiftsFilterFields
      focusedFieldId={panel.focusedFieldId}
      onFocusClear={panel.clearFocus}
      filters={shiftsFilters}
      onChange={handleShiftsFilterChange}
      selectedShiftClients={selectedShiftClients}
      setSelectedShiftClients={setSelectedShiftClients}
      selectedShiftClientLocations={selectedShiftClientLocations}
      setSelectedShiftClientLocations={setSelectedShiftClientLocations}
      selectedClientPositions={selectedClientPositions}
      setSelectedClientPositions={setSelectedClientPositions}
      travelTimeValue={travelTimeValue}
      setTravelTimeValue={setTravelTimeValue}
      canReadShiftActual={canReadShiftActual}
      shiftStatusOptions={SHIFTS_CHECKIN_STATUS_OPTIONS}
      shiftsStaffOptions={shiftsStaffOptionsMerged}
      staffFilterLoading={staffFilterLoading}
      onStaffQueryChange={setStaffFilterQuery}
      staffSelectedLabel={shiftsStaffSelectedLabel}
      shiftsClientOptions={shiftsClientOptions}
      shiftClientLocationOptions={shiftClientLocationOptions}
      shiftsSupervisorOptions={shiftsSupervisorOptions}
      shiftsOutsourcedAgencyOptions={shiftsOutsourcedAgencyOptions}
      shiftsClientPositionOptions={shiftsClientPositionOptions}
      checkinNoteOptions={checkinNoteOptions}
      checkInNoteTypeOptions={checkInNoteTypeOptions}
    />
  );

  const renderJobsFilterFields = () => {
    if (selectedTab === "STAFFINGREQUESTS") {
      return (
        <JobsStaffingRequestsFilterFields
          focusedFieldId={filterPanel.focusedFieldId}
          onFocusClear={filterPanel.clearFocus}
          clients={filterClientList.length ? filterClientList : clients}
          locations={locations}
          filterClient={filterClient}
          clientSearch={clientSearch}
          filterLocation={filterLocation}
          locationSearch={locationSearch}
          filterFromDate={filterFromDate}
          filterToDate={filterToDate}
          staffingShowDeleted={staffingShowDeleted}
          filterSortBy={filterSortBy}
          filterSortDirection={filterSortDirection}
          clientLoading={clientLoading}
          showLocationDropdown={showLocationDropdown}
          setClientSearch={setClientSearch}
          setLocationSearch={setLocationSearch}
          setShowLocationDropdown={setShowLocationDropdown}
          setFilterClient={setFilterClient}
          setFilterLocation={setFilterLocation}
          setSelectedLocation={setSelectedLocation}
          setLocations={setLocations}
          getClientLocations={getClientLocations}
          setFilterFromDate={setFilterFromDate}
          setFilterToDate={setFilterToDate}
          setStaffingShowDeleted={setStaffingShowDeleted}
          setFilterSortBy={setFilterSortBy}
          setFilterSortDirection={setFilterSortDirection}
        />
      );
    }

    if (selectedTab === "SHIFTS") {
      return renderShiftsFilterFields(filterPanel);
    }

    if (selectedTab === "TIMESHEETS") {
      return (
        <JobsTimesheetsFilterFields
          focusedFieldId={filterPanel.focusedFieldId}
          onFocusClear={filterPanel.clearFocus}
          filterClient={filterClient}
          clientSearch={clientSearch}
          filterClientList={filterClientList}
          filterLocationList={filterLocationList}
          filterLocations={filterLocations}
          filterMode={filterMode}
          filterStartDate={filterStartDate}
          filterRange={filterRange}
          clientLoading={clientLoading}
          setFilterClient={setFilterClient}
          setClientSearch={setClientSearch}
          setFilterLocation={setFilterLocation}
          setFilterLocationList={setFilterLocationList}
          setFilterLocations={setFilterLocations}
          setFilterMode={setFilterMode}
          setFilterStartDate={setFilterStartDate}
          setFilterRange={setFilterRange}
        />
      );
    }

    if (selectedTab === "SHIFTASSIGNMENT") {
      return (
        <JobsShiftAssignmentFilterFields
          focusedFieldId={filterPanel.focusedFieldId}
          onFocusClear={filterPanel.clearFocus}
          filters={shiftAssignmentFilters}
          setFilters={setShiftAssignmentFilters}
          setWideDateRange={setShiftAssignmentWideDateRange}
          onFilterPatch={(partial) => {
            setShiftAssignmentWideDateRange(null);
            setShiftAssignmentFilters((prev) => ({ ...prev, ...partial }));
            setCurrentPage(1);
          }}
          staffOptions={shiftAssignmentStaffOptionsMerged}
          officeStaffOptions={shiftAssignmentOfficeStaffOptionsMerged}
          staffFilterLoading={staffFilterLoading}
          officeStaffFilterLoading={officeStaffFilterLoading}
          onStaffQueryChange={setStaffFilterQuery}
          onOfficeStaffQueryChange={setOfficeStaffFilterQuery}
        />
      );
    }

    return null;
  };

  const signoffDateRef = useRef(null);

  const closeStaffingRequestPanel = async () => {
    if (embedMode && onEmbedClose) {
      onEmbedClose();
      return;
    }
    await refreshCurrentTab();
    setSelectedRequest(null);
  };

  const jobsEmbedShellZ = "z-[50450]";
  const jobsEmbedOverlayZ = "z-[50500]";
  const jobsEmbedNestedOverlayZ = "z-[50510]";
  const jobsEmbedDeepOverlayZ = "z-[50520]";
  const staffingRequestModalZ = embedMode ? jobsEmbedShellZ : "z-50";
  const jobsOverlayZ = embedMode ? jobsEmbedOverlayZ : "z-50";
  const jobsShiftDetailOverlayZ = embedMode ? jobsEmbedNestedOverlayZ : "z-[9999]";

  return (
    <>
      {!embedMode && (
    <Layout active="Jobs">
      <div className="py-3 px-2 flex flex-col p-6 min-h-screen timesheet-print-shrink no-mobile-table-stack">
        <div className="timesheet-print-hide">
          <AtlasPageHeader title="Jobs" />
          <AtlasPageDivider />
        </div>

        <div className="mt-6 mx-6 p-4 border-2 border-gray-200 rounded-xl h-full flex flex-col">
          {/* Tabs */}
          <div className="timesheet-print-hide">
            <AtlasTabBar
              tabs={[
                { id: "STAFFINGREQUESTS", label: "Staffing Requests" },
                { id: "SHIFTS", label: "Shifts" },
                { id: "TIMESHEETS", label: "Timesheets" },
                { id: "SHIFTASSIGNMENT", label: "Shift Assignment" },
              ]}
              activeTab={selectedTab}
              onTabChange={setSelectedTab}
            />
          </div>

          <fieldset
            disabled={shiftsReadOnly}
            className="border-0 m-0 p-0 min-w-0 flex flex-col flex-1 min-h-0"
          >
          <div className={`min-w-0 max-w-full ${filterPanel.contentOffsetClass}`}>
            {selectedTab === "SHIFTS" && (
              <ShiftsSummaryStatRow
                compact={filterPanel.isOpen}
                shiftsCount={mainShiftsSummary.shiftsCount}
                actualHours={mainShiftsSummary.actualHours}
                submittedPercent={mainShiftsSummary.submittedPercent}
                conflictCount={mainShiftsSummary.conflictCount}
              />
            )}
            {selectedTab === "TIMESHEETS" ? (
              <TimesheetsPanel
                variant="admin"
                active={selectedTab === "TIMESHEETS"}
                staffRequestId={selectedRequest?.id}
                selectedRequest={selectedRequest}
                editableRequest={editableRequest}
                supervisors={supervisors}
                contentOffsetClass={filterPanel.contentOffsetClass}
              />
            ) : (
              <>
            <AtlasListToolbar
              searchValue={searchTerm}
              onSearchChange={(value) => {
                setSearchTerm(value);
                setCurrentPage(1);
              }}
              searchPlaceholder={`Search ${selectedTab.toLowerCase()}`}
              onFilterClick={filterPanel.toggle}
              filterOpen={filterPanel.isOpen}
              onRefresh={() => {
                if (selectedTab === "STAFFINGREQUESTS") {
                  fetchStaffingRequests();
                } else if (selectedTab === "SHIFTS") {
                  fetchShifts();
                } else if (selectedTab === "TIMESHEETS") {
                  fetchTimesheets();
                } else if (selectedTab === "SHIFTASSIGNMENT") {
                  setReloadKey((prev) => prev + 1);
                }
              }}
              resultsMeta={
                selectedTab === "TIMESHEETS"
                  ? null
                  : {
                      startRow: resultsStart,
                      endRow: resultsEnd,
                      totalCount,
                      pageSize: rowsPerPage,
                      pageSizeOptions: [15, 25, 50, 100, 200],
                      onPageSizeChange: (size) => {
                        setRowsPerPage(size);
                        setCurrentPage(1);
                      },
                      sortBy: getCurrentSortBy(),
                      sortOrder: getCurrentSortDirection(),
                      sortOptions: getCurrentSortOptions(),
                      onSortByChange: setCurrentSortBy,
                      onSortOrderToggle: toggleCurrentSortDirection,
                    }
              }
              actions={
                <>
                  {selectedTab === "STAFFINGREQUESTS" && (
                    <button
                      type="button"
                      onClick={() => setShowCreateRequestModal(true)}
                      className={primaryJobsButtonClass}
                    >
                      CREATE
                    </button>
                  )}
                  {selectedTab === "SHIFTS" && (
                    <>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const params = buildShiftsQueryParams(
                              shiftsFilters,
                              searchTerm.trim(),
                            );
                            const url = `${BASE_URL}/api/jobs/shifts/export?${params.toString()}`;
                            const resp = await fetch(url);
                            if (!resp.ok) throw new Error("Export failed");
                            const blob = await resp.blob();
                            const link = document.createElement("a");
                            link.href = URL.createObjectURL(blob);
                            link.download = `Shifts_${new Date().toISOString().slice(0, 10)}.csv`;
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                          } catch (err) {
                            showError(err.message || "Export failed");
                          }
                        }}
                        className={primaryJobsButtonClass}
                      >
                        EXPORT
                      </button>
                      {showAdminEasterEgg && (
                        <button
                          type="button"
                          disabled={legacyRepairLoading}
                          onClick={handleLegacyShiftAssignmentRepair}
                          className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
                          title={`Repair legacy assignment data for ${shiftsFilters.fromDate || getAppTodayIso()}${shiftsFilters.toDate ? ` to ${shiftsFilters.toDate}` : ""}`}
                        >
                          {legacyRepairLoading ? "Repairing..." : "Repair legacy shifts"}
                        </button>
                      )}
                    </>
                  )}
                  {selectedTab === "TIMESHEETS" &&
                    (filterMode === "day" ? (
                      <div className="relative inline-block" ref={timesheetPrintDropdownRef}>
                        <div className="flex overflow-hidden rounded-lg border border-red-600">
                          <button
                            type="button"
                            onClick={() => {
                              triggerTimesheetPrint({
                                orientation: filterMode === "week" ? "landscape" : timesheetPrintOrientation,
                                documentTitle: getTimesheetPrintTitle(),
                              });
                            }}
                            className="bg-sidebar-active px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
                          >
                            PRINT
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowTimesheetPrintDropdown((v) => !v)}
                            className="flex items-center border-l border-red-700 bg-sidebar-active px-2 py-2 text-white hover:bg-red-600"
                            aria-label="Print orientation"
                          >
                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                        {showTimesheetPrintDropdown && (
                          <>
                            <div className="fixed inset-0 z-[99]" onClick={() => setShowTimesheetPrintDropdown(false)} aria-hidden="true" />
                            <div className="absolute right-0 top-full z-[100] mt-1 min-w-[120px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                              {["portrait", "landscape"].map((orientation) => (
                                <button
                                  key={orientation}
                                  type="button"
                                  className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-gray-100 ${timesheetPrintOrientation === orientation ? "bg-red-50 font-medium text-red-700" : "text-gray-700"}`}
                                  onClick={() => {
                                    setTimesheetPrintOrientation(orientation);
                                    setShowTimesheetPrintDropdown(false);
                                    triggerTimesheetPrint({
                                      orientation,
                                      documentTitle: getTimesheetPrintTitle(),
                                    });
                                  }}
                                >
                                  {orientation === "portrait" ? "Portrait" : "Landscape"}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          triggerTimesheetPrint({
                            isWeekMode: true,
                            documentTitle: getTimesheetPrintTitle(),
                          });
                        }}
                        className={primaryJobsButtonClass}
                      >
                        PRINT
                      </button>
                    ))}
                </>
              }
              chipBar={
                <AtlasFilterChipBar
                  chips={currentFilterChips}
                  onChipClick={(chip) => {
                    if (chip.fieldId === "search") {
                      staffingSearchInputRef.current?.focus?.();
                    } else if (chip.fieldId) {
                      filterPanel.focusField(chip.fieldId);
                    } else {
                      filterPanel.open();
                    }
                  }}
                  onRemove={removeCurrentFilterChip}
                  onClearAll={clearCurrentFilters}
                />
              }
            />
            <AtlasFilterPanel
              isOpen={filterPanel.isOpen}
              onClose={filterPanel.close}
              onClear={clearCurrentFilters}
              activeCount={currentFilterChips.length}
              title={`${selectedTab === "STAFFINGREQUESTS" ? "Staffing Requests" : selectedTab === "SHIFTASSIGNMENT" ? "Shift Assignment" : selectedTab.charAt(0) + selectedTab.slice(1).toLowerCase()} Filters`}
              stackZIndex={embedMode ? 50520 : 60}
            >
              {renderJobsFilterFields()}
            </AtlasFilterPanel>

          {/* Search with action buttons */}
          {/* Table */}
          <div className="flex-1 min-h-0 min-w-0">
            <div className="atlas-list-table-wrap bg-card rounded-lg border border-border overflow-x-auto h-full table-force-rows timesheet-print-overflow-visible">
              <div className="min-w-0 w-full">
                {selectedTab !== "TIMESHEETS" &&
                (loading || dataTab !== selectedTab) &&
                !(
                  selectedTab === "SHIFTS" &&
                  dataTab === "SHIFTS" &&
                  data.length > 0
                ) ? (
                  <div className="px-3 py-12 text-center text-muted-foreground">
                    Loading…
                  </div>
                ) : (
                  renderTable()
                )}
              </div>
            </div>
          </div>

          {/* Pagination (numbered like Admin_Accounts) */}
          <div className="flex items-center justify-end mt-4">
            <nav
              className="flex items-center justify-between mt-auto text-sm text-muted-foreground gap-2"
              aria-label="Pagination"
            >
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-2.5 py-1.5 border border-[#DC1F0033] text-buttonWhite rounded-md hover:bg-gray-100 disabled:opacity-50"
                aria-label="Previous page"
              >
                Previous
              </button>

              {getPageList().map((p, idx) =>
                p === "left-ellipsis" || p === "right-ellipsis" ? (
                  <span key={`ell-${idx}`} className="px-2.5 py-1.5">
                    …
                  </span>
                ) : (
                  <button
                    key={`pg-${p}`}
                    onClick={() => setCurrentPage(Number(p))}
                    aria-current={
                      Number(currentPage) === Number(p) ? "page" : undefined
                    }
                    className={`px-2.5 py-1.5 border border-[#DC1F0033] rounded-md ${
                      Number(currentPage) === Number(p)
                        ? "bg-[#DC1F00] text-white"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    {p}
                  </button>
                ),
              )}

              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="px-2.5 py-1.5 bg-buttonRed hover:bg-buttonRed-hover text-white rounded-md disabled:opacity-50"
                aria-label="Next page"
              >
                Next
              </button>
            </nav>
          </div>
              </>
            )}
          </div>
        </fieldset>
        </div>
      </div>
    </Layout>
      )}

      {embedMode && !selectedRequest && (
        <div
          className={`fixed inset-0 bg-black/50 ${staffingRequestModalZ} flex items-center justify-center p-4`}
        >
          <div className="text-white text-sm">Loading staffing request…</div>
        </div>
      )}

      {!embedMode && selectedTab === "SHIFTS" && !selectedRequest && selectedShiftIds.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200">
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              {selectedShiftIds.length} selected
            </div>
            <div className="flex items-center gap-3">
              <div className="relative" ref={shiftActionsMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowShiftActionsMenu((prev) => !prev)}
                  className="px-4 py-2 bg-sidebar-active text-white rounded hover:bg-red-600"
                >
                  ACTIONS
                </button>
                {showShiftActionsMenu && (
                  <div className="absolute right-0 bottom-full mb-2 w-72 bg-white border border-gray-200 rounded shadow-lg z-50">
                    <button
                      type="button"
                      className="w-full text-left px-4 py-2 hover:bg-gray-50"
                      onClick={() => {
                        setShowShiftActionsMenu(false);
                        setBulkShiftScope("main");
                        setShowBulkShiftUpdateModal(true);
                      }}
                    >
                      Bulk shift update
                    </button>
                    <button
                      type="button"
                      className="w-full text-left px-4 py-2 hover:bg-gray-50"
                      onClick={() => {
                        setShowShiftActionsMenu(false);
                        setBulkShiftScope("main");
                        setShowBulkDivisionPositionModal(true);
                      }}
                    >
                      Bulk shift division and position update
                    </button>
                  </div>
                )}
              </div>
              <button
                type="button"
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => {
                  setSelectedShiftIds([]);
                  setShowShiftActionsMenu(false);
                }}
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      {confirmDialog.open && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 ${jobsOverlayZ} flex items-center justify-center p-4`}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b">
              <h3 className="text-lg font-semibold text-foreground">
                {confirmDialog.title || "Confirm"}
              </h3>
            </div>
            <div className="p-4 text-sm text-muted-foreground">
              {confirmDialog.message || "Are you sure?"}
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button
                className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700"
                onClick={() =>
                  setConfirmDialog({
                    open: false,
                    title: "",
                    message: "",
                    onConfirm: null,
                  })
                }
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white"
                onClick={() =>
                  confirmDialog.onConfirm && confirmDialog.onConfirm()
                }
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedRequest && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 ${staffingRequestModalZ} flex items-center justify-center p-4`}
          onMouseDown={(e) => {
            if (embedMode && e.target === e.currentTarget) {
              closeStaffingRequestPanel();
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
            <div className="flex flex-col md:flex-row h-full">
              <div className="w-full md:w-64 border-b md:border-b-0 md:border-r border-gray-200 p-4 bg-gray-50 flex-shrink-0 flex flex-col md:flex-col gap-3 md:gap-2">
                <div>
                  <h2 className="text-lg md:text-xl font-semibold">
                    Staffing Request {selectedRequest.id || selectedRequest.staff_request_id}
                  </h2>
                  <h6 className="text-xs md:text-sm text-muted-foreground">
                    {selectedRequest.clientName ||
                      selectedRequest.client_name ||
                      (typeof selectedRequest.client === "string"
                        ? selectedRequest.client
                        : selectedRequest.client?.name) ||
                      clients.find(
                        (c) =>
                          String(c.id || c.client_id) ===
                          String(selectedRequest.client_id)
                      )?.name ||
                      "—"}
                  </h6>
                </div>
                <div className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible pr-2 pb-1 md:pb-0 scrollbar-none">
                  {["Details", "Shifts", "Audits"].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveSidebarTab(tab)}
                      className={`px-4 py-2 md:px-3 md:py-2 rounded text-sm md:text-base text-center md:text-left whitespace-nowrap ${
                        activeSidebarTab === tab
                          ? "bg-sidebar-active text-white"
                          : "hover:bg-gray-200 bg-gray-100 md:bg-transparent"
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="flex justify-between items-center p-6 border-b">
                  <h2 className="text-xl font-semibold">
                    {activeSidebarTab} Information
                  </h2>
                  <button
                    type="button"
                    onClick={closeStaffingRequestPanel}
                    className="text-gray-500 hover:text-gray-800"
                  >
                    ✖
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {activeSidebarTab === "Details" ? (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Client
                          </label>
                          <div className="p-3 bg-gray-50 rounded border">
                            {selectedRequest.clientName || "—"}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Created
                          </label>
                          <div className="p-3 bg-gray-50 rounded border">
                            {formatCreatedDate(selectedRequest)}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Location*
                          </label>
                          <div
                            className="relative location-search-container"
                            ref={locationDropdownRef}
                          >
                            <div className="w-full border rounded px-3 py-2 bg-white min-h-[40px] flex flex-wrap items-center gap-2 focus-within:ring-2 focus-within:ring-blue-500">
                              {/* Selected locations as chips inside input */}
                              {selectedLocations.map((locId) => {
                                const location = clientLocations.find(
                                  (l) =>
                                    String(l.location_id) === String(locId),
                                );
                                return (
                                  <div
                                    key={locId}
                                    className="flex items-center gap-1 bg-gray-300 text-black px-2 py-1 rounded text-sm"
                                  >
                                    <span>
                                      {location?.name ||
                                        location?.street ||
                                        `Location #${locId}`}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newLocations =
                                          selectedLocations.filter(
                                            (id) => id !== locId,
                                          );
                                        console.log(
                                          "DEBUG REMOVE - Location chip:",
                                          {
                                            removing_locId: locId,
                                            old_selectedLocations:
                                              selectedLocations,
                                            new_selectedLocations: newLocations,
                                            setting_client_location_id:
                                              newLocations[0] || "",
                                          },
                                        );
                                        setSelectedLocations(newLocations);
                                        setEditableRequest((r) => ({
                                          ...r,
                                          client_location_id:
                                            newLocations[0] || "",
                                        }));
                                      }}
                                      className="hover:bg-gray-400 rounded px-1 ml-1"
                                    >
                                      ×
                                    </button>
                                  </div>
                                );
                              })}
                              <input
                                type="text"
                                className="flex-1 border-0 outline-0 bg-transparent min-w-[120px]"
                                placeholder={
                                  selectedLocations.length === 0
                                    ? "Click to select location..."
                                    : ""
                                }
                                value={locationSearchTerm || ""}
                                onChange={(e) =>
                                  setLocationSearchTerm(e.target.value)
                                }
                                onFocus={() => setShowLocationDropdown(true)}
                                onBlur={() =>
                                  setTimeout(
                                    () => setShowLocationDropdown(false),
                                    200,
                                  )
                                }
                                disabled={!clientLocations.length}
                              />
                            </div>
                            {/* Dropdown for search results */}
                            {showLocationDropdown && (
                              <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-40 overflow-y-auto">
                                {clientLocations
                                  .filter(
                                    (loc) =>
                                      (locationSearchTerm === "" ||
                                        (loc.name || loc.street || "")
                                          .toLowerCase()
                                          .includes(
                                            locationSearchTerm.toLowerCase(),
                                          )),
                                  )
                                  .map((loc) => (
                                    <div
                                      key={loc.location_id}
                                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                                      onClick={() => {
                                        const newLocations = [
                                          String(loc.location_id),
                                        ];
                                        console.log(
                                          "DEBUG ADD - Location selection:",
                                          {
                                            adding_loc_id: loc.location_id,
                                            adding_loc_name: loc.name,
                                            old_selectedLocations:
                                              selectedLocations,
                                            new_selectedLocations: newLocations,
                                            setting_client_location_id:
                                              String(loc.location_id),
                                          },
                                        );
                                        setSelectedLocations(newLocations);
                                        setEditableRequest((r) => ({
                                          ...r,
                                          client_location_id:
                                            String(loc.location_id),
                                        }));
                                        setLocationSearchTerm("");
                                        setShowLocationDropdown(false);
                                      }}
                                    >
                                      {loc.name ||
                                        loc.street ||
                                        `Location #${loc.location_id}`}
                                      {loc.primary ? " (primary)" : ""}
                                    </div>
                                  ))}
                                {clientLocations.filter(
                                  (loc) =>
                                    locationSearchTerm === "" ||
                                    (loc.name || loc.street || "")
                                      .toLowerCase()
                                      .includes(locationSearchTerm.toLowerCase()),
                                ).length === 0 && (
                                  <div className="px-3 py-2 text-gray-500">
                                    {locationSearchTerm === ""
                                      ? "No locations found"
                                      : "No locations found"}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Default Shift Contact*
                          </label>
                          <WrappingSelect
                            value={editableRequest?.contact_account_id || ""}
                            options={requestContactOptions}
                            placeholder={
                              !selectedLocations.length &&
                              !editableRequest?.client_location_id
                                ? "Select a location first"
                                : "Select a contact"
                            }
                            onChange={(val) =>
                              setEditableRequest((r) => ({
                                ...r,
                                contact_account_id:
                                  val && !isNaN(Number(val)) ? String(val) : "",
                              }))
                            }
                            disabled={
                              (!selectedLocations.length &&
                                !editableRequest?.client_location_id) ||
                              (!contacts.length &&
                                !editableRequest?.contact_account_id)
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Default Shift Supervisor*
                          </label>
                          <WrappingSelect
                            value={editableRequest?.supervisor_contact_account_id || ""}
                            options={requestSupervisorOptions}
                            placeholder={
                              !selectedLocations.length &&
                              !editableRequest?.client_location_id
                                ? "Select a location first"
                                : "Select a supervisor"
                            }
                            onChange={(val) =>
                              setEditableRequest((r) => ({
                                ...r,
                                supervisor_contact_account_id:
                                  val && !isNaN(Number(val)) ? String(val) : "",
                              }))
                            }
                            disabled={
                              (!selectedLocations.length &&
                                !editableRequest?.client_location_id) ||
                              (!supervisors.length &&
                                !editableRequest?.supervisor_contact_account_id)
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Default Shift Division*
                          </label>
                          <select
                            className="w-full border rounded px-3 py-2 bg-white"
                            value={editableRequest?.division_id ?? ""}
                            onChange={(e) => {
                              const id = e.target.value;
                              setEditableRequest((r) => ({
                                ...r,
                                division_id:
                                  id && !isNaN(Number(id)) ? String(id) : "",
                              }));
                            }}
                            disabled={
                              !divisions.length && !editableRequest?.division_id
                            }
                          >
                            <option value="">Select a division</option>
                            {divisions.map((d, idx) => (
                              <option
                                key={
                                  d.id !== undefined
                                    ? String(d.id)
                                    : `division-unknown-${idx}`
                                }
                                value={d.id !== undefined ? String(d.id) : ""}
                              >
                                {d.name || `Division #${d.id ?? ""}`}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Notes
                        </label>
                        <textarea
                          className="w-full border rounded px-3 py-2 h-24 resize-none"
                          placeholder="Add notes about this staffing request..."
                          value={editableRequest?.notes || ""}
                          onChange={(e) =>
                            setEditableRequest((r) => ({
                              ...r,
                              notes: e.target.value,
                            }))
                          }
                        />
                        <div className="text-sm text-gray-500 mt-1">0/100</div>
                      </div>

                      <div className="flex justify-end space-x-3 pt-4 border-t">
                        <button
                          type="button"
                          onClick={() =>
                            openReplicateRequestModal(
                              selectedRequest,
                              embedReplicateWeekWindow,
                            )
                          }
                          className="px-4 py-2 rounded border border-red-200 text-red-600 hover:bg-red-50"
                        >
                          Replicate
                        </button>
                        <button
                          type="button"
                          onClick={() => closeStaffingRequestPanel()}
                          className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700"
                        >
                          Close
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              // Validate required dropdowns: location, contact, supervisor, division
                              const missing = [];
                              if (!editableRequest?.client_location_id)
                                missing.push("Location");
                              if (!editableRequest?.contact_account_id)
                                missing.push("Contact");
                              if (
                                !editableRequest?.supervisor_contact_account_id
                              )
                                missing.push("Supervisor");
                              if (!editableRequest?.division_id)
                                missing.push("Division");
                              if (missing.length) {
                                showError(
                                  `Please select: ${missing.join(", ")}`,
                                );
                                return;
                              }

                              // DEBUG: Log location data before save
                              console.log("DEBUG SAVE - Location data:", {
                                selectedLocations,
                                editableRequest_client_location_id:
                                  editableRequest.client_location_id,
                                will_send:
                                  selectedLocations.length > 0
                                    ? selectedLocations[0]
                                    : editableRequest.client_location_id,
                              });

                              const updateBody = {
                                notes: editableRequest.notes,
                                client_location_id: clean(
                                  selectedLocations.length > 0
                                    ? selectedLocations[0]
                                    : editableRequest.client_location_id,
                                ),
                                contact_account_id: clean(
                                  editableRequest.contact_account_id,
                                ),
                                supervisor_contact_account_id: clean(
                                  editableRequest.supervisor_contact_account_id,
                                ),
                                division_id: clean(editableRequest.division_id),
                              };

                              const res = await fetch(
                                `${BASE_URL}/api/jobs/staffingrequests/${selectedRequest.id}`,
                                {
                                  method: "PUT",
                                  headers: {
                                    "Content-Type": "application/json",
                                  },
                                  body: JSON.stringify(updateBody),
                                },
                              );
                              const data = await res.json();
                              if (!res.ok || !data.success) {
                                throw new Error(
                                  data.message ||
                                    "Failed to update staffing request",
                                );
                              }
                              showSuccess(
                                "Staffing request updated successfully",
                              );
                              // Refresh the list
                              const params = new URLSearchParams();
                              params.set("page", String(currentPage));
                              params.set("limit", String(rowsPerPage));
                              params.set(
                                "search",
                                normalizeText(
                                  searchTermByTab.STAFFINGREQUESTS || "",
                                ),
                              );
                              const refreshRes = await fetch(
                                `${BASE_URL}/api/jobs/staffingrequests?${params.toString()}`,
                              );
                              const refreshData = await refreshRes.json();
                              setData(refreshData.data || []);

                              // Fetch the updated single staffing request to ensure details reflect saved values
                              try {
                                const singleRes = await fetch(
                                  `${BASE_URL}/api/jobs/staffingrequests/${selectedRequest.id}`,
                                );
                                const singleData = await singleRes.json();
                                const updated = singleData?.data || singleData;
                                if (updated) setSelectedRequest(updated);
                              } catch (err) {
                                console.error(
                                  "Failed to fetch updated staffing request:",
                                  err,
                                );
                              }
                            } catch (err) {
                              showError(
                                err.message ||
                                  "Failed to update staffing request",
                              );
                            }
                          }}
                          className="px-4 py-2 rounded bg-sidebar-active hover:bg-red-600 text-white font-medium"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : activeSidebarTab === "Shifts" ? (
                    <div className="space-y-4 flex flex-col h-full">
                      <div
                        className={`min-w-0 max-w-full ${staffingRequestShiftsFilterPanel.contentOffsetClass}`}
                      >
                        <ShiftsSummaryStatRow
                          compact={staffingRequestShiftsFilterPanel.isOpen}
                          shiftsCount={staffingRequestShiftsSummary.shiftsCount}
                          actualHours={staffingRequestShiftsSummary.actualHours}
                          submittedPercent={
                            staffingRequestShiftsSummary.submittedPercent
                          }
                          conflictCount={
                            staffingRequestShiftsSummary.conflictCount
                          }
                        />
                      <AtlasListToolbar
                        searchValue={shiftsSearchQuery}
                        onSearchChange={setShiftsSearchQuery}
                        searchPlaceholder="Search shifts..."
                        onFilterClick={staffingRequestShiftsFilterPanel.toggle}
                        filterOpen={staffingRequestShiftsFilterPanel.isOpen}
                        onRefresh={() => {
                          if (selectedRequest) {
                            fetchShiftsForRequest(selectedRequest.id);
                          }
                        }}
                        resultsMeta={{
                          startRow: filteredShifts.length > 0 ? 1 : 0,
                          endRow: filteredShifts.length,
                          totalCount: shiftsData?.shifts?.length || 0,
                          pageSize: filteredShifts.length || 0,
                          pageSizeOptions: [filteredShifts.length || 0],
                          onPageSizeChange: () => {},
                          sortBy: getShiftsSortBy(),
                          sortOrder: shiftsFilters.sortDirection || "asc",
                          sortOptions: canReadShiftActual
                            ? JOBS_SHIFTS_SORT_OPTIONS
                            : JOBS_SHIFTS_SORT_OPTIONS.filter(
                                (o) =>
                                  o.value !== "actuals_submission_status",
                              ),
                          onSortByChange: (value) =>
                            handleShiftsFilterChange("sortBy", value),
                          onSortOrderToggle: () =>
                            handleShiftsFilterChange(
                              "sortDirection",
                              String(
                                shiftsFilters.sortDirection || "asc",
                              ).toLowerCase() === "asc"
                                ? "desc"
                                : "asc",
                            ),
                        }}
                        actions={
                          <>
                            {selectedShiftIds.length > 0 && (
                              <div className="relative" ref={shiftActionsMenuRef}>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setShowShiftActionsMenu((prev) => !prev)
                                  }
                                  className="px-4 py-2 bg-sidebar-active text-white rounded hover:bg-red-600"
                                >
                                  ACTIONS
                                </button>
                                {showShiftActionsMenu && (
                                  <div className="absolute left-0 mt-2 w-72 bg-white border border-gray-200 rounded shadow-lg z-20">
                                    <button
                                      type="button"
                                      className="w-full text-left px-4 py-2 hover:bg-gray-50"
                                      onClick={() => {
                                        setShowShiftActionsMenu(false);
                                        setBulkShiftScope("request");
                                        setShowBulkShiftUpdateModal(true);
                                      }}
                                    >
                                      Bulk shift update
                                    </button>
                                    <button
                                      type="button"
                                      className="w-full text-left px-4 py-2 hover:bg-gray-50"
                                      onClick={() => {
                                        setShowShiftActionsMenu(false);
                                        setBulkShiftScope("request");
                                        setShowBulkDivisionPositionModal(true);
                                      }}
                                    >
                                      Bulk shift division and position update
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                            <button
                              onClick={() => setShowCreateShiftModal(true)}
                              className="px-4 py-2 bg-sidebar-active text-white rounded hover:bg-red-600"
                            >
                              Add Shift
                            </button>
                          </>
                        }
                        chipBar={
                          staffingRequestShiftsFilterChips.length > 0 ? (
                            <AtlasFilterChipBar
                              chips={staffingRequestShiftsFilterChips}
                              onChipClick={(chip) => {
                                if (chip.fieldId) {
                                  staffingRequestShiftsFilterPanel.focusField(
                                    chip.fieldId,
                                  );
                                } else {
                                  staffingRequestShiftsFilterPanel.open();
                                }
                              }}
                              onRemove={(chip) => removeShiftsFilter(chip.key)}
                              onClearAll={resetShiftsFilters}
                            />
                          ) : null
                        }
                      />

                      <AtlasFilterPanel
                        isOpen={staffingRequestShiftsFilterPanel.isOpen}
                        onClose={staffingRequestShiftsFilterPanel.close}
                        onClear={() => {
                          resetShiftsFilters();
                          staffingRequestShiftsFilterPanel.close();
                        }}
                        activeCount={staffingRequestShiftsFilterChips.length}
                        title="Shifts Filters"
                        stackZIndex={embedMode ? 50520 : 60}
                      >
                        {renderShiftsFilterFields(staffingRequestShiftsFilterPanel)}
                      </AtlasFilterPanel>

                      {selectedShiftIds.length > 0 && (
                        <div className="text-sm text-gray-600">
                          {selectedShiftIds.length} of {filteredShifts.length}{" "}
                          selected
                        </div>
                      )}

                      <div className="flex-1 flex flex-col min-h-0 min-w-0">
                        {filteredShifts.length > 0 ? (
                          <div className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-auto no-mobile-table-stack">
                            <table className="w-full table-auto text-sm min-w-[500px]">
                              <thead>
                                <tr className="bg-gray-50 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                                  <th className="px-2 py-1.5 w-8">
                                    <input
                                      type="checkbox"
                                      checked={allVisibleShiftsSelected}
                                      onChange={(e) =>
                                        toggleSelectAllVisibleShifts(
                                          e.target.checked,
                                        )
                                      }
                                    />
                                  </th>
                                  <th className="px-2 py-1.5 w-auto">Position</th>
                                  <th className="px-2 py-1.5">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleShiftsFilterChange(
                                          "sortDirection",
                                          String(
                                            shiftsFilters.sortDirection || "",
                                          ).toLowerCase() === "asc"
                                            ? "desc"
                                            : "asc",
                                        )
                                      }
                                      className="inline-flex items-center gap-1"
                                      title="Sort time"
                                    >
                                      <span>Time</span>
                                      <span>
                                        {String(
                                          shiftsFilters.sortDirection || "asc",
                                        ).toLowerCase() === "asc"
                                          ? "↑"
                                          : "↓"}
                                      </span>
                                    </button>
                                  </th>
                                  <th className="px-2 py-1.5 w-9"></th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {filteredShifts.map((shift) => (
                                  <tr key={shift.shift_id} className="text-sm">
                                    <td className="px-2 py-1">
                                      <input
                                        type="checkbox"
                                        checked={selectedShiftIds.includes(
                                          String(shift.shift_id),
                                        )}
                                        onChange={(e) =>
                                          toggleShiftSelection(
                                            shift.shift_id,
                                            e.target.checked,
                                          )
                                        }
                                      />
                                    </td>
                                    <td className="px-2 py-1 overflow-hidden">
                                      <button
                                        type="button"
                                        onClick={() => openShiftOverview(shift)}
                                        className="text-red-600 hover:text-red-700 hover:underline truncate block max-w-full text-left"
                                        title={shift.position}
                                      >
                                        {shift.position}
                                      </button>
                                    </td>
                                    <td
                                      className="px-2 py-1 truncate text-xs"
                                      title={`${formatStaffingRequestShiftTime(
                                        shift.start_date,
                                        shift.end_date,
                                      )}`}
                                    >
                                      {formatStaffingRequestShiftTime(
                                        shift.start_date,
                                        shift.end_date,
                                      )}
                                    </td>
                                    <td className="px-1 py-1">
                                      <ActionDropdown
                                        menuZIndexClass={
                                          embedMode
                                            ? jobsEmbedOverlayZ
                                            : "z-[9999]"
                                        }
                                        actions={[
                                          {
                                            label: "View related:",
                                            disabled: true,
                                          },
                                          {
                                            label: "	Staff request",
                                            isSubOption: true,
                                            onClick: () => {
                                              // Navigate to Details tab of the same staffing request
                                              setActiveSidebarTab("Details");
                                            },
                                          },
                                          {
                                            label: "	Client",
                                            isSubOption: true,
                                            onClick: () => {
                                              // Navigate to client details using existing ClientDetailModal
                                              const clientId =
                                                selectedRequest?.client_id ||
                                                editableRequest?.client_id;
                                              const clientName =
                                                selectedRequest?.clientName ||
                                                editableRequest?.clientName;
                                              if (clientId) {
                                                setSelectedClientForModal({
                                                  id: clientId,
                                                  name: clientName,
                                                });
                                                setShowClientModal(true);
                                              } else {
                                                showError(
                                                  "No client information available for this shift",
                                                );
                                              }
                                            },
                                          },
                                          {
                                            label: "View in Schedule",
                                            onClick: () => {
                                              console.log(
                                                "View shift in schedule:",
                                                shift.shift_id,
                                              );
                                              // Navigate to schedule page with shift highlighting
                                              navigate(
                                                `/schedule?shiftId=${shift.shift_id}&date=${shift.start_date}`,
                                              );
                                            },
                                          },
                                          {
                                            label: "Duplicate",
                                            onClick: () =>
                                              handleDuplicateShift(shift),
                                          },
                                          {
                                            label: "Delete",
                                            onClick: () =>
                                              handleDeleteShift(shift.shift_id),
                                            danger: true,
                                          },
                                        ]}
                                      />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : shiftsData?.shifts?.length > 0 ? (
                          <div className="flex-1 flex items-center justify-center text-gray-500">
                            No shifts match the current filters.
                          </div>
                        ) : (
                          <div className="flex-1 flex items-center justify-center text-gray-500">
                            No shifts found for this staffing request.
                          </div>
                        )}
                      </div>
                      </div>
                    </div>
                  ) : activeSidebarTab === "Audits" ? (
                    <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                      <RichAuditsPanel
                        subjectId={selectedRequest?.id}
                        fetchUrl={
                          selectedRequest?.id
                            ? `${BASE_URL}/api/jobs/staffingrequests/${selectedRequest.id}/audits/rich`
                            : ""
                        }
                        title="Audit History"
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {ReplicateModal}

      {/* Create Staffing Request Modal */}
      {showCreateRequestModal && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 ${jobsOverlayZ} flex items-center justify-center p-4`}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              attemptCloseCreateRequestModal();
            }
          }}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">
                  Create Staffing Request
                </h2>
                <button
                  onClick={attemptCloseCreateRequestModal}
                  className="text-gray-500 hover:text-gray-800"
                >
                  ✖
                </button>
              </div>

              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client *
                  </label>
                  <ClickableDropdown
                    value={createClientId}
                    onChange={(clientId) => {
                      setCreateRequestDirty(true);
                      setCreateClientId(clientId || "");
                      setCreateSelectedLocationIds([]);
                      setCreateContactId("");
                      setCreateSupervisorId("");
                      setCreateDivisionId("");
                      setCreateErrors((prev) => ({ ...prev, client: "" }));
                    }}
                    options={createClientOptions}
                    placeholder="Select client"
                    className={`w-full !px-3 !py-2 !text-sm ${
                      createErrors.client ? "!border-red-500" : ""
                    }`}
                    searchable={true}
                    clearable={false}
                  >
                    <option value="">Select a client</option>
                    {clients.map((client) => (
                      <option key={String(client.id)} value={String(client.id)}>
                        {client.name}
                      </option>
                    ))}
                  </ClickableDropdown>
                  {createErrors.client && (
                    <div className="text-sm text-gray-700 mt-1">
                      {createErrors.client}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location *
                  </label>
                  <div
                    className="relative location-search-container"
                    ref={createLocationDropdownRef}
                  >
                    <div className="w-full border rounded px-3 py-2 bg-white min-h-[40px] flex flex-wrap items-center gap-2 focus-within:ring-2 focus-within:ring-blue-500">
                      {/* Selected locations as chips inside input */}
                      {createSelectedLocationIds.map((locId) => {
                        const location = createClientLocations.find(
                          (l) => String(l.location_id) === String(locId),
                        );
                        return (
                          <div
                            key={locId}
                            className="flex items-center gap-1 bg-gray-300 text-black px-2 py-1 rounded text-sm"
                          >
                            <span>
                              {location?.name || `Location #${locId}`}
                              {location?.primary ? " (primary)" : ""}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setCreateRequestDirty(true);
                                setCreateSelectedLocationIds((prev) =>
                                  prev.filter((id) => id !== locId),
                                );
                                setCreateErrors((prev) => ({
                                  ...prev,
                                  location: "",
                                }));
                              }}
                              className="hover:bg-gray-400 rounded px-1 ml-1"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                      <input
                        type="text"
                        className="flex-1 border-0 outline-0 bg-transparent min-w-[120px]"
                        placeholder={
                          createSelectedLocationIds.length === 0
                            ? "Click to select locations..."
                            : ""
                        }
                        value={createLocationSearchTerm || ""}
                        onChange={(e) =>
                          {
                            setCreateRequestDirty(true);
                            setCreateLocationSearchTerm(e.target.value);
                          }
                        }
                        onFocus={() => setShowCreateLocationDropdown(true)}
                        disabled={!createClientId}
                      />
                    </div>
                    {/* Dropdown for search results */}
                    {showCreateLocationDropdown && (
                      <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-40 overflow-y-auto">
                        {createClientLocations
                          .filter(
                            (loc) =>
                              !createSelectedLocationIds.includes(
                                String(loc.location_id),
                              ) &&
                              (createLocationSearchTerm === "" ||
                                (loc.name || "")
                                  .toLowerCase()
                                  .includes(
                                    createLocationSearchTerm.toLowerCase(),
                                  )),
                          )
                          .sort((a, b) => {
                            if (a.primary && !b.primary) return -1;
                            if (!a.primary && b.primary) return 1;
                            const nameA = a.name || "";
                            const nameB = b.name || "";
                            return nameA.localeCompare(nameB);
                          })
                          .map((location) => (
                            <div
                              key={String(location.location_id)}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                              onClick={() => {
                                // Only allow one location - replace instead of adding
                                setCreateRequestDirty(true);
                                setCreateSelectedLocationIds([
                                  String(location.location_id),
                                ]);
                                setCreateErrors((prev) => ({
                                  ...prev,
                                  location: "",
                                }));
                                setCreateLocationSearchTerm("");
                                setShowCreateLocationDropdown(false);
                              }}
                            >
                              {location.name}
                              {location.primary ? " (primary)" : ""}
                            </div>
                          ))}
                        {createClientLocations.filter(
                          (loc) =>
                            !createSelectedLocationIds.includes(
                              String(loc.location_id),
                            ) &&
                            (createLocationSearchTerm === "" ||
                              (loc.name || "")
                                .toLowerCase()
                                .includes(
                                  createLocationSearchTerm.toLowerCase(),
                                )),
                        ).length === 0 && (
                          <div className="px-3 py-2 text-gray-500">
                            {createLocationSearchTerm === ""
                              ? "All locations selected"
                              : "No locations found"}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {createErrors.location && (
                    <div className="text-sm text-gray-700 mt-1">
                      {createErrors.location}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Default Shift Contact *
                  </label>
                  <select
                    value={createContactId}
                    onChange={(e) => {
                      setCreateRequestDirty(true);
                      setCreateContactId(e.target.value);
                      setCreateErrors((prev) => ({ ...prev, contact: "" }));
                    }}
                    className={`w-full border rounded px-3 py-2 bg-white ${createErrors.contact ? "border-red-500" : ""}`}
                    disabled={
                      !createClientId ||
                      createSelectedLocationIds.length === 0 ||
                      createDefaultsLoading
                    }
                    required
                  >
                    {!createClientId ? (
                      <option value="">Select a client first</option>
                    ) : createSelectedLocationIds.length === 0 ? (
                      <option value="">Select a location first</option>
                    ) : createDefaultsLoading ? (
                      <option value="">Loading contacts...</option>
                    ) : createContacts.length === 0 ? (
                      <option value="">
                        No contacts available for this client
                      </option>
                    ) : (
                      <>
                        <option value="">Select a contact</option>
                        {createContacts.map((c) => {
                          const aid = getClientContactRowAccountId(c);
                          return (
                          <option
                            key={String(aid ?? c.first_name + c.last_name)}
                            value={aid != null ? String(aid) : ""}
                          >
                            {`${c.first_name || ""} ${c.last_name || ""}`.trim() ||
                              c.name ||
                              `Contact #${aid ?? ""}`}
                          </option>
                        );
                        })}
                      </>
                    )}
                  </select>
                  {createErrors.contact && (
                    <div className="text-sm text-red-600 mt-1">
                      {createErrors.contact}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Default Shift Supervisor *
                  </label>
                  <select
                    value={createSupervisorId}
                    onChange={(e) => {
                      setCreateRequestDirty(true);
                      setCreateSupervisorId(e.target.value);
                      setCreateErrors((prev) => ({ ...prev, supervisor: "" }));
                    }}
                    className={`w-full border rounded px-3 py-2 bg-white ${createErrors.supervisor ? "border-red-500" : ""}`}
                    disabled={
                      !createClientId ||
                      createSelectedLocationIds.length === 0 ||
                      createDefaultsLoading
                    }
                    required
                  >
                    {!createClientId ? (
                      <option value="">Select a client first</option>
                    ) : createSelectedLocationIds.length === 0 ? (
                      <option value="">Select a location first</option>
                    ) : createDefaultsLoading ? (
                      <option value="">Loading supervisors...</option>
                    ) : createSupervisors.length === 0 ? (
                      <option value="">
                        No contacts available for this client
                      </option>
                    ) : (
                      <>
                        <option value="">Select a supervisor</option>
                        {dedupedCreateSupervisors.map((s) => {
                          const aid = getClientContactRowAccountId(s);
                          return (
                          <option
                            key={String(aid ?? `${s.first_name}-${s.last_name}`)}
                            value={aid != null ? String(aid) : ""}
                          >
                            {`${s.first_name || ""} ${s.last_name || ""}`.trim() ||
                              s.name ||
                              `Supervisor #${aid ?? ""}`}
                          </option>
                        );
                        })}
                      </>
                    )}
                  </select>
                  {createErrors.supervisor && (
                    <div className="text-sm text-red-600 mt-1">
                      {createErrors.supervisor}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={createNotes}
                    onChange={(e) => {
                      setCreateRequestDirty(true);
                      setCreateNotes(e.target.value);
                    }}
                    className="w-full border rounded px-3 py-2 h-20 resize-none"
                    placeholder="Add notes about this staffing request..."
                    maxLength={60}
                  />
                  <div className="text-sm text-gray-500 mt-1">
                    {createNotes.length}/60 characters
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Default Shift Division *
                  </label>
                  <select
                    value={createDivisionId}
                    onChange={(e) => {
                      setCreateRequestDirty(true);
                      setCreateDivisionId(e.target.value);
                      setCreateErrors((prev) => ({ ...prev, division: "" }));
                    }}
                    className={`w-full border rounded px-3 py-2 bg-white ${createErrors.division ? "border-red-500" : ""}`}
                    disabled={!createClientId}
                    required
                  >
                    <option value="">Select a division</option>
                    {createDivisions.map((d) => (
                      <option key={String(d.id)} value={String(d.id)}>
                        {d.name || `Division #${d.id}`}
                      </option>
                    ))}
                  </select>
                  {createErrors.division && (
                    <div className="text-sm text-red-600 mt-1">
                      {createErrors.division}
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={attemptCloseCreateRequestModal}
                    className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    ref={createRequestSubmitButtonRef}
                    className="px-4 py-2 rounded bg-sidebar-active hover:bg-red-600 text-white font-medium"
                    onClick={async (e) => {
                      e.preventDefault();
                      if (!validateCreateRequestForm()) {
                        return;
                      }
                      try {
                        const selectedCreateContact = createContacts.find(
                          (c) =>
                            String(getAccountOptionId(c) || "") ===
                            String(createContactId || ""),
                        );
                        const selectedCreateSupervisor = createSupervisors.find(
                          (s) =>
                            String(getAccountOptionId(s) || "") ===
                            String(createSupervisorId || ""),
                        );
                        const selectedCreateDivision = createDivisions.find(
                          (d) => String(d?.id || "") === String(createDivisionId || ""),
                        );
                        const tempCreateRequestDefaults = {
                          contact_account_id: createContactId
                            ? String(createContactId)
                            : "",
                          contact: getAccountOptionLabel(
                            selectedCreateContact,
                            "Contact",
                          ),
                          supervisor_contact_account_id: createSupervisorId
                            ? String(createSupervisorId)
                            : "",
                          supervisor: getAccountOptionLabel(
                            selectedCreateSupervisor,
                            "Supervisor",
                          ),
                          division_id: createDivisionId
                            ? String(createDivisionId)
                            : "",
                          division: String(
                            selectedCreateDivision?.name ||
                              selectedCreateDivision?.label ||
                              "",
                          ),
                        };
                        const res = await fetch(
                          `${BASE_URL}/api/jobs/staffingrequests`,
                          {
                            method: "POST",
                            headers: {
                              "Content-Type": "application/json",
                              ...(localStorage.getItem("token") ||
                              localStorage.getItem("authToken")
                                ? {
                                    Authorization: `Bearer ${localStorage.getItem("token") || localStorage.getItem("authToken")}`,
                                  }
                                : {}),
                            },
                            body: JSON.stringify({
                              clientId: createClientId,
                              locationId: createSelectedLocationIds[0] || null,
                              contactAccountId: createContactId || null,
                              supervisorAccountId: createSupervisorId || null,
                              divisionId: createDivisionId || null,
                              notes: createNotes,
                            }),
                          },
                        );
                        const data = await res.json();
                        if (!res.ok || !data.success) {
                          throw new Error(
                            data.message || "Failed to create staffing request",
                          );
                        }
                        showSuccess("Staffing request created");
                        // Try to use returned created object if present so Created time shows immediately
                        const createdObj =
                          data.data ||
                          data.staffingRequest ||
                          data.record ||
                          (data.success && data.id ? { id: data.id } : null) ||
                          data;
                        if (
                          createdObj &&
                          typeof createdObj === "object" &&
                          !Array.isArray(createdObj)
                        ) {
                          createdObj.contact_account_id =
                            createdObj.contact_account_id ||
                            tempCreateRequestDefaults.contact_account_id ||
                            "";
                          createdObj.contact =
                            createdObj.contact ||
                            tempCreateRequestDefaults.contact ||
                            "";
                          createdObj.contactName =
                            createdObj.contactName ||
                            createdObj.contact ||
                            tempCreateRequestDefaults.contact ||
                            "";
                          createdObj.supervisor_contact_account_id =
                            createdObj.supervisor_contact_account_id ||
                            tempCreateRequestDefaults.supervisor_contact_account_id ||
                            "";
                          createdObj.supervisor =
                            createdObj.supervisor ||
                            createdObj.supervisorName ||
                            tempCreateRequestDefaults.supervisor ||
                            "";
                          createdObj.supervisorName =
                            createdObj.supervisorName ||
                            createdObj.supervisor ||
                            tempCreateRequestDefaults.supervisor ||
                            "";
                          createdObj.division_id =
                            createdObj.division_id ||
                            tempCreateRequestDefaults.division_id ||
                            "";
                          createdObj.division =
                            createdObj.division ||
                            createdObj.divisionName ||
                            createdObj.division_name ||
                            tempCreateRequestDefaults.division ||
                            "";
                          createdObj.divisionName =
                            createdObj.divisionName ||
                            createdObj.division_name ||
                            createdObj.division ||
                            tempCreateRequestDefaults.division ||
                            "";
                          createdObj.client_id =
                            createdObj.client_id ?? createClientId ?? "";
                          if (createSelectedLocationIds[0]) {
                            createdObj.client_location_id =
                              createdObj.client_location_id ??
                              String(createSelectedLocationIds[0]);
                          }
                          const createdClientRow = clients.find(
                            (c) => String(c.id) === String(createClientId),
                          );
                          if (createdClientRow?.name) {
                            createdObj.clientName =
                              createdObj.clientName || createdClientRow.name;
                          }
                        }
                        setShowCreateRequestModal(false);
                        resetCreateRequestForm();
                        // If we have the created object with enough info, open its detail view so Created appears
                        if (
                          createdObj &&
                          (createdObj.id ||
                            createdObj.staffing_request_id ||
                            createdObj.time ||
                            createdObj.created_at)
                        ) {
                          // Normalize id field name
                          const createdId =
                            createdObj.id ||
                            createdObj.staffing_request_id ||
                            createdObj.staffingRequestId ||
                            createdObj.staffing_request_id ||
                            null;
                          // If createdObj seems to be the full record, use it; otherwise fetch the single record by id
                          const extractTime = (obj) =>
                            obj &&
                            (obj.time ||
                              obj.created_at ||
                              obj.created ||
                              obj.createdAt ||
                              obj.timestamp ||
                              obj.time_created ||
                              obj.created_on ||
                              obj.time_stamp);
                          if (extractTime(createdObj)) {
                            // Ensure `time` field exists for the UI
                            createdObj.time =
                              createdObj.time ||
                              createdObj.created_at ||
                              createdObj.created ||
                              createdObj.createdAt ||
                              createdObj.timestamp ||
                              createdObj.time_created ||
                              createdObj.created_on ||
                              createdObj.time_stamp;
                            setSelectedRequest(createdObj);
                          } else if (createdId) {
                            try {
                              const singleRes = await fetch(
                                `${BASE_URL}/api/jobs/staffingrequests/${createdId}`,
                              );
                              const singleData = await singleRes.json();
                              const fetched = singleData?.data || singleData;
                              if (fetched) {
                                if (!extractTime(fetched))
                                  fetched.time = formatAppDateTime(new Date());
                                fetched.contact_account_id =
                                  fetched.contact_account_id ||
                                  tempCreateRequestDefaults.contact_account_id ||
                                  "";
                                fetched.contact =
                                  fetched.contact ||
                                  tempCreateRequestDefaults.contact ||
                                  "";
                                fetched.contactName =
                                  fetched.contactName ||
                                  fetched.contact ||
                                  tempCreateRequestDefaults.contact ||
                                  "";
                                fetched.supervisor_contact_account_id =
                                  fetched.supervisor_contact_account_id ||
                                  tempCreateRequestDefaults.supervisor_contact_account_id ||
                                  "";
                                fetched.supervisor =
                                  fetched.supervisor ||
                                  fetched.supervisorName ||
                                  tempCreateRequestDefaults.supervisor ||
                                  "";
                                fetched.supervisorName =
                                  fetched.supervisorName ||
                                  fetched.supervisor ||
                                  tempCreateRequestDefaults.supervisor ||
                                  "";
                                fetched.division_id =
                                  fetched.division_id ||
                                  tempCreateRequestDefaults.division_id ||
                                  "";
                                fetched.division =
                                  fetched.division ||
                                  fetched.divisionName ||
                                  fetched.division_name ||
                                  tempCreateRequestDefaults.division ||
                                  "";
                                fetched.divisionName =
                                  fetched.divisionName ||
                                  fetched.division_name ||
                                  fetched.division ||
                                  tempCreateRequestDefaults.division ||
                                  "";
                                setSelectedRequest(fetched);
                              }
                            } catch (err) {
                              console.error(
                                "Failed to fetch newly created staffing request:",
                                err,
                              );
                              // fallback to using createdObj with local time
                              createdObj.time = formatAppDateTime(new Date());
                              setSelectedRequest(createdObj);
                            }
                          } else {
                            // Fallback: attach local created time so UI shows something
                            createdObj.time = formatAppDateTime(new Date());
                            setSelectedRequest(createdObj);
                          }
                          // Optimistically insert a row into the main table so the Time column shows immediately
                          try {
                            const createdId =
                              createdObj.id ||
                              createdObj.staffing_request_id ||
                              (data && data.id) ||
                              null;
                            const clientObj = clients.find(
                              (c) => String(c.id) === String(createClientId),
                            );
                            const locationObj = createClientLocations.find(
                              (l) =>
                                String(l.id) ===
                                  String(createSelectedLocationIds[0]) ||
                                String(l.location_id) ===
                                  String(createSelectedLocationIds[0]),
                            );
                            const contactObj = createContacts.find(
                              (c) => String(c.id) === String(createContactId),
                            );
                            const newRow = {
                              id:
                                createdId ||
                                (Math.random() * 1000000).toFixed(0),
                              clientName: clientObj?.name || "",
                              clientLocationName:
                                locationObj?.name || locationObj?.street || "",
                              contact: contactObj
                                ? `${contactObj.first_name || ""} ${contactObj.last_name || ""}`.trim()
                                : "",
                              time:
                                createdObj.time ||
                                createdObj.created_at ||
                                formatAppDateTime(new Date()),
                              shifts: 0,
                              notes: createNotes || "",
                            };
                            setData((prev) => [newRow, ...(prev || [])]);
                            setTotalCount((prev) => Number(prev || 0) + 1);
                          } catch (err) {
                            console.error(
                              "Failed to optimistically add created row:",
                              err,
                            );
                          }
                        }
                        // Refresh table
                        setCurrentPage(1);
                        // Re-fetch current tab
                        (async () => {
                          try {
                            const endpoint =
                              selectedTab === "STAFFINGREQUESTS"
                                ? "staffingrequests"
                                : selectedTab === "SHIFTS"
                                  ? "shifts"
                                  : selectedTab === "TIMESHEETS"
                                    ? "timesheets"
                                    : "shiftassignments";
                            const resp = await fetch(
                              `${BASE_URL}/api/jobs/${endpoint}?page=1&limit=${rowsPerPage}&search=${searchTerm}`,
                            );
                            const result = await resp.json();
                            setData(result.data || []);
                            setTotalPages(result.totalPages || 1);
                          } catch {}
                        })();
                      } catch (err) {
                        showError(
                          err.message || "Failed to create staffing request",
                        );
                      }
                    }}
                  >
                    Create Request
                  </button>
                </div>
              </form>
            </div>
          </div>
          <UnsavedChangesModal
            open={showCreateRequestUnsavedPopup}
            onSave={handleCreateRequestSaveContinue}
            onDiscard={handleCreateRequestDiscardChanges}
            onReview={handleCreateRequestReviewChanges}
          />
          {showCreateRequestValidationAlert && (
            <div className="fixed bottom-5 right-5 z-[120] rounded bg-[#f47d6b] px-5 py-4 text-sm font-medium text-white shadow-xl">
              Please review errors highlighted in red
            </div>
          )}
        </div>
      )}

      {/* Timesheet Drawer & Modal */}
      {/* <TimesheetDrawer
        open={tsDrawerOpen}
        onClose={() => setTsDrawerOpen(false)}
        record={tsRecord}
        onSave={(payload) => {
          setTsDrawerOpen(false);
          showSuccess("Saved (mock)");
        }}
      /> */}
      {/* <TimesheetModal
        open={tsModalOpen}
        onClose={() => setTsModalOpen(false)}
        onSubmit={(reason) => {
          setTsModalOpen(false);
          showError(`Rejected: ${reason || "No reason"}`);
        }}
      /> */}

      {/* Filter Drawer */}
      {showBulkShiftUpdateModal && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 ${jobsOverlayZ} flex items-center justify-center p-4`}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-medium">Bulk Update Shifts</h3>
                <button
                  type="button"
                  className="text-gray-500 hover:text-gray-700"
                  onClick={() => {
                    setShowBulkShiftUpdateModal(false);
                    resetBulkShiftUpdateForm();
                  }}
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={bulkShiftUpdateForm.applyStatus}
                    onChange={handleApplyStatusChange}
                  />
                  <label className="text-sm font-medium">Status</label>
                  {bulkShiftUpdateForm.applyStatus && (
                    <select
                      value={bulkShiftUpdateForm.state}
                      onChange={(e) =>
                        setBulkShiftUpdateForm({
                          ...bulkShiftUpdateForm,
                          state: e.target.value,
                        })
                      }
                    >
                      <option value="">Select status</option>
                      {BULK_STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={bulkShiftUpdateForm.applyTimeRange}
                    onChange={(e) =>
                      setBulkShiftUpdateForm((prev) => ({
                        ...prev,
                        applyTimeRange: e.target.checked,
                      }))
                    }
                  />
                  <label className="text-sm font-medium">Start / End</label>
                  <div className="ml-auto flex items-center gap-2">
                   <input
  readOnly
  className="border rounded px-3 py-2 w-48 cursor-pointer"
  value={
    bulkShiftUpdateForm.start_date
      ? formatAppDateTime(bulkShiftUpdateForm.start_date)
      : ""
  }
  placeholder="Select start"
  onClick={() => setBulkPickerType("start")}
  disabled={!bulkShiftUpdateForm.applyTimeRange}
/>
                    <span>to</span>
                   <input
  readOnly
  className="border rounded px-3 py-2 w-48 cursor-pointer"
  value={
    bulkShiftUpdateForm.end_date
      ? formatAppDateTime(bulkShiftUpdateForm.end_date)
      : ""
  }
  placeholder="Select end"
  onClick={() => setBulkPickerType("end")}
  disabled={!bulkShiftUpdateForm.applyTimeRange}
/>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={bulkShiftUpdateForm.applyBreak}
                    onChange={(e) =>
                      setBulkShiftUpdateForm((prev) => ({
                        ...prev,
                        applyBreak: e.target.checked,
                      }))
                    }
                  />
                  <label className="text-sm font-medium">Break</label>
                  <select
                    className="ml-auto border rounded px-3 py-2 w-40"
                    value={
                      bulkShiftUpdateForm.unpaid_break === true
                        ? "yes"
                        : bulkShiftUpdateForm.unpaid_break === false
                          ? "no"
                          : ""
                    }
                    onChange={(e) =>
                      setBulkShiftUpdateForm((prev) => ({
                        ...prev,
                        unpaid_break: e.target.value === "yes",
                      }))
                    }
                    disabled={!bulkShiftUpdateForm.applyBreak}
                  >
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={bulkShiftUpdateForm.notifyTouched}
                    onChange={(e) =>
                      setBulkShiftUpdateForm((prev) => ({
                        ...prev,
                        notifyTouched: e.target.checked,
                        ...(e.target.checked
                          ? {}
                          : { notify_staff_change: false }),
                      }))
                    }
                  />
                  <label className="text-sm font-medium">
                    Staff Requires Notification of Change
                  </label>
                  <select
                    className="ml-auto border rounded px-3 py-2 w-40"
                    value={
                      bulkShiftUpdateForm.notifyTouched
                        ? bulkShiftUpdateForm.notify_staff_change
                          ? "yes"
                          : "no"
                        : ""
                    }
                    onChange={(e) =>
                      setBulkShiftUpdateForm((prev) => ({
                        ...prev,
                        notify_staff_change: e.target.value === "yes",
                        notifyTouched: e.target.value !== "",
                      }))
                    }
                    disabled={!bulkShiftUpdateForm.notifyTouched}
                  >
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-medium mb-2">Selected Shifts</h4>
                <div className="max-h-52 overflow-auto border rounded">
                  <table className="min-w-full">
                    <thead className="bg-gray-50 text-left text-xs text-gray-600">
                      <tr>
                        <th className="px-3 py-2">Shift</th>
                        <th className="px-3 py-2">Scheduled</th>
                        <th className="px-3 py-2">Client & Position</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {selectedShiftRows.map((shift) => {
                        const id = shift?.shift_id ?? shift?.id ?? "";
                        const start = shift?.start_time || shift?.start_time_display || shift?.scheduledLine1 || shift?.scheduled || "";
                        const end = shift?.end_time || shift?.end_time_display || "";
                        const scheduled = start && end ? `${start} - ${end}` : start || end;
                        const clientLabel =
                          selectedRequest?.clientName ||
                          shift?.clientName ||
                          shift?.client ||
                          "";
                        const positionLabel =
                          shift?.position ||
                          shift?.positionName ||
                          shift?.position_name ||
                          "";
                        return (
                          <tr key={String(id)}>
                            <td className="px-3 py-2">{id}</td>
                          <td className="px-3 py-2">
                            {scheduled || "—"}
                          </td>
                          <td className="px-3 py-2">
                            {(clientLabel || "").trim()}{" "}
                            {positionLabel || ""}
                          </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                  onClick={() => {
                    setShowBulkShiftUpdateModal(false);
                    resetBulkShiftUpdateForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded bg-sidebar-active text-white hover:bg-red-600"
                  onClick={handleBulkShiftUpdate}
                >
                  Update
                </button>
              </div>
            </div>
          </div>

          {bulkPickerType && (
      <DateTimePickerPanel
        value={
          bulkPickerType === "start"
            ? bulkShiftUpdateForm.start_date
            : bulkShiftUpdateForm.end_date
        }
        mode={bulkPickerType === "start" ? "from" : "to"}
        onChange={(date) => {
          if (bulkPickerType === "start") {
            setBulkShiftUpdateForm((prev) => ({
              ...prev,
              start_date: date,
              end_date: "",
            }));
          } else {
            setBulkShiftUpdateForm((prev) => ({
              ...prev,
              end_date: date,
            }));
          }
        }}
        onDone={() => setBulkPickerType(null)}
        onContinue={(date) => {
          setBulkShiftUpdateForm((prev) => ({
            ...prev,
            start_date: date,
          }));
          setBulkPickerType("end");
        }}
        onBack={() => setBulkPickerType("start")}
      />
    )}
        </div>

        
      )}

      {showBulkDivisionPositionModal && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 ${jobsOverlayZ} flex items-center justify-center p-4`}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-medium">
                  Bulk Update Shift Division and Position
                </h3>
                <button
                  type="button"
                  className="text-gray-500 hover:text-gray-700"
                  onClick={() => {
                    setShowBulkDivisionPositionModal(false);
                    setBulkDivisionPositionForm({
                      client_id: "",
                      division_id: "",
                      position_id: "",
                    });
                    setBulkClientOptions([]);
                    setBulkModalDivisions([]);
                    setBulkModalPositions([]);
                    setBulkModalAllPositions([]);
                  }}
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client*
                  </label>
                  <ClickableDropdown
                    value={bulkDivisionPositionForm.client_id}
                    onChange={(cid) => {
                      setBulkDivisionPositionForm((prev) => ({
                        ...prev,
                        client_id: cid,
                        division_id: "",
                        position_id: "",
                      }));
                      // clear dependents
                      setBulkModalDivisions([]);
                      setBulkModalPositions([]);
                      setBulkModalAllPositions([]);
                    }}
                    options={bulkClientOptions}
                    placeholder="Select client"
                    className="px-3 py-2 text-sm border-gray-300 rounded"
                    disabled={!bulkClientOptions.length}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Division*
                  </label>
                  <select
                    className="w-full border rounded px-3 py-2"
                    value={bulkDivisionPositionForm.division_id}
                    onChange={(e) =>
                      setBulkDivisionPositionForm((prev) => ({
                        ...prev,
                        division_id: e.target.value,
                        position_id: "",
                      }))
                    }
                    disabled={!bulkModalDivisions.length}
                  >
                    <option value="">Select division</option>
                    {bulkModalDivisions.map((division) => (
                      <option
                        key={String(division.division_id || division.id)}
                        value={String(division.division_id || division.id)}
                      >
                        {division.name || division.division_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Position*
                  </label>
                  <select
                    className="w-full border rounded px-3 py-2"
                    value={bulkDivisionPositionForm.position_id}
                    onChange={(e) =>
                      setBulkDivisionPositionForm((prev) => ({
                        ...prev,
                        position_id: e.target.value,
                      }))
                    }
                    disabled={!bulkModalPositions.length}
                  >
                    <option value="">Select position</option>
                    {bulkModalPositions.map((position) => (
                      <option
                        key={String(
                          position.position_id || position.id || position.posId,
                        )}
                        value={String(
                          position.position_id || position.id || position.posId,
                        )}
                      >
                        {position.name ||
                          position.position_name ||
                          position.posName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-medium mb-2">Selected Shifts</h4>
                <div className="max-h-52 overflow-auto border rounded">
                  <table className="min-w-full">
                    <thead className="bg-gray-50 text-left text-xs text-gray-600">
                      <tr>
                        <th className="px-3 py-2">Shift</th>
                        <th className="px-3 py-2">Scheduled</th>
                        <th className="px-3 py-2">Client & Position</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {selectedShiftRows.map((shift) => {
                        const id = shift?.shift_id ?? shift?.id ?? "";
                        const start = shift?.start_time || shift?.start_time_display || shift?.scheduledLine1 || shift?.scheduled || "";
                        const end = shift?.end_time || shift?.end_time_display || "";
                        const scheduled = start && end ? `${start} - ${end}` : start || end;
                        const clientLabel =
                          selectedRequest?.clientName ||
                          shift?.clientName ||
                          shift?.client ||
                          "";
                        const positionLabel =
                          shift?.position ||
                          shift?.positionName ||
                          shift?.position_name ||
                          "";
                        return (
                          <tr key={String(id)}>
                            <td className="px-3 py-2">{id}</td>
                          <td className="px-3 py-2">
                            {scheduled || "—"}
                          </td>
                          <td className="px-3 py-2">
                            {(clientLabel || "").trim()}{" "}
                            {positionLabel || ""}
                          </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                  onClick={() => {
                    setShowBulkDivisionPositionModal(false);
                    setBulkDivisionPositionForm({
                      client_id: "",
                      division_id: "",
                      position_id: "",
                    });
                    setBulkClientOptions([]);
                    setBulkModalDivisions([]);
                    setBulkModalPositions([]);
                    setBulkModalAllPositions([]);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded bg-sidebar-active text-white hover:bg-red-600"
                  onClick={handleBulkDivisionPositionUpdate}
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Shift Modal */}
      <CreateShiftModal
        overlayZClass={jobsOverlayZ}
        open={showCreateShiftModal}
        onClose={() => {
          setShowCreateShiftModal(false);
          setDuplicateShiftData(null); // Clear duplicate data when closing
        }}
        initialData={duplicateShiftData}
        onSuccess={async (data) => {
          // Refresh shifts data
          try {
            const refreshRes = await fetch(
              `${BASE_URL}/api/jobs/staffingrequests/${selectedRequest.id}/shifts`,
            );
            const refreshData = await refreshRes.json();
              if (refreshRes.ok) {
                setShiftsData(refreshData);
                showSuccess("Shift created successfully");
              }
            } catch (err) {
              console.error("Error refreshing shifts:", err);
            }
        }}
        staffRequestId={selectedRequest?.id}
        clientLocationName={selectedRequest?.clientLocationName}
        defaultDivisionId={
          editableRequest?.division_id ?? selectedRequest?.division_id ?? ""
        }
      />

      {showShiftActualsModal && selectedActualsShift && canWriteShiftActual && (
        <div
          className={`fixed inset-0 bg-black/50 ${embedMode ? jobsEmbedNestedOverlayZ : "z-[10020]"} flex items-center justify-center p-6`}
          onClick={closeShiftActualsModal}
        >
          <div className="actuals-modal" onClick={(e) => e.stopPropagation()}>
            <div className="actuals-modal__header">
              <h2>{selectedActualsModalTitle}</h2>
            </div>

            <div className="actuals-modal__body">
              <div className="actuals-modal__meta">
                <div className="actuals-modal__meta-text">
                  <div className="actuals-modal__id">
                    ID: {selectedActualsShift?.id || selectedActualsShift?.shift_id}
                  </div>
                  <div className="actuals-modal__location">
                    {selectedActualsShift?.staffRequestLine2 ||
                      selectedActualsShift?.position ||
                      selectedActualsShift?.position_name ||
                      "Shift"}
                  </div>
                  <div className="actuals-modal__staff">
                    {selectedActualsShift?.staffDisplay ||
                      selectedActualsShift?.staff ||
                      "Assigned staff"}
                  </div>
                </div>
                <StaffAvatarCircle
                  accountId={
                    shiftStaffEmployeeForAvatar(selectedActualsShift).accountId
                  }
                  employee={
                    shiftStaffEmployeeForAvatar(selectedActualsShift).employee
                  }
                  name={
                    selectedActualsShift?.staffDisplay ||
                    selectedActualsShift?.staff ||
                    ""
                  }
                  className="actuals-modal__avatar"
                  textClassName="text-2xl font-medium text-gray-700"
                  emptyLabel="?"
                />
              </div>

              <div className="actuals-modal__section">
                <div className="actuals-modal__label">Original Schedule</div>
                <div className="actuals-modal__line">
                  {selectedActualsShift?.startDateIso && selectedActualsShift?.endDateIso
                    ? `${formatShiftDateTime(selectedActualsShift.startDateIso)} - ${formatShiftDateTime(selectedActualsShift.endDateIso, false)}`
                    : selectedActualsShift?.scheduledLine1 ||
                      selectedActualsShift?.scheduled ||
                      "Unavailable"}
                </div>
                <div className="actuals-modal__line">
                  {formatDurationAfterBreak(
                    selectedActualsShift?.startDateIso ||
                      selectedActualsShift?.start_date ||
                      selectedActualsShift?.startDate,
                    selectedActualsShift?.endDateIso ||
                      selectedActualsShift?.end_date ||
                      selectedActualsShift?.endDate,
                    resolveScheduledBreakMinutes(selectedActualsShift),
                    selectedActualsShift?.scheduledLine2 ||
                      selectedActualsShift?.scheduledLine1 ||
                      selectedActualsShift?.scheduled ||
                      "",
                  )}
                </div>
              </div>

              <div className="actuals-modal__section">
                <div className="actuals-modal__label">Check in time</div>
                <div className="actuals-modal__line">
                  {selectedActualsShift?.checkinDateIso &&
                  selectedActualsShift?.checkoutDateIso
                    ? `${formatShiftDateTime(selectedActualsShift.checkinDateIso)} - ${formatShiftDateTime(selectedActualsShift.checkoutDateIso, false)}`
                    : selectedActualsShift?.checkinDateIso
                      ? formatShiftDateTime(selectedActualsShift.checkinDateIso)
                      : "No Checkin"}
                </div>
                <div className="actuals-modal__line">
                  {selectedActualsShift?.checkinDateIso &&
                  selectedActualsShift?.checkoutDateIso
                    ? formatDurationAfterBreak(
                        selectedActualsShift.checkinDateIso,
                        selectedActualsShift.checkoutDateIso,
                        selectedActualsShift.checkinUnpaidBreakMinutes,
                        "",
                      )
                    : ""}
                </div>
                <span
                  className={`status-badge ${
                    selectedActualsPendingReview
                      ? "pending-signoff-review"
                      : selectedActualsSubmitted
                        ? "signoff-complete"
                        : "pending"
                  }`}
                >
                  {selectedActualsPendingReview
                    ? "Pending signoff review"
                    : selectedActualsSubmitted
                      ? "Signoff completed"
                      : "Pending signoff"}
                </span>
              </div>

              <div className="actuals-modal__section">
                <div className="actuals-modal__label">Actual Time</div>
                <div className="actuals-modal__options">
                  <label className="actuals-modal__option">
                    <input
                      type="radio"
                      name="jobsActualState"
                      checked={actualsForm.actualState === "as_scheduled"}
                      onChange={() =>
                        handleJobsActualsFormChange("actualState", "as_scheduled")
                      }
                    />
                    <span>As Scheduled</span>
                  </label>
                  <label className="actuals-modal__option">
                    <input
                      type="radio"
                      name="jobsActualState"
                      checked={actualsForm.actualState === "different"}
                      onChange={() =>
                        handleJobsActualsFormChange("actualState", "different")
                      }
                    />
                    <span>Different</span>
                  </label>
                  <label className="actuals-modal__option">
                    <input
                      type="radio"
                      name="jobsActualState"
                      checked={actualsForm.actualState === "no_show"}
                      onChange={() =>
                        handleJobsActualsFormChange("actualState", "no_show")
                      }
                    />
                    <span>No Show</span>
                  </label>
                </div>
              </div>

              {actualsForm.actualState === "different" && (
                <div className="actuals-modal__different">
                  <div className="actuals-modal__field-grid">
                    <label className="actuals-modal__field">
                      <span>Start*</span>
                      <input
                        type="datetime-local"
                        value={actualsForm.actualStartDate}
                        onChange={(e) =>
                          handleJobsActualsFormChange(
                            "actualStartDate",
                            e.target.value,
                          )
                        }
                      />
                    </label>
                    <label className="actuals-modal__field">
                      <span>End*</span>
                      <input
                        type="datetime-local"
                        min={
                          actualsForm.actualStartDate || undefined
                        }
                        value={actualsForm.actualEndDate}
                        onChange={(e) =>
                          handleJobsActualsFormChange(
                            "actualEndDate",
                            e.target.value,
                          )
                        }
                      />
                    </label>
                  </div>
                  <label className="actuals-modal__field">
                    <span>Break*</span>
                    <div className="actuals-modal__break">
                      <input
                        type="number"
                        min="0"
                        value={actualsForm.actualUnpaidBreakMinutes}
                        onChange={(e) =>
                          handleJobsActualsFormChange(
                            "actualUnpaidBreakMinutes",
                            e.target.value,
                          )
                        }
                      />
                      <span>min</span>
                    </div>
                  </label>
                </div>
              )}

              <label className="actuals-modal__field">
                <span>Notes</span>
                <textarea
                  value={actualsForm.note}
                  onChange={(e) =>
                    handleJobsActualsFormChange("note", e.target.value)
                  }
                  rows={3}
                />
              </label>
            </div>

            <div className="actuals-modal__footer">
              <button
                type="button"
                className="actuals-modal__submit"
                onClick={handleSubmitShiftActualsModal}
                disabled={actualsModalSubmitting}
              >
                {actualsModalSubmitting ? "SUBMITTING..." : "SUBMIT"}
              </button>
              <button
                type="button"
                className="actuals-modal__cancel"
                onClick={closeShiftActualsModal}
                disabled={actualsModalSubmitting}
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shift Detail Modal */}
      <ShiftDetailModal
        overlayZClass={jobsShiftDetailOverlayZ}
        nestedOverlayZClass={embedMode ? jobsEmbedNestedOverlayZ : "z-[10000]"}
        unassignOverlayZClass={embedMode ? jobsEmbedOverlayZ : "z-[50300]"}
        actualsOverlayZClass={embedMode ? jobsEmbedNestedOverlayZ : "z-[10020]"}
        duplicateZIndexClass={embedMode ? jobsEmbedNestedOverlayZ : "z-[10001]"}
        divisionOverlayZClass={embedMode ? jobsEmbedNestedOverlayZ : "z-[10001]"}
        carpoolOverlayZClass={embedMode ? jobsEmbedDeepOverlayZ : "z-[10050]"}
        show={Boolean(selectedShift)}
        onHide={() => setSelectedShift(null)}
        onRefresh={() => {
          if (selectedRequest?.id) {
            void fetchShiftsForRequest(selectedRequest.id);
          }
          setReloadKey((prev) => prev + 1);
          void refreshOpenShiftAssignmentPanel();
        }}
        shiftId={selectedShift?.id}
        initialIsClosedWeek={selectedShift?.isClosedWeek}
        requestDefaults={
          selectedShift?.requestDefaults || {
            contactAccountId: selectedRequest?.contact_account_id || null,
            contactName: selectedRequest?.contact || "",
            supervisorContactAccountId:
              selectedRequest?.supervisor_contact_account_id || null,
            supervisorName:
              selectedRequest?.supervisor ||
              selectedRequest?.supervisorName ||
              "",
          }
        }
        onOpenPersonnelModal={(person) => {
          if (!person) return;
          setSelectedPersonnel(person);
          setShowPersonnelModal(true);
        }}
      />

      <DuplicateShiftModal
        zIndexClass={embedMode ? jobsEmbedNestedOverlayZ : "z-[10001]"}
        open={Boolean(standaloneDuplicateShiftId)}
        onClose={() => setStandaloneDuplicateShiftId(null)}
        shiftId={standaloneDuplicateShiftId}
        requestDefaults={{
          contactAccountId: selectedRequest?.contact_account_id || null,
          contactName: selectedRequest?.contact || "",
          supervisorContactAccountId:
            selectedRequest?.supervisor_contact_account_id || null,
          supervisorName:
            selectedRequest?.supervisor ||
            selectedRequest?.supervisorName ||
            "",
        }}
        onDuplicateSuccess={() => {
          if (selectedRequest?.id) fetchShiftsForRequest(selectedRequest.id);
        }}
      />

      {/* Shift Assignment Side Panel (use the same RightPanel + ShiftDetailPanel as Schedule) */}
      <RightPanel
        overlayZIndex={embedMode ? 50500 : 50}
        isOpen={showShiftAssignmentPanel}
        onClose={() => {
          setShowShiftAssignmentPanel(false);
          setShiftAssignmentPanelShift(null);
          setShiftAssignmentPanelEntry(null);
          setShiftAssignmentPanelAssignment(null);
        }}
        title={shiftAssignmentPanelEntry?.location || "Shift Details"}
        width="500px"
      >
        {showShiftAssignmentPanel &&
          shiftAssignmentPanelShift &&
          shiftAssignmentPanelEntry && (
            <ShiftDetailPanel
              shift={shiftAssignmentPanelShift}
              entry={shiftAssignmentPanelEntry}
              assignment={shiftAssignmentPanelAssignment}
              isClosedWeek={Boolean(
                shiftAssignmentPanelEntry?.isClosedWeek ||
                  shiftAssignmentPanelShift?.isClosedWeek,
              )}
              onShiftNotesSaved={() => void refreshOpenShiftAssignmentPanel()}
              onOpenPersonnelModal={(person) => {
                if (!person) return;
                setSelectedPersonnel(person);
                setShowPersonnelModal(true);
              }}
              onOpenShiftModal={(shift, entry) => {
                const shiftObj = shift || entry?.rawShift || entry || null;
                openShiftOverview(shiftObj);
              }}
              onScheduleRefresh={() => setReloadKey((prev) => prev + 1)}
              onScheduleRefreshAwait={async () => {
                setReloadKey((prev) => prev + 1);
              }}
            />
          )}
      </RightPanel>

      {/* Assignment Detail Modal */}
      <AssignmentDetailModal
        isOpen={Boolean(selectedAssignment)}
        onClose={() => setSelectedAssignment(null)}
        assignment={selectedAssignment}
      />

      {/* Client Detail Modal */}
      <ClientDetailModal
        overlayZClass={embedMode ? jobsEmbedOverlayZ : "z-50"}
        client={selectedClientForModal}
        isOpen={showClientModal}
        onClose={() => {
          setShowClientModal(false);
          setSelectedClientForModal(null);
        }}
        selectedTab="CLIENTS"
        onSaved={() => {
          // Optionally refresh data if needed
          refreshCurrentTab();
        }}
      />

      {/* Staff (Temp Staff) Detail Modal */}
      {showPersonnelModal && selectedPersonnel && (
        <PersonnelDetailModal
          overlayZClass={embedMode ? jobsEmbedOverlayZ : "z-[10100]"}
          person={selectedPersonnel}
          type="temp"
          isOpen={showPersonnelModal}
          onClose={() => {
            setShowPersonnelModal(false);
            setSelectedPersonnel(null);
          }}
        />
      )}

      {/* Unassign Modal (Shift Assignment kebab -> Unassign) */}
      {unassignModal.open && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-40 ${embedMode ? jobsEmbedOverlayZ : "z-[50300]"} flex items-center justify-center p-4`}
          onClick={() =>
            !unassignModal.submitting &&
            setUnassignModal((prev) => ({ ...prev, open: false }))
          }
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">
              Unassign Shift: {unassignModal.staffName || "Staff"}
            </h3>

            <div className="space-y-4 text-sm">
              <div>
                <div className="text-gray-600 mb-2">Count towards reliability</div>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={Boolean(unassignModal.countTowardsReliability)}
                      onChange={() =>
                        setUnassignModal((p) => ({
                          ...p,
                          countTowardsReliability: true,
                        }))
                      }
                      disabled={unassignModal.submitting}
                    />
                    Yes
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={!unassignModal.countTowardsReliability}
                      onChange={() =>
                        setUnassignModal((p) => ({
                          ...p,
                          countTowardsReliability: false,
                        }))
                      }
                      disabled={unassignModal.submitting}
                    />
                    No
                  </label>
                </div>
              </div>

              <div>
                <div className="text-gray-600 mb-2">Reason</div>
                <div className="flex items-center gap-6 flex-wrap">
                  {["Sick", "MIA/No Show", "Other"].map((r) => (
                    <label key={r} className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={unassignModal.reason === r}
                        onChange={() =>
                          setUnassignModal((p) => ({ ...p, reason: r }))
                        }
                        disabled={unassignModal.submitting}
                      />
                      {r}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-gray-600 mb-2">24 hour notice</div>
                <div className="flex items-center gap-6 flex-wrap">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={unassignModal.notice24h === "yes"}
                      onChange={() =>
                        setUnassignModal((p) => ({ ...p, notice24h: "yes" }))
                      }
                      disabled={unassignModal.submitting}
                    />
                    Yes, notification more than 24 hours
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={unassignModal.notice24h === "no"}
                      onChange={() =>
                        setUnassignModal((p) => ({ ...p, notice24h: "no" }))
                      }
                      disabled={unassignModal.submitting}
                    />
                    No, notification less than 24 hours
                  </label>
                </div>
              </div>

              <div>
                <div className="text-gray-600 mb-2">Availability</div>
                <div className="space-y-2">
                  {[
                    { key: "available", label: "Available during shift" },
                    { key: "unavailable_shift", label: "Unavailable during shift" },
                    { key: "unavailable_day", label: "Unavailable all day" },
                    { key: "unavailable_range", label: "Unavailable date range" },
                  ].map((opt) => (
                    <label key={opt.key} className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={unassignModal.availability === opt.key}
                        onChange={() =>
                          setUnassignModal((p) => ({ ...p, availability: opt.key }))
                        }
                        disabled={unassignModal.submitting}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-gray-600 mb-2">Note</div>
                <input
                  type="text"
                  value={unassignModal.note}
                  onChange={(e) =>
                    setUnassignModal((p) => ({ ...p, note: e.target.value }))
                  }
                  disabled={unassignModal.submitting}
                  className="w-full border-b border-gray-300 focus:outline-none focus:border-red-500 py-1"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
                onClick={() => setUnassignModal((p) => ({ ...p, open: false }))}
                disabled={unassignModal.submitting}
              >
                CANCEL
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-60"
                onClick={submitForceUnassignFromShiftAssignment}
                disabled={unassignModal.submitting}
              >
                {unassignModal.submitting
                  ? "UNASSIGNING..."
                  : unassignModal.countTowardsReliability
                    ? "UNASSIGN & REDUCE RELIABILITY"
                    : "UNASSIGN"}
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
}
